import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import './App.css';
import axios from 'axios';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import TermsPage from './Terms';
import PrivacyPage from './Privacy';
import AdminVerifications from './AdminVerifications';
import AdminCertificates from './AdminCertificates';
import CertificateVerificationPage from './CertificateVerificationPage';
import AdminPortal from './AdminPortal';
import TeacherCourseManagement from './TeacherCourseManagement';
import StudentCoursePage from './StudentCoursePage';
import EmployeePortal from './EmployeePortal';
import MyCoursesPage from './MyCoursesPage';
import MyCourseDetailsPage from './MyCourseDetailsPage';
import NotFoundPage from './NotFoundPage';
import MagicCursor from './MagicCursor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_FOOTER_SESSION_KEY = 'kra_admin_footer_access';
const PASSWORD_CHANGE_ALLOWED_PAGES = ['change-password', 'auth', 'reset-password'];
const PENDING_ENROLLMENT_STORAGE_KEY = 'kra_pending_enrollment_v1';

const FEEDBACK_TONE_CLASSES = {
  success: 'border-green-500/40 bg-green-900/30 text-green-200',
  error: 'border-red-500/40 bg-red-900/30 text-red-200',
  warning: 'border-amber-500/40 bg-amber-900/30 text-amber-200',
  info: 'border-blue-500/40 bg-blue-900/30 text-blue-200',
};

const isImageAttachment = (attachment) => String(attachment?.content_type || '').toLowerCase().startsWith('image/');
const isVideoAttachment = (attachment) => String(attachment?.content_type || '').toLowerCase().startsWith('video/');

const resolveAttachmentUrl = (attachment, preview = false) => {
  if (!attachment) return '';
  if (preview && attachment.preview_url) return `${BACKEND_URL}${attachment.preview_url}`;
  if (attachment.download_url) return `${BACKEND_URL}${attachment.download_url}`;
  if (attachment.url) {
    if (/^https?:\/\//i.test(attachment.url)) return attachment.url;
    return `${BACKEND_URL}${attachment.url.startsWith('/') ? attachment.url : `/${attachment.url}`}`;
  }
  return '';
};

const downloadProtectedAttachment = (attachment, preview = false) => {
  const href = resolveAttachmentUrl(attachment, preview);
  if (!href) return;

  const token = localStorage.getItem('token');
  const sep = href.includes('?') ? '&' : '?';
  const url = token ? `${href}${sep}access_token=${encodeURIComponent(token)}` : href;
  if (preview) {
    window.open(url, '_blank', 'noreferrer noopener');
    return;
  }

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;';
  iframe.src = url;
  document.body.appendChild(iframe);
  setTimeout(() => {
    if (document.body.contains(iframe)) document.body.removeChild(iframe);
  }, 30000);
};

const readPendingEnrollment = () => {
  try {
    const raw = localStorage.getItem(PENDING_ENROLLMENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.course_id) return null;
    return parsed;
  } catch (error) {
    return null;
  }
};

const writePendingEnrollment = (payload) => {
  if (!payload || !payload.course_id) return;
  try {
    localStorage.setItem(
      PENDING_ENROLLMENT_STORAGE_KEY,
      JSON.stringify({
        course_id: String(payload.course_id),
        teacher_id: payload.teacher_id ? String(payload.teacher_id) : null,
        created_at: new Date().toISOString(),
      }),
    );
  } catch (error) {
    // Ignore storage failures.
  }
};

const clearPendingEnrollment = () => {
  try {
    localStorage.removeItem(PENDING_ENROLLMENT_STORAGE_KEY);
  } catch (error) {
    // Ignore storage failures.
  }
};

const InlineFeedback = ({ feedback, className = '' }) => {
  if (!feedback?.message) return null;
  const tone = FEEDBACK_TONE_CLASSES[feedback.type] || FEEDBACK_TONE_CLASSES.info;
  return (
    <div className={`mt-2 rounded-lg border px-3 py-2 text-sm ${tone} ${className}`}>
      {feedback.message}
    </div>
  );
};

const PAGE_ROUTE_CONFIG = [
  { page: 'home', paths: ['/'] },
  { page: 'auth', paths: ['/auth'] },
  { page: 'student-dashboard', paths: ['/student/dashboard', '/student-dashboard'] },
  { page: 'student-teacher', paths: ['/student/teacher'] },
  { page: 'student-learning', paths: ['/student/courses'] },
  { page: 'student-my-courses', paths: ['/student/my-courses'] },
  { page: 'student-attendance', paths: ['/student/attendance'] },
  { page: 'student-schedule', paths: ['/student/schedule'] },
  { page: 'student-assignments', paths: ['/student/assignments'] },
  { page: 'student-certificates', paths: ['/student/certificates'] },
  { page: 'student-notifications', paths: ['/student/notifications'] },
  { page: 'student-messages', paths: ['/student/messages'] },
  { page: 'student-course-detail', paths: ['/student/courses/detail'] },
  { page: 'student-my-course-detail', paths: ['/student/my-courses/detail'] },
  { page: 'teacher-dashboard', paths: ['/teacher/dashboard', '/teacher-dashboard'] },
  { page: 'teacher-students', paths: ['/teacher/students'] },
  { page: 'teacher-attendance', paths: ['/teacher/attendance'] },
  { page: 'teacher-classes', paths: ['/teacher/classes'] },
  { page: 'teacher-meetings', paths: ['/teacher/meetings'] },
  { page: 'teacher-analytics', paths: ['/teacher/analytics'] },
  { page: 'teacher-courses', paths: ['/teacher/courses'] },
  { page: 'teacher-course-detail', paths: ['/teacher/courses/detail'] },
  { page: 'teacher-lectures', paths: ['/teacher/lectures'] },
  { page: 'teacher-assignments', paths: ['/teacher/assignments'] },
  { page: 'teacher-clock', paths: ['/teacher/clock'] },
  { page: 'teacher-messages', paths: ['/teacher/messages'] },
  { page: 'teacher-notifications', paths: ['/teacher/notifications'] },
  { page: 'employee-dashboard', paths: ['/employee/dashboard', '/employee-dashboard'] },
  { page: 'reset-password', paths: ['/reset-password'] },
  { page: 'change-password', paths: ['/change-password'] },
  { page: 'courses', paths: ['/courses'] },
  { page: 'courses-category', paths: ['/courses/category'] },
  { page: 'shop', paths: ['/shop'] },
  { page: 'pricing', paths: ['/pricing'] },
  { page: 'community', paths: ['/community'] },
  { page: 'mentorship', paths: ['/mentorship'] },
  { page: 'code-editor', paths: ['/code-editor'] },
  { page: 'jobs', paths: ['/jobs'] },
  { page: 'hackathons', paths: ['/hackathons'] },
  { page: 'dashboard', paths: ['/dashboard'] },
  { page: 'order-confirmation', paths: ['/order-confirmation'] },
  { page: 'profile', paths: ['/profile'] },
  { page: 'terms', paths: ['/terms'] },
  { page: 'privacy', paths: ['/privacy'] },
  { page: 'admin', paths: ['/admin'] },
  { page: 'admin-dashboard', paths: ['/admin-dashboard'] },
  { page: 'admin-verifications', paths: ['/admin/verifications', '/admin-verifications'] },
  { page: 'admin-certificates', paths: ['/admin/certificates', '/admin-certificates'] },
  { page: 'certificate-verification', paths: ['/certificate-verification'] },
];

const PAGE_TO_PATH = PAGE_ROUTE_CONFIG.reduce((result, entry) => {
  result[entry.page] = entry.paths[0];
  return result;
}, {});

const PATH_TO_PAGE = PAGE_ROUTE_CONFIG.reduce((result, entry) => {
  entry.paths.forEach((path) => {
    result[path] = entry.page;
  });
  return result;
}, {});

const resolveCurrentPage = (pathname, search = '') => {
  const params = new URLSearchParams(search);
  const sessionId = params.get('session_id');
  const paypalToken = params.get('token');
  const paypalOrderId = params.get('paypal_order_id');
  const paymentSuccess = params.get('payment') === 'success';
  if (pathname === '/order-confirmation' || sessionId || paypalOrderId || paypalToken || paymentSuccess) {
    return 'order-confirmation';
  }
  if (pathname === '/payment-cancelled' || params.get('payment') === 'cancelled') {
    return 'home';
  }
  if (pathname.startsWith('/student/my-courses/')) {
    return 'student-my-course-detail';
  }
  if (pathname.startsWith('/courses/')) {
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length === 2 && parts[1] !== 'category') {
      return 'courses-category';
    }
    if (parts.length === 3 && parts[0] === 'courses') {
      return 'courses-category';
    }
  }
  if (pathname.startsWith('/courses/category/')) {
    return 'courses-category';
  }
  return PATH_TO_PAGE[pathname] || null;
};

// Helper to resolve backend-hosted image URLs
const resolveImageUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (!BACKEND_URL) return url;
  return `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const DEFAULT_COURSE_IMAGE_URL = 'https://images.unsplash.com/photo-1581092335397-9583eb92d232?auto=format&fit=crop&w=1200&q=60';
const asArray = (value) => (Array.isArray(value) ? value : []);

const handleCourseImageError = (event) => {
  if (event.currentTarget.src === DEFAULT_COURSE_IMAGE_URL) return;
  event.currentTarget.onerror = null;
  event.currentTarget.src = DEFAULT_COURSE_IMAGE_URL;
};

const formatCourseDuration = (value) => {
  const text = String(value || '').trim();
  if (!text) return 'Approximately 4 Months';
  const lowered = text.toLowerCase();
  if (lowered.includes('month') || lowered.includes('week')) return 'Approximately 4 Months';
  return text;
};

const ROBOTICS_CATEGORY_META = [
  {
    value: 'Beginner',
    slug: 'beginner',
    emoji: '🟢',
    accent: 'text-green-300',
    cardTone: 'from-green-950/40 to-black',
    description: 'Build your robotics foundation through hands-on beginner-friendly courses.',
  },
  {
    value: 'Intermediate',
    slug: 'intermediate',
    emoji: '🟡',
    accent: 'text-yellow-300',
    cardTone: 'from-yellow-950/30 to-black',
    description: 'Strengthen programming, control systems, and applied robotics engineering skills.',
  },
  {
    value: 'Advanced',
    slug: 'advanced',
    emoji: '🔵',
    accent: 'text-blue-300',
    cardTone: 'from-blue-950/30 to-black',
    description: 'Build intelligent and autonomous robotic systems for advanced technical challenges.',
  },
  {
    value: 'Specialized',
    slug: 'specialized',
    emoji: '🟣',
    accent: 'text-purple-300',
    cardTone: 'from-purple-950/30 to-black',
    description: 'Master specialized tracks including Arduino, Raspberry Pi, CAD, and IoT robotics.',
  },
  {
    value: 'Project',
    slug: 'project',
    emoji: '🟠',
    accent: 'text-orange-300',
    cardTone: 'from-orange-950/30 to-black',
    description: 'Develop major build projects from robot cars and quadrupeds to humanoid subsystems.',
  },
  {
    value: 'Competition',
    slug: 'competition',
    emoji: '🔴',
    accent: 'text-red-300',
    cardTone: 'from-red-950/30 to-black',
    description: 'Innovate, prototype, and compete through challenge-based labs and hackathons.',
  },
];

const COURSE_GROUP_META = [
  {
    value: 'Robotics',
    slug: 'robotics',
    heading: 'Robotics Categories',
    description: 'Choose a category to open a dedicated robotics course view.',
    categories: ROBOTICS_CATEGORY_META,
  },
  {
    value: 'Coding',
    slug: 'coding',
    heading: 'Coding Categories',
    description: 'Explore dedicated coding tracks by level and development pathway.',
    categories: [
      { value: 'Beginner', slug: 'beginner', emoji: '🧩', accent: 'text-cyan-300', cardTone: 'from-cyan-950/30 to-black', description: 'Coding fundamentals for first-time learners.' },
      { value: 'Intermediate', slug: 'intermediate', emoji: '⚙️', accent: 'text-sky-300', cardTone: 'from-sky-950/30 to-black', description: 'Build practical programming and debugging confidence.' },
      { value: 'Advanced', slug: 'advanced', emoji: '🚀', accent: 'text-indigo-300', cardTone: 'from-indigo-950/30 to-black', description: 'Advanced software development and system thinking.' },
      { value: 'Web Development', slug: 'web-development', emoji: '🌐', accent: 'text-emerald-300', cardTone: 'from-emerald-950/30 to-black', description: 'Frontend, backend, and full-stack web engineering.' },
      { value: 'Mobile Development', slug: 'mobile-development', emoji: '📱', accent: 'text-teal-300', cardTone: 'from-teal-950/30 to-black', description: 'Cross-platform app development and deployment.' },
    ],
  },
  {
    value: 'Artificial Intelligence',
    slug: 'artificial-intelligence',
    heading: 'AI Categories',
    description: 'Open focused AI and data learning pathways in dedicated pages.',
    categories: [
      { value: 'Beginner', slug: 'beginner', emoji: '🤖', accent: 'text-amber-300', cardTone: 'from-amber-950/30 to-black', description: 'AI foundations and entry-level machine learning.' },
      { value: 'Intermediate', slug: 'intermediate', emoji: '🧠', accent: 'text-lime-300', cardTone: 'from-lime-950/30 to-black', description: 'Applied AI concepts and practical model workflows.' },
      { value: 'Advanced', slug: 'advanced', emoji: '🛰️', accent: 'text-fuchsia-300', cardTone: 'from-fuchsia-950/30 to-black', description: 'Advanced AI engineering and career-oriented depth.' },
      { value: 'Data Science', slug: 'data-science', emoji: '📊', accent: 'text-blue-300', cardTone: 'from-blue-950/30 to-black', description: 'Data analysis, visualization, and analytics tracks.' },
    ],
  },
  {
    value: 'Cloud Computing',
    slug: 'cloud-computing',
    heading: 'Cloud Categories',
    description: 'Navigate cloud infrastructure, architecture, and DevOps programs.',
    categories: [
      { value: 'Beginner', slug: 'beginner', emoji: '☁️', accent: 'text-cyan-300', cardTone: 'from-cyan-950/30 to-black', description: 'Cloud essentials and platform fundamentals.' },
      { value: 'Intermediate', slug: 'intermediate', emoji: '🛠️', accent: 'text-blue-300', cardTone: 'from-blue-950/30 to-black', description: 'Infrastructure administration and cloud operations.' },
      { value: 'Advanced', slug: 'advanced', emoji: '🔐', accent: 'text-slate-200', cardTone: 'from-slate-900/60 to-black', description: 'Advanced cloud security and architecture patterns.' },
      { value: 'DevOps', slug: 'devops', emoji: '🔁', accent: 'text-green-300', cardTone: 'from-green-950/30 to-black', description: 'Automation, CI/CD, containers, and orchestration.' },
    ],
  },
  {
    value: 'Cybersecurity',
    slug: 'cybersecurity',
    heading: 'Cybersecurity Categories',
    description: 'Move through cybersecurity learning paths with category-specific pages.',
    categories: [
      { value: 'Beginner', slug: 'beginner', emoji: '🛡️', accent: 'text-emerald-300', cardTone: 'from-emerald-950/30 to-black', description: 'Security fundamentals and core defensive practices.' },
      { value: 'Intermediate', slug: 'intermediate', emoji: '🧪', accent: 'text-yellow-300', cardTone: 'from-yellow-950/30 to-black', description: 'Hands-on security operations and response workflows.' },
      { value: 'Advanced', slug: 'advanced', emoji: '⚠️', accent: 'text-red-300', cardTone: 'from-red-950/30 to-black', description: 'Advanced offensive and defensive cybersecurity skills.' },
    ],
  },
  {
    value: 'Engineering',
    slug: 'engineering',
    heading: 'Engineering Categories',
    description: 'Explore practical engineering tracks from fundamentals to advanced pathways.',
    categories: [
      { value: 'Beginner', slug: 'beginner', emoji: '🧱', accent: 'text-orange-300', cardTone: 'from-orange-950/30 to-black', description: 'Core engineering principles and structured problem solving.' },
      { value: 'Intermediate', slug: 'intermediate', emoji: '🔧', accent: 'text-amber-300', cardTone: 'from-amber-950/30 to-black', description: 'Applied design, modeling, and cross-domain engineering practice.' },
      { value: 'Advanced', slug: 'advanced', emoji: '🏗️', accent: 'text-stone-200', cardTone: 'from-stone-900/60 to-black', description: 'Professional and career-focused advanced engineering tracks.' },
    ],
  },
  {
    value: 'CAD',
    slug: 'cad',
    heading: 'CAD Categories',
    description: 'Open dedicated CAD learning pages for design and prototyping pathways.',
    categories: [
      { value: 'Beginner', slug: 'beginner', emoji: '📐', accent: 'text-violet-300', cardTone: 'from-violet-950/30 to-black', description: 'CAD and modeling foundations for new learners.' },
      { value: 'Intermediate', slug: 'intermediate', emoji: '🧰', accent: 'text-indigo-300', cardTone: 'from-indigo-950/30 to-black', description: 'Product design workflows and prototype development.' },
      { value: 'Advanced', slug: 'advanced', emoji: '🏛️', accent: 'text-purple-300', cardTone: 'from-purple-950/30 to-black', description: 'Advanced portfolio-driven CAD and design execution.' },
    ],
  },
  {
    value: 'Mathematics',
    slug: 'mathematics',
    heading: 'Mathematics Categories',
    description: 'Navigate grade-focused and advanced mathematics pathways.',
    categories: [
      { value: 'Grade 1-8', slug: 'grade-1-8', emoji: '➕', accent: 'text-cyan-300', cardTone: 'from-cyan-950/30 to-black', description: 'Foundational mathematics for early learners.' },
      { value: 'High School', slug: 'high-school', emoji: '📘', accent: 'text-blue-300', cardTone: 'from-blue-950/30 to-black', description: 'High school algebra, functions, and exam readiness.' },
      { value: 'Advanced Mathematics', slug: 'advanced-mathematics', emoji: '∫', accent: 'text-sky-300', cardTone: 'from-sky-950/30 to-black', description: 'Calculus, linear algebra, and advanced math pathways.' },
    ],
  },
  {
    value: 'French',
    slug: 'french',
    heading: 'French Categories',
    description: 'Choose dedicated French-learning pages by fluency objective.',
    categories: [
      { value: 'Beginner', slug: 'beginner', emoji: '🇫🇷', accent: 'text-blue-300', cardTone: 'from-blue-950/30 to-black', description: 'French basics for new learners and younger students.' },
      { value: 'Intermediate', slug: 'intermediate', emoji: '🗣️', accent: 'text-cyan-300', cardTone: 'from-cyan-950/30 to-black', description: 'Conversation, writing, and practical fluency growth.' },
      { value: 'Advanced', slug: 'advanced', emoji: '🎓', accent: 'text-slate-200', cardTone: 'from-slate-900/60 to-black', description: 'Advanced language mastery and professional readiness.' },
      { value: 'Immigration', slug: 'immigration', emoji: '🧭', accent: 'text-emerald-300', cardTone: 'from-emerald-950/30 to-black', description: 'TEF/TCF pathways and immigration preparation tracks.' },
    ],
  },
  {
    value: 'STEM',
    slug: 'stem',
    heading: 'STEM Categories',
    description: 'Open age-grouped STEM tracks for focused learning experiences.',
    categories: [
      { value: 'Ages 6-9', slug: 'ages-6-9', emoji: '🧒', accent: 'text-yellow-300', cardTone: 'from-yellow-950/30 to-black', description: 'Discovery-focused STEM for young learners.' },
      { value: 'Ages 10-13', slug: 'ages-10-13', emoji: '🧑', accent: 'text-orange-300', cardTone: 'from-orange-950/30 to-black', description: 'Practical junior innovation and build-based learning.' },
      { value: 'Ages 14-18', slug: 'ages-14-18', emoji: '🧑‍💻', accent: 'text-red-300', cardTone: 'from-red-950/30 to-black', description: 'Senior STEM pathways with technology specialization.' },
    ],
  },
  {
    value: 'K.R.A Before & After-School Clubs',
    slug: 'clubs',
    heading: 'Before & After-School Club Categories',
    description: 'Browse fixed-price club offerings by enrichment area and care program type.',
    categories: [
      { value: 'Care Programs', slug: 'care-programs', emoji: '🕒', accent: 'text-amber-300', cardTone: 'from-amber-950/30 to-black', description: 'Before-school, after-school, and homework support care options.' },
      { value: 'Sports & Recreation', slug: 'sports-recreation', emoji: '🏀', accent: 'text-orange-300', cardTone: 'from-orange-950/30 to-black', description: 'Movement, games, team sports, and active recreation clubs.' },
      { value: 'STEM & Technology', slug: 'stem-technology', emoji: '🧪', accent: 'text-cyan-300', cardTone: 'from-cyan-950/30 to-black', description: 'Hands-on robotics, coding, design, and discovery clubs.' },
      { value: 'Arts & Creativity', slug: 'arts-creativity', emoji: '🎨', accent: 'text-pink-300', cardTone: 'from-pink-950/30 to-black', description: 'Creative expression through art, design, theatre, and making.' },
      { value: 'Games & Thinking Skills', slug: 'games-thinking-skills', emoji: '♟️', accent: 'text-lime-300', cardTone: 'from-lime-950/30 to-black', description: 'Strategy, puzzles, debate, and critical-thinking clubs.' },
      { value: 'Business & Life Skills', slug: 'business-life-skills', emoji: '💼', accent: 'text-emerald-300', cardTone: 'from-emerald-950/30 to-black', description: 'Leadership, entrepreneurship, confidence, and life-skill clubs.' },
      { value: 'Language & Academic Clubs', slug: 'language-academic-clubs', emoji: '📚', accent: 'text-sky-300', cardTone: 'from-sky-950/30 to-black', description: 'Language, literacy, enrichment, and academic support clubs.' },
    ],
  },
  {
    value: 'Career',
    slug: 'career',
    heading: 'Career Categories',
    description: 'Explore dedicated career and certification-focused course pages.',
    categories: [
      { value: 'Career Path', slug: 'career-path', emoji: '🧭', accent: 'text-green-300', cardTone: 'from-green-950/30 to-black', description: 'Structured role-based technology career pathways.' },
      { value: 'Certification', slug: 'certification', emoji: '📜', accent: 'text-yellow-300', cardTone: 'from-yellow-950/30 to-black', description: 'Industry-recognized certification preparation tracks.' },
      { value: 'Interview Preparation', slug: 'interview-preparation', emoji: '🎯', accent: 'text-orange-300', cardTone: 'from-orange-950/30 to-black', description: 'Interview readiness and technical portfolio development.' },
    ],
  },
];

const COURSE_GROUP_META_BY_SLUG = COURSE_GROUP_META.reduce((result, group) => {
  result[group.slug] = group;
  return result;
}, {});

const COURSE_GROUP_META_BY_VALUE = COURSE_GROUP_META.reduce((result, group) => {
  result[group.value.toLowerCase()] = group;
  return result;
}, {});

const COURSE_NAV_LABELS = {
  'K.R.A Before & After-School Clubs': 'K.R.A Before & After-School Clubs',
  Robotics: 'Robotics',
  Coding: 'Coding & Software Development',
  'Artificial Intelligence': 'AI & Data Science',
  'Cloud Computing': 'Cloud & DevOps',
  Cybersecurity: 'Cybersecurity',
  Engineering: 'Engineering & Electronics',
  CAD: 'CAD & 3D Design',
  Mathematics: 'Mathematics',
  French: 'French',
  STEM: 'STEM & Youth Programs',
  Career: 'Career & Certification',
};

const COURSE_GROUP_META_ORDERED = [...COURSE_GROUP_META].sort((left, right) => (
  left.value === 'K.R.A Before & After-School Clubs' ? -1 : right.value === 'K.R.A Before & After-School Clubs' ? 1 : 0
));

const BEFORE_AFTER_SCHOOL_CLUBS_GROUP = 'K.R.A Before & After-School Clubs';

const isBeforeAfterSchoolClubCourse = (course) => String(course?.catalog_group || course?.catalogGroup || '').trim() === BEFORE_AFTER_SCHOOL_CLUBS_GROUP;

const getBeforeAfterSchoolClubPrice = (course) => {
  const clubOption = (course?.program_options || []).find((item) => item.id === 'club');
  const numericPrice = Number(course?.price ?? clubOption?.price_cad ?? 200);
  return Number.isFinite(numericPrice) && numericPrice > 0 ? numericPrice : 200;
};

// Safely convert any FastAPI/pydantic error into a readable string.
const parseApiError = (error, fallback = 'An unexpected error occurred. Please try again.') => {
  try {
    const detail = error?.response?.data?.detail;
    if (!detail) return error?.message || fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((e) => {
          if (typeof e === 'string') return e;
          const field = Array.isArray(e.loc) ? e.loc.slice(1).join(' \u2192 ') : '';
          const msg = e.msg || 'Invalid value';
          return field ? `${field}: ${msg}` : msg;
        })
        .join(' | ');
    }
    if (typeof detail === 'object') return detail.msg || detail.message || fallback;
    return String(detail);
  } catch (_) {
    return fallback;
  }
};

axios.defaults.timeout = 30000;

const clearStoredAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  delete axios.defaults.headers.common.Authorization;
};

let refreshPromise = null;
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const statusCode = error.response?.status;
    const hasAuthHeader = Boolean(originalRequest.headers?.Authorization || axios.defaults.headers.common.Authorization);
    if (statusCode === 401 && hasAuthHeader && !originalRequest._retry && !originalRequest.skipAuthRefresh) {
      originalRequest._retry = true;
      try {
        const currentRefreshToken = localStorage.getItem('refresh_token');
        if (!currentRefreshToken) {
          clearStoredAuth();
          return Promise.reject(error);
        }
        if (!refreshPromise) {
          refreshPromise = axios.post(`${API}/auth/refresh`, { refresh_token: currentRefreshToken }, { skipAuthRefresh: true })
            .finally(() => { refreshPromise = null; });
        }
        const refreshResponse = await refreshPromise;
        const newAccessToken = refreshResponse.data?.access_token;
        if (!newAccessToken) {
          clearStoredAuth();
          return Promise.reject(error);
        }
        localStorage.setItem('token', newAccessToken);
        if (refreshResponse.data?.refresh_token) {
          localStorage.setItem('refresh_token', refreshResponse.data.refresh_token);
        }
        axios.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${newAccessToken}`,
        };
        return axios(originalRequest);
      } catch (refreshError) {
        clearStoredAuth();
        return Promise.reject(refreshError);
      }
    }
    if (statusCode === 401 && hasAuthHeader && !originalRequest.skipAuthRefresh) {
      clearStoredAuth();
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const bootstrapAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      try {
        const userResponse = await axios.get(`${API}/auth/me`);
        setUser(userResponse.data);
      } catch (error) {
        clearStoredAuth();
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    bootstrapAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const authPayload = response.data || {};
      return await establishSession(authPayload);
    } catch (error) {
      return { success: false, error: parseApiError(error, 'Login failed') };
    }
  };

  const establishSession = async (authPayload) => {
    try {
      const newToken = authPayload?.access_token;
      const refreshToken = authPayload?.refresh_token;
      const payloadUser = authPayload?.user;
      if (!newToken) {
        return { success: false, error: 'Missing access token' };
      }
      setToken(newToken);
      localStorage.setItem('token', newToken);
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
      axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
      const userResponse = payloadUser ? { data: payloadUser } : await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);
      return { success: true, user: userResponse.data };
    } catch (error) {
      return { success: false, error: parseApiError(error, 'Unable to establish session') };
    }
  };

  const register = async (name, email, password) => {
    try {
      await axios.post(`${API}/auth/register`, { name, email, password });
      return { success: true, message: 'Registration successful! Please login.' };
    } catch (error) {
      return { success: false, error: parseApiError(error, 'Registration failed') };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    clearStoredAuth();
  };

  const refreshUser = async () => {
    const activeToken = localStorage.getItem('token');
    if (!activeToken) {
      setUser(null);
      return null;
    }
    axios.defaults.headers.common.Authorization = `Bearer ${activeToken}`;
    const response = await axios.get(`${API}/auth/me`);
    setUser(response.data || null);
    return response.data || null;
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, establishSession, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AnnouncementBanner = ({ placement }) => {
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    const loadAnnouncements = async () => {
      try {
        const response = await axios.get(`${API}/announcements/active`, { params: { placement } });
        setAnnouncements(response.data?.announcements || []);
      } catch (error) {
        setAnnouncements([]);
      }
    };

    loadAnnouncements();
  }, [placement]);

  if (!announcements.length) {
    return null;
  }

  return (
    <div className="space-y-3 mb-6">
      {announcements.map((announcement) => (
        <div
          key={announcement.id}
          className={`rounded-2xl border px-4 py-4 ${announcement.priority === 'urgent' ? 'border-red-500/50 bg-red-900/20' : announcement.priority === 'important' ? 'border-yellow-400/50 bg-yellow-900/20' : 'border-blue-500/40 bg-blue-900/20'}`}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-400">{announcement.priority || 'information'}</div>
              <div className="text-lg font-bold text-white">{announcement.title}</div>
            </div>
            <div className="text-xs text-gray-400">{(announcement.placements || []).join(' • ')}</div>
          </div>
          <p className="mt-2 text-sm text-gray-200">{announcement.content}</p>
        </div>
      ))}
    </div>
  );
};

// Header Component
const Header = ({ onPageChange, currentPage }) => {
  const { user, logout } = useAuth();
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [coursesMenuOpen, setCoursesMenuOpen] = useState(false);
  const coursesMenuRef = useRef(null);

  const userRole = (user?.role || 'student').toLowerCase();
  const isTeacher = userRole === 'teacher' || userRole === 'mentor';
  const isAdmin = userRole === 'admin';
  const isEmployee = userRole === 'employee';
  const isStudent = userRole === 'student';
  const isDashboardPage = currentPage === 'dashboard' || currentPage.startsWith('student-') || currentPage.startsWith('teacher-') || currentPage === 'employee-dashboard' || currentPage.startsWith('admin-') || currentPage === 'admin';

  const handleLogout = () => {
    setAuthMenuOpen(false);
    setMobileMenuOpen(false);
    try {
      sessionStorage.removeItem(ADMIN_FOOTER_SESSION_KEY);
    } catch (error) {
      // Ignore session storage failures.
    }
    logout();
  };

  const mainNavItems = isEmployee
    ? [{ key: 'employee-dashboard', label: 'Employee Portal' }]
    : [
        { key: 'home', label: 'Home' },
        { key: 'courses', label: 'Courses' },
        { key: 'shop', label: 'Shop' },
        isTeacher ? { key: 'teacher-messages', label: 'Messages' } : { key: 'pricing', label: 'Pricing' },
        { key: 'community', label: 'Community' },
        isTeacher ? { key: 'teacher-courses', label: 'My Courses' } : { key: 'mentorship', label: 'Mentors' },
        { key: 'code-editor', label: 'Code Lab' },
        { key: 'jobs', label: 'Jobs' },
        { key: 'hackathons', label: 'Hackathons' },
        ...(user && isStudent ? [{ key: 'student-my-courses', label: 'My Courses' }] : []),
        ...(user ? [{ key: isAdmin ? 'admin-dashboard' : (isEmployee ? 'employee-dashboard' : (isTeacher ? 'teacher-dashboard' : 'student-dashboard')), label: isAdmin ? 'Admin Dashboard' : (isEmployee ? 'Employee Dashboard' : (isTeacher ? 'Teacher Dashboard' : 'Student Dashboard')) }] : []),
      ];

  useEffect(() => {
    setAuthMenuOpen(false);
    setMobileMenuOpen(false);
    setCoursesMenuOpen(false);
  }, [currentPage]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (coursesMenuRef.current && !coursesMenuRef.current.contains(event.target)) {
        setCoursesMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const isActiveNavItem = (itemKey) => {
    if (itemKey === 'student-dashboard' || itemKey === 'teacher-dashboard' || itemKey === 'employee-dashboard') return isDashboardPage;
    if (itemKey === 'teacher-courses') return currentPage === 'teacher-courses' || currentPage === 'teacher-course-detail';
    if (itemKey === 'student-learning') return currentPage === 'student-learning' || currentPage === 'student-course-detail';
    if (itemKey === 'student-my-courses') return currentPage === 'student-my-courses' || currentPage === 'student-my-course-detail';
    if (itemKey === 'courses') return currentPage === 'courses' || currentPage === 'courses-category';
    return currentPage === itemKey;
  };
  const handleNavigate = (itemKey) => {
    setAuthMenuOpen(false);
    setMobileMenuOpen(false);
    setCoursesMenuOpen(false);
    onPageChange(itemKey);
  };

  const handleCourseGroupNavigate = (group) => {
    setCoursesMenuOpen(false);
    setMobileMenuOpen(false);
    onPageChange('courses', { path: `/courses/${group.slug}` });
  };

  const renderCourseGroupLinks = (mobile = false) => (
    <div className={mobile ? 'mt-2 space-y-1 border-l border-yellow-400/20 pl-3' : 'absolute left-0 top-full z-50 mt-3 w-80 rounded-2xl border border-yellow-400/25 bg-gray-950 p-2 shadow-2xl'}>
      {COURSE_GROUP_META_ORDERED.map((group) => (
        <button
          key={group.slug}
          type="button"
          onClick={() => handleCourseGroupNavigate(group)}
          className={`w-full rounded-xl px-3 py-2 text-left text-sm font-semibold transition-colors ${mobile ? 'text-gray-200 hover:bg-gray-900 hover:text-yellow-400' : 'text-gray-100 hover:bg-yellow-500/10 hover:text-yellow-300'}`}
        >
          {COURSE_NAV_LABELS[group.value] || group.value}
        </button>
      ))}
    </div>
  );

  return (
    <header className="bg-black border-b-2 border-yellow-400 shadow-2xl sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-3 py-2 min-h-16">
          <div className="flex items-center cursor-pointer group shrink-0" onClick={() => handleNavigate('home')}>
            <img
              src="/kra-logo.png"
              alt="K.R.A Logo"
              className="h-12 w-12 mr-3 group-hover:scale-110 transition-transform duration-300"
            />
            <div className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">K.R.A</div>
          </div>

          <nav className="hidden md:flex flex-1 flex-wrap justify-between md:justify-evenly gap-y-2 px-2">
            {mainNavItems.map(item => (
              item.key === 'courses' ? (
                <div key={item.key} ref={coursesMenuRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setCoursesMenuOpen((value) => !value)}
                    aria-haspopup="menu"
                    aria-expanded={coursesMenuOpen}
                    className={`hover:text-yellow-400 transition-colors text-sm font-medium inline-flex items-center gap-1 ${isActiveNavItem(item.key) ? 'text-yellow-400 border-b-2 border-yellow-400' : 'text-white'}`}
                  >
                    <span>{item.label}</span><span aria-hidden="true">▾</span>
                  </button>
                  {coursesMenuOpen && renderCourseGroupLinks()}
                </div>
              ) : (
                <button
                  key={item.key}
                  onClick={() => handleNavigate(item.key)}
                  className={`hover:text-yellow-400 transition-colors text-sm font-medium ${
                    isActiveNavItem(item.key) ? 'text-yellow-400 border-b-2 border-yellow-400' : 'text-white'
                  }`}
                >
                  <span>{item.label}</span>
                </button>
              )
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <button
              type="button"
              onClick={() => setMobileMenuOpen((value) => !value)}
              className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-xl border border-yellow-400/30 bg-gray-900 text-yellow-400 hover:border-yellow-400 hover:bg-gray-800"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-2">
                {mobileMenuOpen ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" /> : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
            {user ? (
              <div className="relative flex items-center">
                <button
                  onClick={() => setAuthMenuOpen(!authMenuOpen)}
                  className="flex items-center gap-3 rounded-full border border-yellow-400/20 bg-gray-900 px-3 py-2 hover:border-yellow-400 transition-all max-w-[11rem]"
                >
                  <div className="h-9 w-9 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center text-black font-bold overflow-hidden">
                    {user.profileImage || user.profile_photo_url ? (
                      <img src={resolveImageUrl(user.profileImage || user.profile_photo_url)} alt="Profile" className="h-full w-full object-cover" />
                    ) : (
                      (user.firstname || user.name || 'U').charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="hidden lg:block text-left">
                    <div className="text-yellow-400 font-medium text-sm">{user.firstname || user.name}</div>
                    <div className="text-xs text-gray-300 capitalize">{user.subscription_tier} • {isAdmin ? 'admin' : (isEmployee ? 'employee' : (isTeacher ? 'teacher' : 'student'))}</div>
                  </div>
                </button>

                {authMenuOpen && (
                  <div className="absolute right-0 top-full mt-3 w-56 rounded-2xl border border-yellow-400/20 bg-gray-950 shadow-2xl overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-gray-800">
                      <div className="text-yellow-400 font-semibold">{user.firstname || user.name}</div>
                      <div className="text-xs text-gray-400 capitalize">{isAdmin ? 'Admin' : (isEmployee ? 'Employee' : (isTeacher ? 'Teacher' : 'Student'))} account</div>
                    </div>
                    <button onClick={() => { setAuthMenuOpen(false); onPageChange('profile'); }} className="w-full text-left px-4 py-3 text-sm text-gray-200 hover:bg-gray-900 hover:text-yellow-400">Profile</button>
                    <button onClick={() => { setAuthMenuOpen(false); onPageChange(isAdmin ? 'admin-dashboard' : (isEmployee ? 'employee-dashboard' : (isTeacher ? 'teacher-dashboard' : 'student-dashboard'))); }} className="w-full text-left px-4 py-3 text-sm text-gray-200 hover:bg-gray-900 hover:text-yellow-400">Dashboard</button>
                    <button onClick={handleLogout} className="w-full text-left px-4 py-3 text-sm text-red-300 hover:bg-gray-900 hover:text-red-200">Logout</button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => onPageChange('auth')}
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black px-6 py-2 rounded-lg font-bold transition-all transform hover:scale-105 shadow-lg"
              >
                Login
              </button>
            )}
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden pb-4">
            <div className="rounded-2xl border border-yellow-400/20 bg-gray-950 p-3 shadow-2xl space-y-2">
              {mainNavItems.map((item) => (
                item.key === 'courses' ? (
                  <div key={item.key} className="rounded-xl border border-yellow-400/10 px-2 py-1">
                    <button
                      type="button"
                      onClick={() => setCoursesMenuOpen((value) => !value)}
                      aria-expanded={coursesMenuOpen}
                      className={`w-full rounded-lg px-2 py-2 text-left text-sm font-semibold transition-colors ${isActiveNavItem(item.key) ? 'text-yellow-400' : 'text-gray-100 hover:text-yellow-400'}`}
                    >
                      <span className="inline-flex items-center gap-2">{item.label}<span aria-hidden="true">{coursesMenuOpen ? '▴' : '▾'}</span></span>
                    </button>
                    {coursesMenuOpen && renderCourseGroupLinks(true)}
                  </div>
                ) : (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => handleNavigate(item.key)}
                    className={`w-full rounded-xl px-4 py-3 text-left text-sm font-semibold transition-colors ${isActiveNavItem(item.key) ? 'bg-yellow-500/15 text-yellow-400 border border-yellow-400/20' : 'text-gray-100 hover:bg-gray-900 hover:text-yellow-400'}`}
                  >
                    {item.label}
                  </button>
                )
              ))}
              {user ? (
                <>
                  <button type="button" onClick={() => handleNavigate('profile')} className="w-full rounded-xl px-4 py-3 text-left text-sm font-semibold text-gray-100 hover:bg-gray-900 hover:text-yellow-400">Profile</button>
                  <button type="button" onClick={handleLogout} className="w-full rounded-xl px-4 py-3 text-left text-sm font-semibold text-red-300 hover:bg-gray-900 hover:text-red-200">Logout</button>
                </>
              ) : (
                <button type="button" onClick={() => handleNavigate('auth')} className="w-full rounded-xl px-4 py-3 text-left text-sm font-semibold text-black bg-yellow-500 hover:bg-yellow-400">Login</button>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// AI Chatbot Component
const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [chatError, setChatError] = useState('');
  const [sessionId, setSessionId] = useState('');
  const messagesEndRef = useRef(null);
  const sendingRef = useRef(false);
  const sessionStorageKey = 'kra_chat_session_id';
  const { user } = useAuth();

  const getHistoryStorageKey = (sid) => `kra_chat_history_${sid}`;

  const writeCachedHistory = (sid, rows) => {
    if (!sid) return;
    try {
      localStorage.setItem(getHistoryStorageKey(sid), JSON.stringify(rows || []));
    } catch (error) {
      // Ignore storage failures.
    }
  };

  const readCachedHistory = (sid) => {
    if (!sid) return [];
    try {
      const raw = localStorage.getItem(getHistoryStorageKey(sid));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  useEffect(() => {
    let stored = '';
    try {
      stored = localStorage.getItem(sessionStorageKey) || '';
    } catch (error) {
      stored = '';
    }
    if (stored) {
      setSessionId(stored);
      return;
    }
    const base = user?.id ? `user_${user.id}` : 'guest';
    const generated = `${base}_${Date.now()}`;
    setSessionId(generated);
    try {
      localStorage.setItem(sessionStorageKey, generated);
    } catch (error) {
      // Ignore storage failures.
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isOpen || !sessionId) return;
    const loadHistory = async () => {
      const cachedRows = readCachedHistory(sessionId)
        .filter((row) => row?.role && row?.content)
        .map((row) => ({
          id: row.id,
          role: row.role,
          content: row.content,
          timestamp: row.created_at ? new Date(row.created_at) : new Date(),
        }));
      if (cachedRows.length) {
        setMessages(cachedRows);
      }

      try {
        const response = await axios.get(`${API}/chat/history`, {
          params: { session_id: sessionId, limit: 120 },
        });
        const rows = response.data?.messages || [];
        writeCachedHistory(sessionId, rows);
        setMessages(
          rows
            .filter((row) => row?.role && row?.content)
            .map((row) => ({
              id: row.id,
              role: row.role,
              content: row.content,
              timestamp: row.created_at ? new Date(row.created_at) : new Date(),
            }))
        );
        setShowSupport(rows.some((row) => row?.suggest_support));
      } catch (error) {
        setChatError(error.response?.data?.detail || 'Unable to load chat history');
      }
    };
    loadHistory();
  }, [isOpen, sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    const rows = (messages || []).map((row) => ({
      id: row.id || `${row.role}_${row.timestamp ? new Date(row.timestamp).getTime() : Date.now()}`,
      role: row.role,
      content: row.content,
      created_at: row.timestamp || new Date(),
      suggest_support: false,
    }));
    writeCachedHistory(sessionId, rows);
  }, [messages, sessionId]);

  useEffect(() => {
    if (!isOpen) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, loading, isOpen]);

  const addAssistantMessage = (text) => {
    const content = (text || '').trim();
    if (!content) return;
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.role === 'assistant' && (last?.content || '').trim() === content) {
        return prev;
      }
      return [...prev, { role: 'assistant', content, timestamp: new Date() }];
    });
  };

  const sendMessage = async () => {
    const trimmed = inputMessage.trim();
    if (!trimmed || loading || sendingRef.current) return;
    sendingRef.current = true;

    const userMessage = { role: 'user', content: trimmed, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    setChatError('');

    const activeSessionId = sessionId || `${user?.id ? `user_${user.id}` : 'guest'}_${Date.now()}`;
    if (!sessionId) {
      setSessionId(activeSessionId);
      try {
        localStorage.setItem(sessionStorageKey, activeSessionId);
      } catch (error) {
        // Ignore storage failures.
      }
    }

    try {
      const response = await axios.post(`${API}/chat`, {
        message: trimmed,
        session_id: activeSessionId,
        include_history: false,
      });

      addAssistantMessage(response.data?.response || 'I did not catch that. Please ask again.');
      if (response.data?.suggest_support) {
        setShowSupport(true);
      }
    } catch (error) {
      setChatError(error.response?.data?.detail || 'Sorry, I\'m having trouble responding right now. Please try again.');
      addAssistantMessage('Sorry, I\'m having trouble responding right now. Please try again later.');
    } finally {
      setLoading(false);
      sendingRef.current = false;
    }
  };

  const clearConversation = async () => {
    if (!sessionId) {
      setMessages([]);
      setShowSupport(false);
      return;
    }
    try {
      setLoading(true);
      await axios.delete(`${API}/chat/history`, { params: { session_id: sessionId } });
      setMessages([]);
      setShowSupport(false);
      setChatError('');
      try {
        localStorage.removeItem(getHistoryStorageKey(sessionId));
      } catch (error) {
        // Ignore storage failures.
      }
    } catch (error) {
      setChatError(error.response?.data?.detail || 'Unable to clear chat history');
    } finally {
      setLoading(false);
    }
  };

  const renderChatContent = (text) => {
    const raw = String(text || '');
    const urlPattern = /(https?:\/\/[^\s]+)|(\/(?:[a-z0-9-]+(?:\/[a-z0-9-]+)*)?)/gi;
    const nodes = [];
    let lastIndex = 0;
    let match;

    while ((match = urlPattern.exec(raw)) !== null) {
      const [fullMatch, absoluteUrl, relativePath] = match;
      const start = match.index;
      if (start > lastIndex) {
        nodes.push(raw.slice(lastIndex, start));
      }

      const normalizedLink = absoluteUrl
        ? (() => {
            try {
              const parsed = new URL(fullMatch);
              if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') {
                return `${window.location.origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
              }
              return fullMatch;
            } catch {
              return fullMatch;
            }
          })()
        : `${window.location.origin}${relativePath}`;
      const displayText = absoluteUrl
        ? normalizedLink
        : `${window.location.origin}${relativePath}`;

      nodes.push(
        <a
          key={`${start}-${fullMatch}`}
          href={normalizedLink}
          target="_blank"
          rel="noreferrer"
          className="text-yellow-300 underline underline-offset-2 break-all"
        >
          {displayText}
        </a>
      );
      lastIndex = start + fullMatch.length;
    }

    if (lastIndex < raw.length) {
      nodes.push(raw.slice(lastIndex));
    }

    return nodes.length ? nodes : raw;
  };

  const escalateSupport = async () => {
    try {
      setLoading(true);
      const recent = messages.slice(-10).map(m => ({ role: m.role, content: m.content }));
      const res = await axios.post(`${API}/support/escalate`, { messages: recent });
      const { status, smtp_configured, support_email } = res.data || {};
      if (status === 'sent' && smtp_configured) {
        console.log('Support email sent successfully.');
        return;
      }
      const to = support_email || 'K.R.A_initiativeinc@gmail.com';
      const subject = encodeURIComponent('KRA Support Request');
      const body = encodeURIComponent(messages.map(m => `${m.role}: ${m.content}`).join('\n\n'));
      window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
    } catch (e) {
      console.error('Failed to escalate support:', e);
      const to = 'K.R.A_initiativeinc@gmail.com';
      const subject = encodeURIComponent('KRA Support Request');
      const body = encodeURIComponent(messages.map(m => `${m.role}: ${m.content}`).join('\n\n'));
      window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
    } finally {
      setLoading(false);
      setShowSupport(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black p-4 rounded-full shadow-lg z-50 transform hover:scale-110 transition-all"
      >
        <div className="text-2xl">🤖</div>
      </button>

      {isOpen && (
        <div className="fixed bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-96 h-[70vh] max-h-[620px] bg-black border-2 border-yellow-400 rounded-lg shadow-2xl z-50 flex flex-col">
          <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-4 py-3 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="font-bold">K.R.A Assistant</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={clearConversation}
                  disabled={loading || messages.length === 0}
                  className="bg-black text-yellow-400 px-2 py-1 rounded text-sm disabled:opacity-50"
                >
                  Clear
                </button>
                <button
                  onClick={escalateSupport}
                  disabled={!user}
                  className={`bg-black text-yellow-400 px-2 py-1 rounded text-sm ${!user ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-900'}`}
                >
                  Email Support
                </button>
                <button onClick={() => setIsOpen(false)} className="text-black hover:text-gray-700">✕</button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {chatError && <div className="text-xs text-red-400 bg-red-950/40 border border-red-700 rounded p-2">{chatError}</div>}
            {messages.length === 0 && (
              <div className="text-gray-400 text-center">
                👋 Hi! I'm your K.R.A learning assistant.
                {user ? ' How can I help you today?' : ' Please sign in to send messages. You can still read tips here.'}
              </div>
            )}
            {messages.map((message, index) => (
              <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-xs px-3 py-2 rounded-lg ${message.role === 'user' ? 'bg-yellow-500 text-black' : 'bg-gray-700 text-white'}`}>
                  {renderChatContent(message.content)}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-700 text-white px-3 py-2 rounded-lg">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-4 border-t border-yellow-400">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Ask me anything..."
                className="flex-1 bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
              />
              <button
                onClick={sendMessage}
                disabled={loading || !inputMessage.trim()}
                className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send
              </button>
            </div>
            {showSupport && (
              <button onClick={escalateSupport} className="mt-3 w-full text-sm text-yellow-400 hover:text-yellow-300">
                Need human support? Escalate conversation
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const OtpBoxes = ({ value, onChange, disabled = false }) => {
  const chars = Array.from({ length: 6 }, (_, i) => value[i] || '');
  const setAt = (index, next) => {
    const onlyDigit = (next || '').replace(/\D/g, '').slice(-1);
    const nextChars = [...chars];
    nextChars[index] = onlyDigit;
    onChange(nextChars.join(''));
    if (onlyDigit) {
      const nextInput = document.getElementById(`otp-box-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  return (
    <div className="flex items-center justify-center gap-2">
      {chars.map((digit, index) => (
        <input
          key={index}
          id={`otp-box-${index}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          disabled={disabled}
          value={digit}
          onChange={(e) => setAt(index, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !chars[index] && index > 0) {
              const prevInput = document.getElementById(`otp-box-${index - 1}`);
              if (prevInput) prevInput.focus();
            }
          }}
          className="w-11 h-12 text-center text-xl font-bold bg-gray-800 text-yellow-400 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
        />
      ))}
    </div>
  );
};

// Authentication Component - role-aware Khan Academy style flow
const AuthPage = ({ onSuccess, onPageChange }) => {
  const { login } = useAuth();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [bio, setBio] = useState('');
  const [parentFirstName, setParentFirstName] = useState('');
  const [parentLastName, setParentLastName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentEmailConfirm, setParentEmailConfirm] = useState('');
  const [parentCellPhone, setParentCellPhone] = useState('');
  const [parentWorkPhone, setParentWorkPhone] = useState('');
  const [parentHomePhone, setParentHomePhone] = useState('');
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [secondaryName, setSecondaryName] = useState('');
  const [secondaryPhone, setSecondaryPhone] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePreview, setProfilePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [forgotOtpSent, setForgotOtpSent] = useState(false);
  const [forgotResendAvailableAt, setForgotResendAvailableAt] = useState(null);
  const [pendingRegistrationSessionId, setPendingRegistrationSessionId] = useState('');
  const [registrationOtp, setRegistrationOtp] = useState('');
  const [registrationResendAvailableAt, setRegistrationResendAvailableAt] = useState(null);
  const [timerNow, setTimerNow] = useState(Date.now());
  const [toast, setToast] = useState({ open: false, message: '', type: 'info' });
  const [authErrors, setAuthErrors] = useState({});
  const [authFeedback, setAuthFeedback] = useState({ message: '', type: 'info' });

  const roleCards = [
    { role: 'student', title: 'Continue as Student', description: 'Access your learning dashboard, assignments, saved courses, and notifications.' },
    { role: 'teacher', title: 'Continue as Teacher', description: 'Sign in with your official K.R.A teacher account created by an administrator.' },
    { role: 'employee', title: 'K.R.A Employee Portal', description: 'Internal staff sign-in only. Contact an administrator if you need access.', compact: true },
  ];

  useEffect(() => {
    const timer = window.setInterval(() => setTimerNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const mode = String(params.get('mode') || '').toLowerCase();
    const role = String(params.get('role') || '').toLowerCase();
    if (role === 'student' || role === 'teacher' || role === 'employee') {
      setSelectedRole(role);
    }
    if (mode === 'register') {
      setIsLogin(false);
    } else if (mode === 'login' || mode === 'signin') {
      setIsLogin(true);
    }
  }, [location.search]);

  useEffect(() => {
    if (!authFeedback?.message || authFeedback.type === 'error') return;
    const timer = window.setTimeout(() => setAuthFeedback({ message: '', type: 'info' }), 3500);
    return () => window.clearTimeout(timer);
  }, [authFeedback]);

  const roleLabel = selectedRole === 'teacher' ? 'Teacher' : (selectedRole === 'employee' ? 'Employee' : 'Student');
  const normalizedRoleEmail = (rawEmail, role = selectedRole) => {
    const trimmed = String(rawEmail || '').trim().toLowerCase();
    if ((role || '').toLowerCase() === 'teacher' || (role || '').toLowerCase() === 'employee') {
      return trimmed.includes('@') ? trimmed : `${trimmed}@krainitiative.com`;
    }
    return trimmed;
  };

  const secondsLeft = (targetTime) => {
    if (!targetTime) return 0;
    return Math.max(0, Math.ceil((targetTime - timerNow) / 1000));
  };

  const showToast = (msg, type = 'info') => {
    setToast({ open: true, message: msg, type });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast((t) => ({ ...t, open: false })), 4000);
  };

  const clearForm = () => {
    setEmail('');
    setPassword('');
    setFirstname('');
    setLastname('');
    setBio('');
    setParentFirstName('');
    setParentLastName('');
    setParentEmail('');
    setParentEmailConfirm('');
    setParentCellPhone('');
    setParentWorkPhone('');
    setParentHomePhone('');
    setEmergencyName('');
    setEmergencyPhone('');
    setSecondaryName('');
    setSecondaryPhone('');
    setProfilePhoto(null);
    setProfilePreview('');
    setAuthErrors({});
    setAuthFeedback({ message: '', type: 'info' });
  };

  const clearAuthError = (field) => {
    setAuthErrors((current) => ({ ...current, [field]: '' }));
  };

  const setFieldError = (field, messageText) => {
    setAuthErrors((current) => ({ ...current, [field]: messageText }));
  };

  const applyAuthFailure = (errorMessage) => {
    const messageText = String(errorMessage || 'Authentication failed');
    const nextErrors = {};
    if (/email/i.test(messageText)) nextErrors.email = messageText;
    if (/password/i.test(messageText)) nextErrors.password = messageText;
    if (/otp|code/i.test(messageText)) {
      nextErrors.resetOtp = messageText;
      nextErrors.registrationOtp = messageText;
    }
    if (/verify|verified|verification/i.test(messageText) && !nextErrors.email) {
      nextErrors.email = messageText;
    }
    setAuthErrors(nextErrors);
    return messageText;
  };

  const startForgotPasswordOtp = async () => {
    if (!resetEmail.trim()) {
      setMessage('Enter the email address on your account');
      setAuthFeedback({ message: 'Enter the email address on your account.', type: 'error' });
      setFieldError('resetEmail', 'Enter the email address on your account');
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/forgot-password/request`, { email: normalizedRoleEmail(resetEmail) });
      setForgotOtpSent(true);
      setForgotResendAvailableAt(Date.now() + (response.data?.resend_available_in_seconds || 60) * 1000);
      setMessage(response.data?.message || 'Verification code sent');
      setAuthFeedback({ message: response.data?.message || 'Verification code sent.', type: 'success' });
      showToast('OTP sent to your email', 'info');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Unable to send verification code';
      setMessage(applyAuthFailure(errorMessage));
      setAuthFeedback({ message: errorMessage, type: 'error' });
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const resendForgotPasswordOtp = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/forgot-password/resend-otp`, { email: normalizedRoleEmail(resetEmail) });
      setForgotResendAvailableAt(Date.now() + (response.data?.resend_available_in_seconds || 60) * 1000);
      setMessage(response.data?.message || 'A new verification code was sent');
      setAuthFeedback({ message: response.data?.message || 'A new verification code was sent.', type: 'success' });
      showToast('A new OTP has been sent', 'info');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Unable to resend code';
      setMessage(applyAuthFailure(errorMessage));
      setAuthFeedback({ message: errorMessage, type: 'error' });
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const finishForgotPasswordReset = async () => {
    if (resetOtp.length !== 6) {
      setMessage('Enter the 6-digit verification code');
      setAuthFeedback({ message: 'Enter the 6-digit verification code.', type: 'error' });
      setFieldError('resetOtp', 'Enter the 6-digit verification code');
      return;
    }
    if (!resetNewPassword || resetNewPassword !== resetConfirmPassword) {
      setMessage('Passwords do not match');
      setAuthFeedback({ message: 'Passwords do not match.', type: 'error' });
      setFieldError('resetNewPassword', 'Passwords do not match');
      setFieldError('resetConfirmPassword', 'Passwords do not match');
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/forgot-password/reset`, {
        email: normalizedRoleEmail(resetEmail),
        otp: resetOtp,
        password: resetNewPassword,
      });
      setMessage(response.data?.message || 'Password reset successful');
      setAuthFeedback({ message: response.data?.message || 'Password reset successful.', type: 'success' });
      showToast('Password reset successful', 'info');
      setShowForgotPassword(false);
      setForgotOtpSent(false);
      setResetOtp('');
      setResetNewPassword('');
      setResetConfirmPassword('');
      setIsLogin(true);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Unable to reset password';
      setMessage(applyAuthFailure(errorMessage));
      setAuthFeedback({ message: errorMessage, type: 'error' });
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const requestRegistrationOtp = async () => {
    if (selectedRole === 'teacher') {
      setMessage('Teacher accounts are created by administrators. Please sign in with your official @krainitiative.com account.');
      setAuthFeedback({ message: 'Teacher accounts are admin-managed. Please sign in with your official account.', type: 'warning' });
      return;
    }
    if (!firstname || !lastname || !email || !password) {
      setMessage('Please fill in first name, last name, email, and password');
      setAuthFeedback({ message: 'Please complete all required fields before continuing.', type: 'error' });
      if (!firstname) setFieldError('firstname', 'Required');
      if (!lastname) setFieldError('lastname', 'Required');
      if (!email) setFieldError('email', 'Required');
      if (!password) setFieldError('password', 'Required');
      return;
    }
    if (selectedRole === 'student') {
      const requiredContactFields = [
        [parentFirstName, 'parentFirstName', 'Parent first name is required'],
        [parentLastName, 'parentLastName', 'Parent last name is required'],
        [parentEmail, 'parentEmail', 'Parent email is required'],
        [parentCellPhone, 'parentCellPhone', 'Parent cell phone is required'],
        [emergencyName, 'emergencyName', 'Emergency contact name is required'],
        [emergencyPhone, 'emergencyPhone', 'Emergency contact phone is required'],
      ];
      const missingField = requiredContactFields.find(([value]) => !String(value || '').trim());
      if (missingField) {
        setMessage(missingField[2]);
        setAuthFeedback({ message: missingField[2], type: 'error' });
        setFieldError(missingField[1], missingField[2]);
        return;
      }
      if (parentEmail.trim().toLowerCase() !== parentEmailConfirm.trim().toLowerCase()) {
        setMessage('Parent email addresses do not match.');
        setAuthFeedback({ message: 'Parent email addresses do not match.', type: 'error' });
        setFieldError('parentEmailConfirm', 'Parent email addresses do not match.');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parentEmail.trim())) {
        setMessage('Enter a valid parent email address.');
        setAuthFeedback({ message: 'Enter a valid parent email address.', type: 'error' });
        setFieldError('parentEmail', 'Enter a valid email address.');
        return;
      }
    }
    if (!profilePhoto) {
      setMessage('Profile photo is required');
      setAuthFeedback({ message: 'Profile photo is required.', type: 'error' });
      setFieldError('profilePhoto', 'Required');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('firstname', firstname.trim());
      formData.append('lastname', lastname.trim());
      formData.append('name', `${firstname.trim()} ${lastname.trim()}`.trim());
      formData.append('bio', bio.trim());
      formData.append('email', normalizedRoleEmail(email));
      formData.append('password', password);
      formData.append('role', selectedRole);
      formData.append('profile_photo', profilePhoto);
      if (selectedRole === 'student') {
        formData.append('parent_first_name', parentFirstName.trim());
        formData.append('parent_last_name', parentLastName.trim());
        formData.append('parent_email', parentEmail.trim().toLowerCase());
        formData.append('parent_email_confirm', parentEmailConfirm.trim().toLowerCase());
        formData.append('parent_cell_phone', parentCellPhone.trim());
        formData.append('parent_work_phone', parentWorkPhone.trim());
        formData.append('parent_home_phone', parentHomePhone.trim());
        formData.append('emergency_name', emergencyName.trim());
        formData.append('emergency_phone', emergencyPhone.trim());
        formData.append('secondary_name', secondaryName.trim());
        formData.append('secondary_phone', secondaryPhone.trim());
      }

      const response = await axios.post(`${API}/auth/register-with-docs/request-otp`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setPendingRegistrationSessionId(response.data?.session_id || '');
      setRegistrationOtp('');
      setRegistrationResendAvailableAt(Date.now() + (response.data?.resend_available_in_seconds || 60) * 1000);
      setMessage(response.data?.message || 'Verification code sent to your email');
      setAuthFeedback({ message: response.data?.message || 'Verification code sent to your email.', type: 'success' });
      showToast('Verify your email with OTP to finish account creation', 'info');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Unable to start account verification';
      setMessage(applyAuthFailure(errorMessage));
      setAuthFeedback({ message: errorMessage, type: 'error' });
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const verifyRegistrationOtp = async () => {
    if (!pendingRegistrationSessionId) {
      setMessage('Verification session missing. Please start again.');
      setAuthFeedback({ message: 'Verification session missing. Please start again.', type: 'error' });
      return;
    }
    if (registrationOtp.length !== 6) {
      setMessage('Enter the 6-digit verification code');
      setAuthFeedback({ message: 'Enter the 6-digit verification code.', type: 'error' });
      setFieldError('registrationOtp', 'Enter the 6-digit verification code');
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/register-with-docs/verify-otp`, {
        session_id: pendingRegistrationSessionId,
        otp: registrationOtp,
      });
      setPendingRegistrationSessionId('');
      setRegistrationOtp('');
      setIsLogin(true);
      setPassword('');
      setAuthErrors({});
      const successMessage = response.data?.message || 'Account created successfully. Redirecting to sign in...';
      setMessage(successMessage);
      setAuthFeedback({ message: successMessage, type: 'success' });
      showToast('Account created successfully. Please sign in to continue enrollment.', 'info');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Unable to verify code';
      setMessage(applyAuthFailure(errorMessage));
      setAuthFeedback({ message: errorMessage, type: 'error' });
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const resendRegistrationOtp = async () => {
    if (!pendingRegistrationSessionId) return;
    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/register-with-docs/resend-otp`, {
        session_id: pendingRegistrationSessionId,
      });
      setRegistrationResendAvailableAt(Date.now() + (response.data?.resend_available_in_seconds || 60) * 1000);
      setMessage(response.data?.message || 'A new verification code was sent');
      setAuthFeedback({ message: response.data?.message || 'A new verification code was sent.', type: 'success' });
      showToast('A new OTP has been sent', 'info');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Unable to resend code';
      setMessage(applyAuthFailure(errorMessage));
      setAuthFeedback({ message: errorMessage, type: 'error' });
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedRole) {
      setMessage('Choose Student, Teacher, or Employee to continue');
      setAuthFeedback({ message: 'Choose a role to continue.', type: 'error' });
      return;
    }

    if (showForgotPassword) {
      if (!forgotOtpSent) {
        await startForgotPasswordOtp();
      } else {
        await finishForgotPasswordReset();
      }
      return;
    }

    if (isLogin) {
      if (!email || !password) {
        setMessage('Please enter email and password');
        setAuthFeedback({ message: 'Please enter email and password.', type: 'error' });
        if (!email) setFieldError('email', 'Required');
        if (!password) setFieldError('password', 'Required');
        return;
      }
      setLoading(true);
      const loginEmail = normalizedRoleEmail(email);
      const result = await login(loginEmail, password);
      setLoading(false);
      if (result.success) {
        setAuthFeedback({ message: 'Login successful.', type: 'success' });
        onSuccess(result.user);
      } else {
        const errorMessage = applyAuthFailure(result.error || 'Login failed');
        setMessage(errorMessage);
        setAuthFeedback({ message: errorMessage, type: 'error' });
        showToast(errorMessage, 'error');
      }
      return;
    }

    if (pendingRegistrationSessionId) {
      await verifyRegistrationOtp();
      return;
    }

    await requestRegistrationOtp();
  };

  const resetToRoleSelection = () => {
    setSelectedRole(null);
    setShowForgotPassword(false);
    setForgotOtpSent(false);
    setPendingRegistrationSessionId('');
    setMessage('');
    clearForm();
  };

  const forgotCooldown = secondsLeft(forgotResendAvailableAt);
  const registrationCooldown = secondsLeft(registrationResendAvailableAt);

  return (
    <div className="bg-black min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(250,204,21,0.15),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(17,24,39,0.9),transparent_35%)]"></div>
      <div className="relative w-full max-w-5xl grid lg:grid-cols-[1.1fr_0.9fr] gap-6 items-stretch">
        <div className="hidden lg:flex flex-col justify-between rounded-3xl border border-yellow-400/20 bg-gradient-to-br from-gray-950 via-gray-900 to-black p-8 shadow-2xl">
          <div>
            <img src="/kra-logo.png" alt="K.R.A Logo" className="h-16 w-16 mb-6" />
            <p className="text-yellow-400 text-sm uppercase tracking-[0.3em] mb-4">K.R.A Learning Platform</p>
            <h2 className="text-5xl font-bold text-white leading-tight mb-4">Secure verification for learners.</h2>
            <p className="text-gray-300 text-lg leading-relaxed max-w-xl">Login remains fast with email and password. OTP is only required for signup, password reset, and account deletion.</p>
          </div>
        </div>

        <div className="w-full bg-gray-900/95 backdrop-blur rounded-3xl shadow-2xl p-6 sm:p-8 border border-yellow-400/30">
          <div className="text-center mb-8">
            <img src="/kra-logo.png" alt="K.R.A Logo" className="mx-auto h-16 w-16 mb-4" />
            <h2 className="text-3xl font-bold text-yellow-400">{selectedRole ? `${roleLabel} Access` : 'Welcome to K.R.A'}</h2>
            <p className="text-gray-400 mt-2">{selectedRole ? (isLogin ? 'Sign in to continue your learning journey' : 'Create your account with email verification') : 'Choose how you want to continue'}</p>
          </div>

          {!selectedRole ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {roleCards.map((card) => (
                <button key={card.role} type="button" onClick={() => { setSelectedRole(card.role); setIsLogin(true); setShowForgotPassword(false); setMessage(''); }} className={`text-left rounded-2xl border border-yellow-400/20 bg-gradient-to-b from-gray-800 to-gray-950 hover:border-yellow-400 hover:shadow-lg transition-all duration-300 ${card.compact ? 'p-3 opacity-80 sm:col-span-2 sm:max-w-xs sm:mx-auto' : 'p-5'}`}>
                  <div className={`text-yellow-400 uppercase tracking-[0.2em] mb-2 ${card.compact ? 'text-[10px]' : 'text-sm'}`}>{card.role}</div>
                  <div className={`text-white font-bold mb-2 ${card.compact ? 'text-sm' : 'text-xl'}`}>{card.title}</div>
                  <div className={`text-gray-300 leading-relaxed ${card.compact ? 'text-xs' : 'text-sm'}`}>{card.description}</div>
                </button>
              ))}
            </div>
          ) : showForgotPassword ? (
            <div className="space-y-4">
              <div>
                <label className="block text-yellow-400 text-sm font-medium mb-2">Account Email</label>
                <input type="email" value={resetEmail} onChange={(e) => { setResetEmail(e.target.value); clearAuthError('resetEmail'); }} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border focus:border-yellow-400 focus:outline-none transition-colors ${authErrors.resetEmail ? 'border-red-500' : 'border-gray-600'}`} placeholder="Enter the email on your account" />
                {authErrors.resetEmail && <div className="mt-1 text-sm text-red-400">{authErrors.resetEmail}</div>}
              </div>

              {forgotOtpSent && (
                <>
                  <div>
                    <label className="block text-yellow-400 text-sm font-medium mb-2">Verification Code</label>
                    <OtpBoxes value={resetOtp} onChange={(value) => { setResetOtp(value); clearAuthError('resetOtp'); }} disabled={loading} />
                    {authErrors.resetOtp && <div className="mt-1 text-sm text-red-400">{authErrors.resetOtp}</div>}
                  </div>
                  <div>
                    <label className="block text-yellow-400 text-sm font-medium mb-2">New Password</label>
                    <input type="password" value={resetNewPassword} onChange={(e) => { setResetNewPassword(e.target.value); clearAuthError('resetNewPassword'); }} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border focus:border-yellow-400 focus:outline-none transition-colors ${authErrors.resetNewPassword ? 'border-red-500' : 'border-gray-600'}`} minLength={8} placeholder="Enter new password" />
                    {authErrors.resetNewPassword && <div className="mt-1 text-sm text-red-400">{authErrors.resetNewPassword}</div>}
                  </div>
                  <div>
                    <label className="block text-yellow-400 text-sm font-medium mb-2">Confirm Password</label>
                    <input type="password" value={resetConfirmPassword} onChange={(e) => { setResetConfirmPassword(e.target.value); clearAuthError('resetConfirmPassword'); }} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border focus:border-yellow-400 focus:outline-none transition-colors ${authErrors.resetConfirmPassword ? 'border-red-500' : 'border-gray-600'}`} minLength={8} placeholder="Confirm new password" />
                    {authErrors.resetConfirmPassword && <div className="mt-1 text-sm text-red-400">{authErrors.resetConfirmPassword}</div>}
                  </div>
                  <button type="button" disabled={loading || forgotCooldown > 0} onClick={resendForgotPasswordOtp} className="w-full border border-yellow-400 text-yellow-400 py-2 rounded-lg disabled:opacity-50">
                    {forgotCooldown > 0 ? `Resend OTP in ${forgotCooldown}s` : 'Resend OTP'}
                  </button>
                </>
              )}

              <button className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold py-3 px-4 rounded-lg transition-all disabled:opacity-50" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Processing...' : (forgotOtpSent ? 'Verify OTP & Reset Password' : 'Send Reset OTP')}
              </button>
              <InlineFeedback feedback={authFeedback} />
              <button type="button" onClick={() => { setShowForgotPassword(false); setForgotOtpSent(false); setResetOtp(''); setResetNewPassword(''); setResetConfirmPassword(''); setMessage(''); }} className="w-full text-yellow-400 hover:text-yellow-300 transition-colors">Back to sign in</button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between rounded-2xl bg-gray-800/80 border border-yellow-400/15 px-4 py-3">
                <div>
                  <div className="text-sm text-gray-300">Selected role</div>
                  <div className="text-yellow-400 font-semibold capitalize">{selectedRole}</div>
                </div>
                <button type="button" onClick={resetToRoleSelection} className="text-sm text-gray-300 hover:text-yellow-400 transition-colors">Change role</button>
              </div>

              {!isLogin && !pendingRegistrationSessionId && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-yellow-400 text-sm font-medium mb-2">First Name</label>
                    <input type="text" value={firstname} onChange={(e) => { setFirstname(e.target.value); clearAuthError('firstname'); }} className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border focus:border-yellow-400 focus:outline-none transition-colors ${authErrors.firstname ? 'border-red-500' : 'border-gray-600'}`} placeholder="Enter first name" />
                    {authErrors.firstname && <div className="mt-1 text-sm text-red-400">{authErrors.firstname}</div>}
                  </div>
                  <div>
                    <label className="block text-yellow-400 text-sm font-medium mb-2">Last Name</label>
                    <input type="text" value={lastname} onChange={(e) => { setLastname(e.target.value); clearAuthError('lastname'); }} className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border focus:border-yellow-400 focus:outline-none transition-colors ${authErrors.lastname ? 'border-red-500' : 'border-gray-600'}`} placeholder="Enter last name" />
                    {authErrors.lastname && <div className="mt-1 text-sm text-red-400">{authErrors.lastname}</div>}
                  </div>
                </div>
              )}

              {!pendingRegistrationSessionId && (
                <>
                  <div>
                    <label className="block text-yellow-400 text-sm font-medium mb-2">Email</label>
                    {(selectedRole === 'teacher' || selectedRole === 'employee') ? (
                      <div className="flex items-stretch rounded-lg border border-gray-600 overflow-hidden bg-gray-800 focus-within:border-yellow-400">
                        <input
                          type="text"
                          value={email}
                          onChange={(e) => {
                            const nextValue = e.target.value || '';
                            setEmail(nextValue.replace(/@krainitiative\.com$/i, ''));
                            clearAuthError('email');
                          }}
                          autoComplete="username"
                          autoCapitalize="none"
                          autoCorrect="off"
                          spellCheck={false}
                          className={`flex-1 bg-gray-800 text-white px-4 py-3 focus:outline-none ${authErrors.email ? 'border-red-500' : ''}`}
                          placeholder="firstname.lastname"
                        />
                        <span className="px-4 py-3 text-yellow-400 bg-gray-900 border-l border-gray-700">@krainitiative.com</span>
                      </div>
                    ) : (
                      <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); clearAuthError('email'); }} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border focus:border-yellow-400 focus:outline-none transition-colors ${authErrors.email ? 'border-red-500' : 'border-gray-600'}`} placeholder="Enter your email" />
                    )}
                    {authErrors.email && <div className="mt-1 text-sm text-red-400">{authErrors.email}</div>}
                  </div>

                  {!isLogin && <div><label className="block text-yellow-400 text-sm font-medium mb-2">Short Bio</label><textarea value={bio} onChange={(e) => setBio(e.target.value)} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none transition-colors min-h-[96px]" placeholder={selectedRole === 'teacher' ? 'Tell students about your teaching focus' : 'Share your learning goals'} /></div>}

                  <div>
                    <label className="block text-yellow-400 text-sm font-medium mb-2">Password</label>
                    <input type="password" value={password} onChange={(e) => { setPassword(e.target.value); clearAuthError('password'); }} autoComplete={isLogin ? 'current-password' : 'new-password'} autoCapitalize="none" autoCorrect="off" spellCheck={false} className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border focus:border-yellow-400 focus:outline-none transition-colors ${authErrors.password ? 'border-red-500' : 'border-gray-600'}`} placeholder="Enter your password" minLength={8} />
                    {authErrors.password && <div className="mt-1 text-sm text-red-400">{authErrors.password}</div>}
                  </div>

                  {!isLogin && selectedRole === 'student' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-yellow-400 text-sm font-medium mb-2">Profile Photo (JPG/PNG, max 5MB)</label>
                        <input type="file" accept="image/*" onChange={(e) => { const file = e.target.files?.[0]; if (!file) return; setProfilePhoto(file); setProfilePreview(URL.createObjectURL(file)); clearAuthError('profilePhoto'); }} className={`w-full bg-gray-800 text-white px-4 py-2 rounded-lg border focus:border-yellow-400 focus:outline-none ${authErrors.profilePhoto ? 'border-red-500' : 'border-gray-600'}`} />
                        {authErrors.profilePhoto && <div className="mt-1 text-sm text-red-400">{authErrors.profilePhoto}</div>}
                        {profilePreview && <img src={profilePreview} alt="Profile Preview" className="mt-2 w-24 h-24 rounded-full object-cover border-2 border-yellow-400" />}
                      </div>
                    </div>
                  )}

                  {!isLogin && selectedRole === 'student' && (
                    <div className="space-y-5 rounded-2xl border border-yellow-400/15 bg-gray-800/50 p-4 sm:p-5">
                      <div>
                        <h3 className="text-lg font-bold text-yellow-400">Parent / Guardian Information</h3>
                        <p className="text-xs text-gray-400 mt-1">Required contact details for the student account.</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-yellow-400 text-sm font-medium mb-2">Parent Email *</label>
                          <input type="email" value={parentEmail} onChange={(e) => { setParentEmail(e.target.value); clearAuthError('parentEmail'); }} className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border ${authErrors.parentEmail ? 'border-red-500' : 'border-gray-600'}`} placeholder="parent@example.com" />
                          {authErrors.parentEmail && <div className="mt-1 text-sm text-red-400">{authErrors.parentEmail}</div>}
                        </div>
                        <div>
                          <label className="block text-yellow-400 text-sm font-medium mb-2">Confirm Parent Email *</label>
                          <input type="email" value={parentEmailConfirm} onChange={(e) => { setParentEmailConfirm(e.target.value); clearAuthError('parentEmailConfirm'); }} className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border ${authErrors.parentEmailConfirm ? 'border-red-500' : 'border-gray-600'}`} placeholder="Confirm parent email" />
                          {authErrors.parentEmailConfirm && <div className="mt-1 text-sm text-red-400">{authErrors.parentEmailConfirm}</div>}
                        </div>
                        <div>
                          <label className="block text-yellow-400 text-sm font-medium mb-2">Parent First Name *</label>
                          <input type="text" value={parentFirstName} onChange={(e) => { setParentFirstName(e.target.value); clearAuthError('parentFirstName'); }} className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border ${authErrors.parentFirstName ? 'border-red-500' : 'border-gray-600'}`} placeholder="First name" />
                          {authErrors.parentFirstName && <div className="mt-1 text-sm text-red-400">{authErrors.parentFirstName}</div>}
                        </div>
                        <div>
                          <label className="block text-yellow-400 text-sm font-medium mb-2">Parent Last Name *</label>
                          <input type="text" value={parentLastName} onChange={(e) => { setParentLastName(e.target.value); clearAuthError('parentLastName'); }} className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border ${authErrors.parentLastName ? 'border-red-500' : 'border-gray-600'}`} placeholder="Last name" />
                          {authErrors.parentLastName && <div className="mt-1 text-sm text-red-400">{authErrors.parentLastName}</div>}
                        </div>
                        <div>
                          <label className="block text-yellow-400 text-sm font-medium mb-2">Cell Phone *</label>
                          <input type="tel" value={parentCellPhone} onChange={(e) => { setParentCellPhone(e.target.value); clearAuthError('parentCellPhone'); }} className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border ${authErrors.parentCellPhone ? 'border-red-500' : 'border-gray-600'}`} placeholder="Cell phone" />
                          {authErrors.parentCellPhone && <div className="mt-1 text-sm text-red-400">{authErrors.parentCellPhone}</div>}
                        </div>
                        <div>
                          <label className="block text-gray-300 text-sm font-medium mb-2">Work Phone</label>
                          <input type="tel" value={parentWorkPhone} onChange={(e) => setParentWorkPhone(e.target.value)} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Optional" />
                        </div>
                        <div>
                          <label className="block text-gray-300 text-sm font-medium mb-2">Home Phone</label>
                          <input type="tel" value={parentHomePhone} onChange={(e) => setParentHomePhone(e.target.value)} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Optional" />
                        </div>
                      </div>

                      <div>
                        <h3 className="text-lg font-bold text-yellow-400 mb-3">Emergency Contact</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-yellow-400 text-sm font-medium mb-2">Name *</label>
                            <input type="text" value={emergencyName} onChange={(e) => { setEmergencyName(e.target.value); clearAuthError('emergencyName'); }} className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border ${authErrors.emergencyName ? 'border-red-500' : 'border-gray-600'}`} placeholder="Emergency contact name" />
                            {authErrors.emergencyName && <div className="mt-1 text-sm text-red-400">{authErrors.emergencyName}</div>}
                          </div>
                          <div>
                            <label className="block text-yellow-400 text-sm font-medium mb-2">Phone Number *</label>
                            <input type="tel" value={emergencyPhone} onChange={(e) => { setEmergencyPhone(e.target.value); clearAuthError('emergencyPhone'); }} className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border ${authErrors.emergencyPhone ? 'border-red-500' : 'border-gray-600'}`} placeholder="Emergency phone" />
                            {authErrors.emergencyPhone && <div className="mt-1 text-sm text-red-400">{authErrors.emergencyPhone}</div>}
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-lg font-bold text-yellow-400 mb-3">Secondary Contact <span className="text-xs font-normal text-gray-400">(Optional)</span></h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-gray-300 text-sm font-medium mb-2">Name</label>
                            <input type="text" value={secondaryName} onChange={(e) => setSecondaryName(e.target.value)} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Optional" />
                          </div>
                          <div>
                            <label className="block text-gray-300 text-sm font-medium mb-2">Phone Number</label>
                            <input type="tel" value={secondaryPhone} onChange={(e) => setSecondaryPhone(e.target.value)} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Optional" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {!isLogin && pendingRegistrationSessionId && (
                <div className="bg-gray-800/80 border border-yellow-400/20 rounded-xl p-4 space-y-4">
                  <div className="text-center text-gray-200">Enter the code sent to <span className="text-yellow-400 font-semibold">{email}</span></div>
                  <OtpBoxes value={registrationOtp} onChange={(value) => { setRegistrationOtp(value); clearAuthError('registrationOtp'); }} disabled={loading} />
                  {authErrors.registrationOtp && <div className="mt-1 text-sm text-red-400">{authErrors.registrationOtp}</div>}
                  <button type="button" disabled={loading || registrationCooldown > 0} onClick={resendRegistrationOtp} className="w-full border border-yellow-400 text-yellow-400 py-2 rounded-lg disabled:opacity-50">
                    {registrationCooldown > 0 ? `Resend OTP in ${registrationCooldown}s` : 'Resend OTP'}
                  </button>
                </div>
              )}

              <button className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold py-3 px-4 rounded-lg transition-all disabled:opacity-50" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Processing...' : (isLogin ? `Sign In as ${roleLabel}` : (pendingRegistrationSessionId ? 'Verify OTP & Create Account' : `Create ${roleLabel} Account`))}
              </button>
              <InlineFeedback feedback={authFeedback} />

              {isLogin && (
                <div className="flex items-center justify-between text-sm text-gray-300">
                  <button type="button" onClick={() => { setShowForgotPassword(true); setForgotOtpSent(false); setResetEmail(email); setMessage(''); }} className="text-yellow-400 hover:text-yellow-300 transition-colors">Forgot password?</button>
                  {selectedRole === 'student' ? (
                    <button type="button" onClick={() => { setIsLogin(false); setPendingRegistrationSessionId(''); setMessage(''); }} className="text-yellow-400 hover:text-yellow-300 transition-colors">Need an account?</button>
                  ) : (
                    <span className="text-gray-400">{selectedRole === 'employee' ? 'Employee accounts are admin-managed' : 'Teacher accounts are admin-managed'}</span>
                  )}
                </div>
              )}

              {!isLogin && selectedRole === 'student' && (
                <div className="text-center">
                  <button type="button" onClick={() => { setIsLogin(true); setPendingRegistrationSessionId(''); setMessage(''); }} className="text-yellow-400 hover:text-yellow-300 transition-colors">Already have an account? Sign in</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {toast.open && <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg border ${toast.type === 'error' ? 'bg-red-800 text-red-100 border-red-500' : 'bg-gray-800 text-white border-yellow-400/40'}`}>{toast.message}</div>}
    </div>
  );
};

const ResetPasswordPage = ({ onPageChange }) => {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setEmail(params.get('email') || '');
    setToken(params.get('token') || '');
  }, []);

  const handleReset = async () => {
    if (!email || !token) {
      setMessage('Missing reset token or email');
      return;
    }
    if (!password || password !== confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/forgot-password/reset`, {
        email,
        token,
        password,
      });
      setMessage(response.data?.message || 'Password reset successful');
      setTimeout(() => onPageChange('auth'), 1800);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Unable to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-black min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg bg-gray-900 rounded-3xl shadow-2xl p-8 border border-yellow-400/30">
        <div className="text-center mb-8">
          <img
            src="/kra-logo.png"
            alt="K.R.A Logo"
            className="mx-auto h-16 w-16 mb-4"
          />
          <h2 className="text-3xl font-bold text-yellow-400">Reset Password</h2>
          <p className="text-gray-400 mt-2">Set a new password for your K.R.A account</p>
        </div>

        {message && <div className="mb-4 p-3 rounded-lg bg-gray-800 text-gray-100 border border-yellow-400/20">{message}</div>}

        <div className="space-y-4">
          <div>
            <label className="block text-yellow-400 text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none transition-colors"
              placeholder="Enter your email"
            />
          </div>
          <div>
            <label className="block text-yellow-400 text-sm font-medium mb-2">Reset Token</label>
            <input
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none transition-colors"
              placeholder="Paste the reset token from your email"
            />
          </div>
          <div>
            <label className="block text-yellow-400 text-sm font-medium mb-2">New Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none transition-colors"
              placeholder="Create a new password"
              minLength={8}
            />
          </div>
          <div>
            <label className="block text-yellow-400 text-sm font-medium mb-2">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none transition-colors"
              placeholder="Confirm the new password"
              minLength={8}
            />
          </div>
          <button
            onClick={handleReset}
            disabled={loading}
            className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold py-3 px-4 rounded-lg transition-all disabled:opacity-50"
          >
            {loading ? 'Updating password...' : 'Reset Password'}
          </button>
          <button
            onClick={() => onPageChange('auth')}
            className="w-full text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
};

const ChangePasswordPage = ({ onPageChange }) => {
  const { refreshUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const submitChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage('All fields are required');
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage('Password confirmation does not match');
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      await refreshUser();
      setMessage(response.data?.message || 'Password changed successfully');
      setTimeout(() => onPageChange('teacher-dashboard'), 1200);
    } catch (error) {
      setMessage(parseApiError(error, 'Unable to change password'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-black min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg bg-gray-900 rounded-3xl shadow-2xl p-8 border border-yellow-400/30">
        <div className="text-center mb-8">
          <img src="/kra-logo.png" alt="K.R.A Logo" className="mx-auto h-16 w-16 mb-4" />
          <h2 className="text-3xl font-bold text-yellow-400">Change Password</h2>
          <p className="text-gray-400 mt-2">For security, teachers must change their temporary password before accessing the dashboard.</p>
        </div>

        {message && <div className="mb-4 p-3 rounded-lg bg-gray-800 text-gray-100 border border-yellow-400/20">{message}</div>}

        <div className="space-y-4">
          <div>
            <label className="block text-yellow-400 text-sm font-medium mb-2">Current Password</label>
            <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none transition-colors" placeholder="Enter temporary/current password" />
          </div>
          <div>
            <label className="block text-yellow-400 text-sm font-medium mb-2">New Password</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none transition-colors" placeholder="Use uppercase, lowercase, number, and symbol" minLength={8} />
          </div>
          <div>
            <label className="block text-yellow-400 text-sm font-medium mb-2">Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none transition-colors" placeholder="Re-enter your new password" minLength={8} />
          </div>
          <button onClick={submitChange} disabled={loading} className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold py-3 px-4 rounded-lg transition-all disabled:opacity-50">
            {loading ? 'Updating password...' : 'Change Password'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Global Footer Component
const Footer = ({ onPageChange, currentPage }) => {
  const year = new Date().getFullYear();
  const { user, login } = useAuth();
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminStatus, setAdminStatus] = useState('');
  const instagramLinks = {
    general: 'https://www.instagram.com/k.r.a_code_chronicles?igsh=M2FwMGoyY3lwazU%3D&utm_source=qr',
    robotics: 'https://www.instagram.com/k.r.a_robotics?igsh=cG81ejVmeHdta2o2&utm_source=qr',
    kids: 'https://www.instagram.com/k.r.a_kids_programming?igsh=MW9wNnQ4N3BvbTVzMg%3D%3D&utm_source=qr',
  };

  const doAdminLogin = async () => {
    setAdminStatus('');
    try {
      const res = await login(adminEmail, adminPassword);
      if (res.success && (res.user?.role === 'admin')) {
        try {
          sessionStorage.setItem(ADMIN_FOOTER_SESSION_KEY, '1');
        } catch (error) {
          // Ignore session storage failures.
        }
        setAdminOpen(false);
        onPageChange('admin-dashboard');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        try {
          sessionStorage.removeItem(ADMIN_FOOTER_SESSION_KEY);
        } catch (error) {
          // Ignore session storage failures.
        }
        setAdminStatus(res.error || 'Your account is not an admin');
      }
    } catch (e) {
      try {
        sessionStorage.removeItem(ADMIN_FOOTER_SESSION_KEY);
      } catch (error) {
        // Ignore session storage failures.
      }
      setAdminStatus('Login failed');
    }
  };
  return (
    <footer className="bg-black border-t-2 border-yellow-400 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <div className="flex items-center mb-4">
              <img
                src="/kra-logo.png"
                alt="K.R.A Logo"
                className="h-10 w-10 mr-3"
              />
              <div className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">K.R.A</div>
            </div>
            <p className="text-gray-400 text-sm">
              Learning platform for Engineering, Technology, and Innovation.
              Build skills, connect with mentors, and grow your career.
            </p>
          </div>

          {/* Learn */}
          <div>
            <h4 className="text-yellow-400 font-bold mb-3">Learn</h4>
            <div className="space-y-2 text-sm">
              <button onClick={() => onPageChange('courses')} className="block text-gray-300 hover:text-yellow-400">Courses</button>
              <button onClick={() => onPageChange('pricing')} className="block text-gray-300 hover:text-yellow-400">Pricing</button>
              <button onClick={() => onPageChange('mentorship')} className="block text-gray-300 hover:text-yellow-400">Mentors</button>
              <button onClick={() => onPageChange(user ? ((user.role || 'student').toLowerCase() === 'teacher' ? 'teacher-dashboard' : (user.role || 'student').toLowerCase() === 'employee' ? 'employee-dashboard' : 'student-dashboard') : 'dashboard')} className="block text-gray-300 hover:text-yellow-400">Dashboard</button>
            </div>
          </div>

          {/* Explore */}
          <div>
            <h4 className="text-yellow-400 font-bold mb-3">Explore</h4>
            <div className="space-y-2 text-sm">
              <button onClick={() => onPageChange('community')} className="block text-gray-300 hover:text-yellow-400">Community</button>
              <button onClick={() => onPageChange('hackathons')} className="block text-gray-300 hover:text-yellow-400">Hackathons</button>
              <button onClick={() => onPageChange('jobs')} className="block text-gray-300 hover:text-yellow-400">Jobs</button>
              <button onClick={() => onPageChange('code-editor')} className="block text-gray-300 hover:text-yellow-400">Code Lab</button>
              <button onClick={() => {
                setAdminOpen(true);
              }} className="block text-gray-300 hover:text-yellow-400">Admin</button>
            </div>
          </div>

          {/* Shop & Contact */}
          <div>
            <h4 className="text-yellow-400 font-bold mb-3">Shop & Contact</h4>
            <div className="space-y-2 text-sm">
              <button onClick={() => onPageChange('shop')} className="block text-gray-300 hover:text-yellow-400">Shop</button>
              <a href="mailto:K.R.A_initiativeinc@gmail.com" className="block text-gray-300 hover:text-yellow-400">Support</a>
              <div className="mt-3 space-y-2">
                <a href={instagramLinks.general} target="_blank" rel="noreferrer" className="block text-gray-300 hover:text-yellow-400">Instagram: KRA Initiative</a>
                <a href={instagramLinks.robotics} target="_blank" rel="noreferrer" className="block text-gray-300 hover:text-yellow-400">Instagram: KRA Robotics</a>
                <a href={instagramLinks.kids} target="_blank" rel="noreferrer" className="block text-gray-300 hover:text-yellow-400">Instagram: KRA Kids Programming</a>
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter (client-side only) */}
        <div className="mt-10 bg-gray-900 border border-yellow-400/30 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row gap-3 items-center">
            <div className="text-gray-300 text-sm">Subscribe for updates and new courses:</div>
            <NewsletterForm />
          </div>
        </div>

        {adminOpen && (
          <div className="mt-6 bg-gray-900 border border-yellow-400/40 rounded-lg p-4">
            <div className="font-semibold text-yellow-400 mb-2">Admin Login</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} type="email" placeholder="admin@example.com" className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-700 focus:border-yellow-400 focus:outline-none" />
              <input value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} type="password" placeholder="Password" className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-700 focus:border-yellow-400 focus:outline-none" />
              <div className="flex gap-2">
                <button onClick={doAdminLogin} className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded font-medium">Login</button>
                <button onClick={() => setAdminOpen(false)} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded">Cancel</button>
              </div>
            </div>
            {adminStatus && <div className="text-xs text-red-400 mt-2">{adminStatus}</div>}
            <div className="text-xs text-gray-400 mt-2">Only admins can access the administration portal. Use your admin credentials here.</div>
          </div>
        )}

        {/* Bottom bar */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-gray-400">
          <div>© {year} K.R.A • All rights reserved</div>
          <div className="flex gap-4 mt-2 sm:mt-0">
            <button onClick={() => { onPageChange('terms'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="hover:text-yellow-400">Terms</button>
            <button onClick={() => { onPageChange('privacy'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="hover:text-yellow-400">Privacy</button>
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="hover:text-yellow-400">Back to top ↑</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Newsletter form component hooked to backend
const NewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('');
  const handleSubscribe = async () => {
    if (!email) return;
    try {
      setStatus('');
      await axios.post(`${API}/newsletter/subscribe`, { email, name });
      setStatus('Subscribed!');
      setEmail('');
      setName('');
    } catch (e) {
      setStatus(e.response?.data?.detail || 'Failed to subscribe');
    }
  };
  return (
    <div className="w-full space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 items-stretch">
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@example.com" className="w-full min-w-0 bg-gray-800 text-white px-3 py-2 rounded border border-gray-700 focus:border-yellow-400 focus:outline-none" />
        <input value={name} onChange={(e) => setName(e.target.value)} type="text" placeholder="Name (optional)" className="w-full min-w-0 bg-gray-800 text-white px-3 py-2 rounded border border-gray-700 focus:border-yellow-400 focus:outline-none" />
        <button onClick={handleSubscribe} className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded font-medium">Subscribe</button>
      </div>
      {status && <div className="text-xs text-gray-300 break-words">{status}</div>}
    </div>
  );
};

// Courses Page Component
const CoursesPage = ({ onPageChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [courseFetchError, setCourseFetchError] = useState('');
  const [categoryRouteError, setCategoryRouteError] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [savingCourseId, setSavingCourseId] = useState(null);
  const [enrollingCourseId, setEnrollingCourseId] = useState(null);
  const [registrationCourse, setRegistrationCourse] = useState(null);
  const [registrationStep, setRegistrationStep] = useState(1);
  const [registrationErrors, setRegistrationErrors] = useState({});
  const [registrationForm, setRegistrationForm] = useState({
    student_information: {
      first_name: '',
      last_name: '',
      date_of_birth: '',
      age: '',
      gender: '',
      email: '',
      phone_number: '',
    },
    parent_guardian_information: {
      name: '',
      email: '',
      phone_number: '',
    },
    address_information: {
      street_address: '',
      city: '',
      province_state: '',
      postal_code: '',
      country: 'Canada',
    },
    emergency_contact: {
      name: '',
      relationship: '',
      phone_number: '',
    },
    educational_information: {
      current_school_name: '',
      current_grade_level: '',
      educational_goals: '',
      previous_experience: 'No Experience',
      program_expectations: '',
    },
    program_selection: {
      program_type: 'group',
      preferred_days: [],
      preferred_times: [],
      sessions_per_week: '5',
    },
    terms_and_conditions_accepted: false,
    consent_forms_accepted: false,
    requires_supporting_documents: false,
    uploaded_documents: [],
    note: '',
    teacher_id: '',
  });
  const [courseActionFeedback, setCourseActionFeedback] = useState({});
  const [registrationFeedback, setRegistrationFeedback] = useState({ message: '', type: 'info' });
  const [guestEnrollmentPromptCourse, setGuestEnrollmentPromptCourse] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [publicTeacherSearch, setPublicTeacherSearch] = useState('');
  const [publicTeacherCategory, setPublicTeacherCategory] = useState('all');
  const [publicCourseSearch, setPublicCourseSearch] = useState('');
  const [publicCatalogGroup, setPublicCatalogGroup] = useState('all');
  const [publicCourseCategory, setPublicCourseCategory] = useState('all');
  const [publicCourseAvailability, setPublicCourseAvailability] = useState('all');
  const [publicCourseDifficulty, setPublicCourseDifficulty] = useState('all');
  const [selectedTeacherProfile, setSelectedTeacherProfile] = useState(null);
  const [selectedTeacherByCourse, setSelectedTeacherByCourse] = useState({});
  const { user } = useAuth();
  const userRole = (user?.role || 'student').toLowerCase();
  const isStudent = userRole === 'student';
  const dayOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const timeOptions = [
    '8:00 AM - 9:00 AM',
    '9:00 AM - 10:00 AM',
    '10:00 AM - 11:00 AM',
    '11:00 AM - 12:00 PM',
    '12:00 PM - 1:00 PM',
    '1:00 PM - 2:00 PM',
    '2:00 PM - 3:00 PM',
    '3:00 PM - 4:00 PM',
    '4:00 PM - 5:00 PM',
    '5:00 PM - 6:00 PM',
    '6:00 PM - 7:00 PM',
    '7:00 PM - 8:00 PM',
    '8:00 PM - 9:00 PM',
    '9:00 PM - 10:00 PM',
  ];
  const experienceOptions = ['No Experience', 'Beginner', 'Intermediate', 'Advanced'];
  const pendingEnrollmentRestoredRef = useRef(false);
  const routeParts = location.pathname.split('/').filter(Boolean);
  const isLegacyRoboticsCategoryRoute = location.pathname.startsWith('/courses/category/');
  const routeGroupSlug = routeParts[0] === 'courses' && routeParts.length >= 2 && routeParts[1] !== 'category'
    ? decodeURIComponent(routeParts[1].trim().toLowerCase())
    : (isLegacyRoboticsCategoryRoute ? 'robotics' : '');
  const routeCategorySlug = routeParts.length === 3 && routeParts[0] === 'courses'
    ? decodeURIComponent(routeParts[2].trim().toLowerCase())
    : '';
  const groupMetaFromRoute = routeGroupSlug ? COURSE_GROUP_META_BY_SLUG[routeGroupSlug] : null;
  const categoryMetaFromRoute = groupMetaFromRoute && routeCategorySlug
    ? (groupMetaFromRoute.categories || []).find((item) => item.slug === routeCategorySlug)
    : null;
  const isGroupRoute = Boolean(groupMetaFromRoute);
  const isCategoryRoute = Boolean(isGroupRoute && categoryMetaFromRoute);
  const selectedCourseIsClub = isBeforeAfterSchoolClubCourse(selectedCourse);
  const registrationCourseIsClub = isBeforeAfterSchoolClubCourse(registrationCourse);

  const goToCategory = (groupMeta, categoryMeta) => {
    if (!groupMeta || !categoryMeta) return;
    navigate(`/courses/${groupMeta.slug}/${categoryMeta.slug}`);
    setPublicCatalogGroup(groupMeta.value);
    setPublicCourseCategory(categoryMeta.value);
    setPublicCourseAvailability('all');
    setPublicCourseDifficulty('all');
  };

  const backToCourses = () => {
    setPublicCourseSearch('');
    setPublicCatalogGroup('all');
    setPublicCourseCategory('all');
    setPublicCourseAvailability('all');
    setPublicCourseDifficulty('all');
    navigate('/courses');
  };

  const pushCourseFeedback = (courseId, messageText, type = 'info', autoDismiss = true) => {
    if (!courseId || !messageText) return;
    setCourseActionFeedback((current) => ({
      ...current,
      [courseId]: { message: messageText, type },
    }));
    if (autoDismiss && type !== 'error') {
      window.setTimeout(() => {
        setCourseActionFeedback((current) => {
          if (!current[courseId] || current[courseId].message !== messageText) return current;
          const next = { ...current };
          delete next[courseId];
          return next;
        });
      }, 3200);
    }
  };

  const setRegistrationInlineFeedback = (messageText, type = 'info', autoDismiss = true) => {
    setRegistrationFeedback({ message: messageText, type });
    if (autoDismiss && type !== 'error') {
      window.setTimeout(() => {
        setRegistrationFeedback((current) => {
          if (current.message !== messageText) return current;
          return { message: '', type: 'info' };
        });
      }, 3500);
    }
  };

  const buildRegistrationForm = (course, teacherId = '') => {
    const nameParts = String(user?.name || '').trim().split(/\s+/).filter(Boolean);
    const isClubCourse = isBeforeAfterSchoolClubCourse(course);
    return {
      student_information: {
        first_name: user?.firstname || nameParts[0] || '',
        last_name: user?.lastname || nameParts.slice(1).join(' ') || '',
        date_of_birth: '',
        age: '',
        gender: '',
        email: user?.email || '',
        phone_number: '',
      },
      parent_guardian_information: {
        name: '',
        email: '',
        phone_number: '',
      },
      address_information: {
        street_address: '',
        city: '',
        province_state: '',
        postal_code: '',
        country: 'Canada',
      },
      emergency_contact: {
        name: '',
        relationship: '',
        phone_number: '',
      },
      educational_information: {
        current_school_name: '',
        current_grade_level: '',
        educational_goals: '',
        previous_experience: 'No Experience',
        program_expectations: `I want to build practical skills and confidence in ${course?.title || 'this program'}.`,
      },
      program_selection: {
        program_type: isClubCourse ? 'club' : 'group',
        preferred_days: [],
        preferred_times: [],
        sessions_per_week: isClubCourse ? '1' : '5',
      },
      terms_and_conditions_accepted: false,
      consent_forms_accepted: false,
      requires_supporting_documents: false,
      uploaded_documents: [],
      note: '',
      teacher_id: teacherId || course?.teacher_id || '',
    };
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const dob = new Date(dateOfBirth);
    if (Number.isNaN(dob.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age -= 1;
    }
    return age >= 0 ? String(age) : '';
  };

  const isMinorRegistration = Number(registrationForm.student_information.age || 0) > 0 && Number(registrationForm.student_information.age) < 18;

  const getAvailableTeachersForCourse = (course) => (
    teachers.filter((teacher) => asArray(teacher.courses).some((item) => item?.id === course?.id))
  );

  const getProgramOption = (course, programType) => {
    const options = asArray(course?.program_options);
    if (programType === 'club') {
      return options.find((item) => item.id === 'club') || {
        id: 'club',
        title: 'Club Enrollment',
        price_cad: getBeforeAfterSchoolClubPrice(course),
        price_cad_with_tax: getBeforeAfterSchoolClubPrice(course),
        billing: 'club',
        allowed_sessions_per_week: [1],
        includes: ['Fixed club tuition', 'Structured before- and after-school enrichment'],
        recommendation: 'Flat club pricing applies to this offering.',
      };
    }
    return options.find((item) => item.id === programType) || (
      programType === 'one_on_one'
        ? {
            id: 'one_on_one',
            title: 'One-on-One Learning Program',
            price_cad: 40,
            price_cad_with_tax: 542.4,
            billing: 'month',
            minimum_sessions_per_week: 3,
            allowed_sessions_per_week: [3, 4, 5, 6, 7],
            recommendation: 'We do not bill hourly. One-on-one classes are billed monthly.',
          }
        : {
            id: 'group',
            title: 'Group Learning Program',
            price_cad: 600,
            price_cad_with_tax: 678,
            billing: 'month',
            rate_per_class: 30,
            default_sessions_per_week: 5,
            allowed_sessions_per_week: [5, 10, 15, 20],
            includes: ['1 Hour Per Class', 'Monday to Friday', '5 Classes Per Week (Default)', 'Monthly Billing Only', 'Instructor Support', 'Learning Materials'],
            recommendation: 'We do not bill hourly. Group classes are billed monthly.',
          }
    );
  };

  const calculateEnrollmentPricing = (programType, sessionsPerWeekRaw, course = null) => {
    if (programType === 'club' || isBeforeAfterSchoolClubCourse(course)) {
      const total = Number(getBeforeAfterSchoolClubPrice(course).toFixed(2));
      return {
        perClassRate: total,
        sessionsPerWeek: 1,
        subtotal: total,
        hstAmount: 0,
        total,
        taxRate: 0,
        billing: 'club',
      };
    }
    const taxRate = 0.13;
    const numeric = Number(sessionsPerWeekRaw || 0);
    const sessionsPerWeek = programType === 'one_on_one'
      ? Math.min(Math.max(numeric || 3, 3), 7)
      : Math.min(Math.max(numeric || 5, 5), 20);
    const perClassRate = programType === 'one_on_one' ? 40 : 30;
    const subtotal = Number((perClassRate * sessionsPerWeek * 4).toFixed(2));
    const hstAmount = Number((subtotal * taxRate).toFixed(2));
    return {
      perClassRate,
      sessionsPerWeek,
      subtotal,
      hstAmount,
      total: Number((subtotal + hstAmount).toFixed(2)),
      taxRate,
    };
  };

  const openRegistrationFlow = (course) => {
    if (!user) {
      const teacherId = selectedTeacherByCourse[course.id] || course.teacher_id || '';
      writePendingEnrollment({ course_id: course.id, teacher_id: teacherId });
      setGuestEnrollmentPromptCourse(course);
      return;
    }
    if (!isStudent) {
      pushCourseFeedback(course.id, 'Only student accounts can enroll in courses.', 'error', false);
      return;
    }
    const teacherId = selectedTeacherByCourse[course.id] || course.teacher_id || '';
    writePendingEnrollment({ course_id: course.id, teacher_id: teacherId });
    setRegistrationCourse(course);
    setRegistrationStep(1);
    setRegistrationErrors({});
    setRegistrationForm(buildRegistrationForm(course, teacherId));
    setRegistrationFeedback({ message: '', type: 'info' });
  };

  const closeRegistrationFlow = () => {
    setRegistrationCourse(null);
    setRegistrationStep(1);
    setRegistrationErrors({});
    setRegistrationFeedback({ message: '', type: 'info' });
  };

  const updateRegistrationSection = (section, field, value) => {
    setRegistrationForm((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [field]: value,
      },
    }));
    setRegistrationErrors((current) => ({ ...current, [`${section}.${field}`]: '' }));
  };

  const updateRegistrationRoot = (field, value) => {
    setRegistrationForm((current) => ({ ...current, [field]: value }));
    setRegistrationErrors((current) => ({ ...current, [field]: '' }));
  };

  const toggleProgramSelection = (field, optionValue) => {
    setRegistrationForm((current) => {
      const existing = current.program_selection[field] || [];
      return {
        ...current,
        program_selection: {
          ...current.program_selection,
          [field]: existing.includes(optionValue)
            ? existing.filter((item) => item !== optionValue)
            : [...existing, optionValue],
        },
      };
    });
    setRegistrationErrors((current) => ({ ...current, [`program_selection.${field}`]: '' }));
  };

  const registrationInputClass = (fieldPath, extraClasses = '') => {
    const hasError = Boolean(registrationErrors[fieldPath]);
    return `bg-gray-800 text-white px-4 py-3 rounded-lg border ${hasError ? 'border-red-500 ring-1 ring-red-500/50' : 'border-gray-600'} ${extraClasses}`;
  };

  const registrationFieldError = (fieldPath) => {
    const messageText = registrationErrors[fieldPath];
    if (!messageText) return null;
    return <div className="mt-1 text-xs text-red-300">{messageText}</div>;
  };

  const validateRegistrationStep = (step) => {
    const nextErrors = {};
    if (step === 1) {
      const studentInfo = registrationForm.student_information;
      const addressInfo = registrationForm.address_information;
      const emergencyInfo = registrationForm.emergency_contact;
      ['first_name', 'last_name', 'date_of_birth', 'age', 'email', 'phone_number'].forEach((field) => {
        if (!String(studentInfo[field] || '').trim()) nextErrors[`student_information.${field}`] = 'Required';
      });
      ['street_address', 'city', 'province_state', 'postal_code', 'country'].forEach((field) => {
        if (!String(addressInfo[field] || '').trim()) nextErrors[`address_information.${field}`] = 'Required';
      });
      ['name', 'relationship', 'phone_number'].forEach((field) => {
        if (!String(emergencyInfo[field] || '').trim()) nextErrors[`emergency_contact.${field}`] = 'Required';
      });
      if (isMinorRegistration) {
        ['name', 'email', 'phone_number'].forEach((field) => {
          if (!String(registrationForm.parent_guardian_information[field] || '').trim()) {
            nextErrors[`parent_guardian_information.${field}`] = 'Required for minors';
          }
        });
      }
    }
    if (step === 2) {
      const edu = registrationForm.educational_information;
      ['current_school_name', 'current_grade_level', 'educational_goals', 'previous_experience', 'program_expectations'].forEach((field) => {
        if (!String(edu[field] || '').trim()) nextErrors[`educational_information.${field}`] = 'Required';
      });
    }
    if (step === 3) {
      const program = registrationForm.program_selection;
      if (!registrationCourseIsClub && !program.program_type) nextErrors['program_selection.program_type'] = 'Select a program';
      if (!registrationCourseIsClub && program.program_type === 'one_on_one' && Number(program.sessions_per_week || 0) < 3) nextErrors['program_selection.sessions_per_week'] = 'Minimum 3 classes per week';
      if (!registrationCourseIsClub && program.program_type === 'group' && Number(program.sessions_per_week || 0) < 5) nextErrors['program_selection.sessions_per_week'] = 'Minimum 5 classes per week';
      if (!(program.preferred_days || []).length) nextErrors['program_selection.preferred_days'] = 'Select preferred days';
      if (!(program.preferred_times || []).length) nextErrors['program_selection.preferred_times'] = 'Select preferred times';
      if (!registrationForm.terms_and_conditions_accepted) nextErrors.terms_and_conditions_accepted = 'Required';
      if (!registrationForm.consent_forms_accepted) nextErrors.consent_forms_accepted = 'Required';
    }
    setRegistrationErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submitRegistration = async () => {
    const firstInvalidStep = [1, 2, 3].find((step) => !validateRegistrationStep(step));
    if (!registrationCourse || firstInvalidStep) {
      setRegistrationStep(firstInvalidStep || 1);
      setRegistrationInlineFeedback('Please complete required fields before continuing.', 'error', false);
      return;
    }
    try {
      setEnrollingCourseId(registrationCourse.id);
      setRegistrationInlineFeedback('', 'info');
      const pricing = calculateEnrollmentPricing(
        registrationForm.program_selection.program_type,
        registrationForm.program_selection.sessions_per_week,
        registrationCourse,
      );
      const payload = {
        enrollment_payload: {
          course_id: registrationCourse.id,
          teacher_id: registrationForm.teacher_id || selectedTeacherByCourse[registrationCourse.id] || registrationCourse.teacher_id || null,
          note: registrationForm.note,
          student_information: registrationForm.student_information,
          parent_guardian_information: registrationForm.parent_guardian_information,
          address_information: registrationForm.address_information,
          emergency_contact: registrationForm.emergency_contact,
          educational_information: registrationForm.educational_information,
          program_selection: {
            ...registrationForm.program_selection,
            program_type: registrationCourseIsClub ? 'club' : registrationForm.program_selection.program_type,
            sessions_per_week: registrationCourseIsClub ? 1 : Number(registrationForm.program_selection.sessions_per_week || 0),
          },
          terms_and_conditions_accepted: registrationForm.terms_and_conditions_accepted,
          consent_forms_accepted: registrationForm.consent_forms_accepted,
          requires_supporting_documents: registrationForm.requires_supporting_documents,
          uploaded_documents: registrationForm.uploaded_documents,
        },
        provider: 'stripe',
        idempotency_key: `enrollment-${registrationCourse.id}-${user?.id || user?.user_id || 'student'}`,
      };
      const response = await axios.post(`${API}/payments/create-checkout-session`, payload);
      if (response.data?.checkout_url) {
        setRegistrationInlineFeedback('Redirecting to secure checkout...', 'success');
        window.location.href = response.data.checkout_url;
        return;
      }
      setRegistrationInlineFeedback('Payment session created. Please continue to checkout.', 'success');
    } catch (error) {
      setRegistrationInlineFeedback(parseApiError(error, 'Unable to submit enrollment request'), 'error', false);
    } finally {
      setEnrollingCourseId(null);
    }
  };

  const continueGuestEnrollmentAsStudent = (mode = 'register') => {
    if (!guestEnrollmentPromptCourse) return;
    const teacherId = selectedTeacherByCourse[guestEnrollmentPromptCourse.id] || guestEnrollmentPromptCourse.teacher_id || '';
    writePendingEnrollment({ course_id: guestEnrollmentPromptCourse.id, teacher_id: teacherId });
    localStorage.setItem('kra_post_login_redirect', 'courses');
    setGuestEnrollmentPromptCourse(null);
    if (onPageChange) {
      onPageChange('auth', { search: `?role=student&mode=${mode}` });
    }
  };

  useEffect(() => {
    const effectiveGroup = isGroupRoute && groupMetaFromRoute
      ? groupMetaFromRoute.value
      : publicCatalogGroup;
    const effectiveCategory = isCategoryRoute && categoryMetaFromRoute
      ? categoryMetaFromRoute.value
      : publicCourseCategory;
    const timer = window.setTimeout(() => {
      fetchCourses({
        search: publicCourseSearch,
        catalog_group: effectiveGroup,
        category: effectiveCategory,
        availability_status: publicCourseAvailability,
        difficulty_level: publicCourseDifficulty,
      });
    }, 220);
    return () => window.clearTimeout(timer);
  }, [
    publicCourseSearch,
    publicCatalogGroup,
    publicCourseCategory,
    publicCourseAvailability,
    publicCourseDifficulty,
    isCategoryRoute,
    groupMetaFromRoute,
    categoryMetaFromRoute,
  ]);

  useEffect(() => {
    if (!isGroupRoute) {
      setCategoryRouteError('');
      return;
    }
    if (!groupMetaFromRoute || (routeCategorySlug && !categoryMetaFromRoute)) {
      setCategoryRouteError('Category not found. Please choose a valid course category.');
      return;
    }
    setCategoryRouteError('');
    if (publicCatalogGroup !== groupMetaFromRoute.value) {
      setPublicCatalogGroup(groupMetaFromRoute.value);
    }
    if (categoryMetaFromRoute && publicCourseCategory !== categoryMetaFromRoute.value) {
      setPublicCourseCategory(categoryMetaFromRoute.value);
    } else if (!categoryMetaFromRoute && publicCourseCategory !== 'all') {
      setPublicCourseCategory('all');
    }
  }, [isGroupRoute, groupMetaFromRoute, categoryMetaFromRoute, publicCatalogGroup, publicCourseCategory, routeCategorySlug]);

  useEffect(() => {
    fetchTeachers();
  }, []);

  useEffect(() => {
    fetchCategoryCounts();
  }, []);

  const fetchCourses = async (criteria = {}) => {
    try {
      setCourseFetchError('');
      const params = {
        limit: 500,
        search: (criteria.search || '').trim() || undefined,
        catalog_group: criteria.catalog_group && criteria.catalog_group !== 'all' ? criteria.catalog_group : undefined,
        category: criteria.category && criteria.category !== 'all' ? criteria.category : undefined,
        availability_status: criteria.availability_status && criteria.availability_status !== 'all' ? criteria.availability_status : undefined,
        difficulty_level: criteria.difficulty_level && criteria.difficulty_level !== 'all' ? criteria.difficulty_level : undefined,
      };
      const response = await axios.get(`${API}/courses`, { params });
      const coursesData = response.data.courses || response.data || [];
      setCourses(coursesData);
    } catch (error) {
      setCourseFetchError(parseApiError(error, 'Failed to load courses. Please try again.'));
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryCounts = async () => {
    try {
      const response = await axios.get(`${API}/courses/catalog-structure`);
      const groups = response.data?.groups || [];
      const counts = {};
      groups.forEach((group) => {
        const groupName = String(group.name || '').trim();
        if (!groupName) return;
        counts[groupName] = {};
        Object.entries(group.counts || {}).forEach(([category, count]) => {
          counts[groupName][category] = Number(count || 0);
        });
      });
      setCategoryCounts(counts);
    } catch (error) {
      setCategoryCounts({});
    }
  };

  const fetchTeachers = async () => {
    try {
      setTeachersLoading(true);
      const response = await axios.get(`${API}/teachers`);
      setTeachers(response.data?.teachers || []);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers([]);
    } finally {
      setTeachersLoading(false);
    }
  };

  const openTeacherProfile = async (teacherId) => {
    try {
      const response = await axios.get(`${API}/teachers/${teacherId}`);
      setSelectedTeacherProfile(response.data?.teacher || null);
    } catch (error) {
      console.warn('Error loading teacher profile:', error.response?.data?.detail || 'Unknown error');
    }
  };

  const requestEnrollmentUnderTeacher = async (teacherId) => {
    if (!user) {
      pushCourseFeedback(`teacher-${teacherId}`, 'Please sign in with a Student account first.', 'warning');
      localStorage.setItem('kra_post_login_redirect', 'courses');
      if (onPageChange) onPageChange('auth', { search: '?role=student&mode=login' });
      return;
    }
    if (!isStudent) {
      pushCourseFeedback(`teacher-${teacherId}`, 'Only student accounts can request teacher enrollment.', 'error', false);
      return;
    }
    try {
      await axios.post(`${API}/student/teacher-assignment/request`, { teacher_id: teacherId });
      pushCourseFeedback(`teacher-${teacherId}`, 'Enrollment request sent to teacher.', 'success');
    } catch (error) {
      pushCourseFeedback(`teacher-${teacherId}`, parseApiError(error, 'Unable to request teacher enrollment'), 'error', false);
    }
  };

  const saveCourse = async (courseId) => {
    try {
      setSavingCourseId(courseId);
      await axios.post(`${API}/courses/${courseId}/save`);
      pushCourseFeedback(courseId, 'Course saved to your profile.', 'success');
    } catch (error) {
      pushCourseFeedback(courseId, parseApiError(error, 'Unable to save course'), 'error', false);
    } finally {
      setSavingCourseId(null);
    }
  };

  useEffect(() => {
    if (pendingEnrollmentRestoredRef.current) return;
    if (!user || !isStudent || !courses.length) return;

    const params = new URLSearchParams(location.search || '');
    const queryCourseId = params.get('course_id');
    const pending = readPendingEnrollment();
    const targetCourseId = queryCourseId || pending?.course_id;
    if (!targetCourseId) return;

    const matchedCourse = (courses || []).find((entry) => entry.id === targetCourseId);
    if (!matchedCourse) return;

    const teacherId = pending?.teacher_id || selectedTeacherByCourse[targetCourseId] || matchedCourse.teacher_id || '';
    pendingEnrollmentRestoredRef.current = true;
    setSelectedTeacherByCourse((current) => ({ ...current, [targetCourseId]: teacherId }));
    setSelectedCourse(matchedCourse);
    setRegistrationCourse(matchedCourse);
    setRegistrationStep(1);
    setRegistrationErrors({});
    setRegistrationForm(buildRegistrationForm(matchedCourse, teacherId));
    setRegistrationInlineFeedback('Enrollment flow restored. Continue your registration and payment.', 'info');
    pushCourseFeedback(targetCourseId, 'Enrollment flow restored for this course.', 'success');
    clearPendingEnrollment();

    if (queryCourseId && onPageChange) {
      onPageChange('courses', { replace: true });
    }
  }, [user, isStudent, courses, location.search, selectedTeacherByCourse, onPageChange]);

  if (loading) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  const normalizedTeacherSearch = publicTeacherSearch.trim().toLowerCase();
  const teacherCategories = Array.from(
    new Set(
      teachers
        .flatMap((teacher) => [
          ...(teacher.categories || []),
          ...(teacher.courses || []).map((course) => course.category),
        ])
        .map((item) => String(item || '').trim())
        .filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));
  const filteredTeachers = teachers.filter((teacher) => {
    const categoryMatch = publicTeacherCategory === 'all'
      || (teacher.categories || []).includes(publicTeacherCategory)
      || (teacher.courses || []).some((course) => course.category === publicTeacherCategory);
    if (!categoryMatch) return false;
    if (!normalizedTeacherSearch) return true;
    const searchable = [
      teacher.name,
      teacher.email,
      teacher.bio,
      ...(teacher.expertise || []),
      ...(teacher.courses || []).flatMap((course) => [course.title, course.category]),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return searchable.includes(normalizedTeacherSearch);
  });

  const normalizedCourseSearch = publicCourseSearch.trim().toLowerCase();
  const activeGroupMeta = publicCatalogGroup !== 'all' ? COURSE_GROUP_META_BY_VALUE[String(publicCatalogGroup).toLowerCase()] : null;
  const activeRouteGroupMeta = isCategoryRoute ? groupMetaFromRoute : null;
  const effectiveGroupMeta = activeRouteGroupMeta || activeGroupMeta;
  const activeCategoryMeta = categoryMetaFromRoute || (effectiveGroupMeta?.categories || []).find(
    (category) => String(category.value || '').toLowerCase() === String(publicCourseCategory || '').toLowerCase(),
  ) || null;
  const courseCategories = Array.from(
    new Set(
      [
        ...((effectiveGroupMeta?.categories || []).map((item) => item.value)),
        ...courses
          .map((course) => String(course.category || '').trim())
          .filter(Boolean),
      ],
    ),
  ).sort((a, b) => a.localeCompare(b));
  const difficultyOptions = Array.from(
    new Set(
      [
        'Beginner',
        'Intermediate',
        'Advanced',
        'Specialized',
        'Project',
        'Competition',
        ...courses.map((course) => String(course.difficulty_level || course.level || '').trim()).filter(Boolean),
      ],
    ),
  ).sort((a, b) => a.localeCompare(b));
  const filteredCourses = courses.filter((course) => {
    const normalizedGroup = String(course.catalog_group || course.catalogGroup || '').trim().toLowerCase();
    const groupMatch = publicCatalogGroup === 'all' || normalizedGroup === publicCatalogGroup.toLowerCase();
    const categoryMatch = publicCourseCategory === 'all' || String(course.category || '').trim().toLowerCase() === publicCourseCategory.toLowerCase();
    const availabilityMatch = publicCourseAvailability === 'all' || String(course.availability_status || '').trim().toLowerCase() === publicCourseAvailability;
    const difficultyMatch = publicCourseDifficulty === 'all'
      || String(course.difficulty_level || course.level || '').trim().toLowerCase() === publicCourseDifficulty.toLowerCase();
    if (!groupMatch) return false;
    if (!categoryMatch) return false;
    if (!availabilityMatch) return false;
    if (!difficultyMatch) return false;
    if (!normalizedCourseSearch) return true;
    const searchable = [
      course.title,
      course.description,
      course.category,
      course.best_for,
      course.difficulty_level,
      course.level,
      course.teacher_name,
      course.instructor,
      ...(course.keywords || []),
      ...asArray(course.prerequisites),
      ...asArray(course.learning_outcomes),
      ...asArray(course.projects),
      ...asArray(course.career_paths),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return searchable.includes(normalizedCourseSearch);
  });

  const activeCategoryCount = effectiveGroupMeta && publicCourseCategory !== 'all'
    ? Number(categoryCounts?.[effectiveGroupMeta.value]?.[publicCourseCategory] || 0)
    : filteredCourses.length;

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-yellow-400 mb-4">📚 Courses</h1>
          <p className="text-xl text-gray-300">Master new skills with our comprehensive learning paths</p>
        </div>

        {categoryRouteError && (
          <div className="mb-6 rounded-xl border border-red-500/40 bg-red-900/20 px-4 py-3 text-red-200">
            <div className="font-semibold">Invalid category route</div>
            <div className="text-sm mt-1">{categoryRouteError}</div>
            <button
              type="button"
              onClick={backToCourses}
              className="mt-3 border border-red-300/50 hover:bg-red-500/20 px-3 py-2 rounded-lg text-sm"
            >
              Return to Courses
            </button>
          </div>
        )}

        {isCategoryRoute && categoryMetaFromRoute && (
          <div className="mb-6 rounded-2xl border border-yellow-400/30 bg-gray-900 p-6">
            <button
              type="button"
              onClick={backToCourses}
              className="mb-4 text-sm border border-yellow-400/40 text-yellow-300 hover:bg-yellow-400 hover:text-black px-3 py-2 rounded-lg"
            >
              ← Back to Courses
            </button>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">{categoryMetaFromRoute.emoji}</span>
              <h3 className="text-2xl font-bold text-yellow-300">{categoryMetaFromRoute.value.toUpperCase()} {groupMetaFromRoute?.value?.toUpperCase()}</h3>
            </div>
            <p className="text-gray-300">{categoryMetaFromRoute.description}</p>
            <p className="text-sm text-gray-400 mt-2">{activeCategoryCount} Courses Available</p>
          </div>
        )}

        {courseFetchError && (
          <div className="mb-6 rounded-xl border border-red-500/40 bg-red-900/20 px-4 py-3 text-red-200 text-sm">
            {courseFetchError}
          </div>
        )}

        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
            <input
              type="text"
              value={publicCourseSearch}
              onChange={(e) => setPublicCourseSearch(e.target.value)}
              placeholder="Search title, keywords, description, category, or level"
              className="w-full bg-gray-900 border border-yellow-400/30 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
            />
            <select
              value={isGroupRoute && groupMetaFromRoute ? groupMetaFromRoute.value : publicCatalogGroup}
              onChange={(e) => {
                setPublicCatalogGroup(e.target.value);
                setPublicCourseCategory('all');
              }}
              disabled={isGroupRoute && Boolean(groupMetaFromRoute)}
              className="w-full bg-gray-900 border border-yellow-400/30 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
            >
              <option value="all">All Groups</option>
              {COURSE_GROUP_META_ORDERED.map((group) => (
                <option key={group.slug} value={group.value}>{group.value}</option>
              ))}
            </select>
            <select
              value={isCategoryRoute && categoryMetaFromRoute ? categoryMetaFromRoute.value : publicCourseCategory}
              onChange={(e) => setPublicCourseCategory(e.target.value)}
              disabled={isCategoryRoute && Boolean(categoryMetaFromRoute)}
              className="w-full bg-gray-900 border border-yellow-400/30 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
            >
              <option value="all">All Categories</option>
              {courseCategories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <select
              value={publicCourseAvailability}
              onChange={(e) => setPublicCourseAvailability(e.target.value)}
              className="w-full bg-gray-900 border border-yellow-400/30 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
            >
              <option value="all">Availability: All</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
            <select
              value={publicCourseDifficulty}
              onChange={(e) => setPublicCourseDifficulty(e.target.value)}
              className="w-full bg-gray-900 border border-yellow-400/30 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
            >
              <option value="all">Difficulty: All</option>
              {difficultyOptions.map((difficulty) => (
                <option key={difficulty} value={difficulty}>{difficulty}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => {
                setPublicCourseSearch('');
                setPublicCatalogGroup(isGroupRoute && groupMetaFromRoute ? groupMetaFromRoute.value : 'all');
                setPublicCourseCategory(isCategoryRoute && categoryMetaFromRoute ? categoryMetaFromRoute.value : 'all');
                setPublicCourseAvailability('all');
                setPublicCourseDifficulty('all');
              }}
              className="w-full bg-gray-900 border border-yellow-400/40 rounded-xl px-4 py-3 text-yellow-300 hover:text-black hover:bg-yellow-400 font-semibold transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {!isCategoryRoute && (
          <div className="space-y-8 mb-10">
            {(isGroupRoute && groupMetaFromRoute ? [groupMetaFromRoute] : COURSE_GROUP_META_ORDERED).map((group) => (
              <div key={group.slug} className="bg-gray-900 border border-yellow-400/25 rounded-2xl p-6">
                <div className="flex items-start justify-between gap-3 mb-5">
                  <div>
                    <h2 className="text-2xl font-bold text-yellow-400">{group.heading}</h2>
                    <p className="text-gray-400 text-sm mt-1">{group.description}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {(group.categories || []).map((category) => {
                    const count = Number(categoryCounts?.[group.value]?.[category.value] || 0);
                    const isActive = String(publicCatalogGroup || '').toLowerCase() === group.value.toLowerCase()
                      && String(publicCourseCategory || '').toLowerCase() === category.value.toLowerCase();
                    return (
                      <button
                        key={`${group.slug}-${category.slug}`}
                        type="button"
                        onClick={() => goToCategory(group, category)}
                        className={`text-left rounded-2xl border p-5 transition-all bg-gradient-to-b ${category.cardTone} ${isActive ? 'border-yellow-400 shadow-[0_0_22px_rgba(250,204,21,0.18)]' : 'border-yellow-400/20 hover:border-yellow-300/60'}`}
                      >
                        <div className="text-2xl mb-3">{category.emoji}</div>
                        <h3 className={`text-lg font-bold ${category.accent}`}>{category.value}</h3>
                        <p className="text-sm text-gray-300 mt-2 min-h-[48px]">{category.description}</p>
                        <div className="text-xs text-gray-400 mt-4">{count} Course{count === 1 ? '' : 's'}</div>
                        <div className="text-sm text-yellow-300 mt-2 font-semibold">Explore Category &rarr;</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCourses.map((course) => (
            <div key={course.id} className="bg-gray-900 border border-yellow-400/30 rounded-lg overflow-hidden hover:border-yellow-400 transition-all">
              <div className="h-44 bg-gray-800 border-b border-yellow-400/20">
                <img
                  src={resolveImageUrl(course.thumbnail_url) || DEFAULT_COURSE_IMAGE_URL}
                  alt={course.title}
                  onError={handleCourseImageError}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="text-xs uppercase tracking-wide px-2 py-1 rounded-full bg-cyan-400/10 text-cyan-300 border border-cyan-400/30">
                    {course.catalog_group || course.catalogGroup || 'General'}
                  </span>
                  <span className="text-xs uppercase tracking-wide px-2 py-1 rounded-full bg-yellow-400/10 text-yellow-400 border border-yellow-400/30">
                    {course.category || 'General'}
                  </span>
                  <span className="text-xs uppercase tracking-wide px-2 py-1 rounded-full bg-gray-800 text-gray-200 border border-gray-700">
                    {course.difficulty_level || course.level || 'Beginner'}
                  </span>
                </div>
                
                <h3 className="text-xl font-bold mb-2 text-yellow-400">{course.title}</h3>
                <p className="text-gray-400 mb-4 line-clamp-3">{course.description}</p>
                {course.best_for && <div className="text-sm text-gray-300 mb-3">Best For: <span className="text-yellow-300">{course.best_for}</span></div>}
                                <div className="text-sm text-gray-300 mb-4">
                                  Instructor: <span className="text-yellow-400 font-medium">{course.teacher_name || course.instructor || 'K.R.A Instructor Team'}</span>
                                </div>
                {!!asArray(course.assigned_teachers).length && (
                  <div className="text-xs text-gray-400 mb-4">
                    Assigned Teachers: {asArray(course.assigned_teachers).filter(Boolean).map((item) => item.name).filter(Boolean).join(', ')}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
                  <div className="bg-gray-800 rounded-lg px-3 py-2 border border-yellow-400/10">
                    <div className="text-gray-400">Availability</div>
                    <div className="text-green-300 font-semibold">{course.availability_label || 'OPEN FOR ENROLLMENT'}</div>
                  </div>
                  <div className="bg-gray-800 rounded-lg px-3 py-2 border border-yellow-400/10">
                    <div className="text-gray-400">Delivery</div>
                    <div className="text-yellow-300 font-semibold">{course.delivery_label || 'ONLINE'}</div>
                  </div>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Lessons:</span>
                    <span className="text-yellow-400">{course.lessons?.length || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Duration:</span>
                    <span className="text-yellow-400">{formatCourseDuration(course.duration || 'Approximately 4 Months')}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Level:</span>
                    <span className="text-yellow-400">{course.difficulty_level || course.level || 'Beginner'}</span>
                  </div>
                  {isBeforeAfterSchoolClubCourse(course) && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Club Fee:</span>
                      <span className="text-yellow-300 font-semibold">${getBeforeAfterSchoolClubPrice(course).toFixed(2)} CAD</span>
                    </div>
                  )}
                </div>

                {user ? (
                  <div className="space-y-2">
                    <button
                      onClick={() => setSelectedCourse(course)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-all"
                    >
                      View Course
                    </button>
                    <button
                      onClick={() => saveCourse(course.id)}
                      disabled={savingCourseId === course.id}
                      className="w-full border border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black py-2 px-4 rounded-lg font-medium transition-all disabled:opacity-60"
                    >
                      {savingCourseId === course.id ? 'Saving...' : 'Save Course'}
                    </button>
                    <button
                      onClick={() => openRegistrationFlow(course)}
                      disabled={enrollingCourseId === course.id}
                      className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black py-3 px-4 rounded-lg font-bold transition-all transform hover:scale-105 disabled:opacity-60"
                    >
                      {enrollingCourseId === course.id ? 'Submitting...' : 'Enroll Now'}
                    </button>
                    <InlineFeedback feedback={courseActionFeedback[course.id]} />
                  </div>
                ) : (
                  <div className="text-center space-y-2">
                    <button
                      onClick={() => setSelectedCourse(course)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-all"
                    >
                      View Course
                    </button>
                    <p className="text-yellow-300 text-sm">Please login or create an account before enrolling in a course.</p>
                    <button
                      onClick={() => openRegistrationFlow(course)}
                      className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black py-3 px-4 rounded-lg font-bold transition-all"
                    >
                      Enroll Course
                    </button>
                    <button
                      onClick={() => {
                        localStorage.setItem('kra_post_login_redirect', 'courses');
                        if (onPageChange) onPageChange('auth', { search: '?role=student&mode=login' });
                      }}
                      className="w-full border border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black py-3 px-4 rounded-lg font-bold transition-all"
                    >
                      Already have account? Sign In
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {courses.length > 0 && filteredCourses.length === 0 && (
          <div className="mt-6 text-center text-gray-400">
            {activeCategoryMeta
              ? `No ${activeCategoryMeta.value} courses match your filters right now.`
              : 'No courses match your search.'}
          </div>
        )}
        {courses.length === 0 && (
          <div className="mt-6 text-center text-gray-400">No courses available right now.</div>
        )}

        {(!courses || courses.length === 0) && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-2xl font-bold text-yellow-400 mb-2">No Courses Available</h3>
            <p className="text-gray-400">Courses are being loaded. Please check back soon!</p>
          </div>
        )}
      </div>

      {/* Course Viewer Modal */}
      {selectedCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-yellow-400">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-yellow-400">{selectedCourse.title}</h2>
                <button 
                  onClick={() => setSelectedCourse(null)}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  ✕
                </button>
              </div>

              <div className="h-56 mb-6 overflow-hidden rounded-xl border border-yellow-400/20 bg-gray-800">
                <img
                  src={resolveImageUrl(selectedCourse.thumbnail_url) || DEFAULT_COURSE_IMAGE_URL}
                  alt={`${selectedCourse.title || 'Course'} course image`}
                  onError={handleCourseImageError}
                  className="h-full w-full object-cover"
                />
              </div>

              {/* Course Content */}
              <div className="space-y-6">
                <div className="bg-gray-800 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4 text-sm">
                    <div className="bg-gray-900 rounded-lg p-3 border border-yellow-400/10">
                      <div className="text-gray-400">Category</div>
                      <div className="text-white font-medium">{selectedCourse.category}</div>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-3 border border-yellow-400/10">
                      <div className="text-gray-400">Availability</div>
                      <div className="text-green-300 font-medium">{selectedCourse.availability_label || 'OPEN FOR ENROLLMENT'}</div>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-3 border border-yellow-400/10">
                      <div className="text-gray-400">Delivery</div>
                      <div className="text-yellow-300 font-medium">{selectedCourse.delivery_label || 'ONLINE'}</div>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-3 border border-yellow-400/10">
                      <div className="text-gray-400">{selectedCourseIsClub ? 'Club Fee' : 'Program Options'}</div>
                      <div className="text-white font-medium">{selectedCourseIsClub ? `$${getBeforeAfterSchoolClubPrice(selectedCourse).toFixed(2)} CAD` : 'Group and 1:1'}</div>
                    </div>
                  </div>
                  {selectedCourse.best_for && (
                    <div className="mb-3 text-sm text-gray-300">
                      Best For: <span className="text-yellow-300">{selectedCourse.best_for}</span>
                    </div>
                  )}
                  <h3 className="text-yellow-400 font-bold mb-2">📖 Course Overview</h3>
                  <p className="text-gray-300">{selectedCourse.overview || selectedCourse.description}</p>
                  <div className="mt-3 text-sm text-gray-300">
                    Instructor Information: <span className="text-yellow-400">{selectedCourse.teacher_name || selectedCourse.instructor || 'K.R.A Instructor Team'}</span>
                  </div>
                  {!!asArray(selectedCourse.assigned_teachers).length && (
                    <div className="mt-2 text-sm text-gray-300">
                      Assigned Teachers: <span className="text-yellow-300">{asArray(selectedCourse.assigned_teachers).filter(Boolean).map((item) => item.name).filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                  {selectedCourse.instructor_bio && <p className="mt-2 text-gray-400 text-sm">{selectedCourse.instructor_bio}</p>}
                  {selectedCourse.instructor_expertise?.length > 0 && <p className="mt-2 text-gray-400 text-sm">Expertise: {selectedCourse.instructor_expertise.join(', ')}</p>}
                </div>

                <div className="bg-gray-800 p-4 rounded-lg">
                  <h3 className="text-yellow-400 font-bold mb-3">👥 Select Teacher For Enrollment</h3>
                  {teachers.filter((teacher) => asArray(teacher.courses).some((item) => item?.id === selectedCourse.id)).length > 0 ? (
                    <select
                      value={selectedTeacherByCourse[selectedCourse.id] || ''}
                      onChange={(e) => setSelectedTeacherByCourse({ ...selectedTeacherByCourse, [selectedCourse.id]: e.target.value })}
                      className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                    >
                      <option value="">Use default instructor</option>
                      {teachers
                        .filter((teacher) => asArray(teacher.courses).some((item) => item?.id === selectedCourse.id))
                        .map((teacher) => (
                          <option key={teacher.id} value={teacher.id}>{teacher.name} ({teacher.availability_status})</option>
                        ))}
                    </select>
                  ) : (
                    <p className="text-gray-400 text-sm">This course currently uses its assigned instructor profile.</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h3 className="text-yellow-400 font-bold mb-3">Prerequisites</h3>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      {(asArray(selectedCourse.prerequisites).length ? asArray(selectedCourse.prerequisites) : ['None']).map((item, index) => (
                        <li key={`prereq-${index}`}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h3 className="text-yellow-400 font-bold mb-3">What You Will Learn</h3>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      {(asArray(selectedCourse.what_you_will_learn).length ? asArray(selectedCourse.what_you_will_learn) : asArray(selectedCourse.learning_objectives)).map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h3 className="text-yellow-400 font-bold mb-3">Learning Outcomes</h3>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      {asArray(selectedCourse.learning_outcomes).map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h3 className="text-yellow-400 font-bold mb-3">Skills Developed</h3>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      {asArray(selectedCourse.skills_developed).map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h3 className="text-yellow-400 font-bold mb-3">Projects & Activities</h3>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      {(asArray(selectedCourse.projects).length ? asArray(selectedCourse.projects) : asArray(selectedCourse.projects_activities)).map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h3 className="text-yellow-400 font-bold mb-3">Student Expectations</h3>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      {asArray(selectedCourse.student_expectations).map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h3 className="text-yellow-400 font-bold mb-3">Potential Career Paths</h3>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      {(asArray(selectedCourse.career_paths).length ? asArray(selectedCourse.career_paths) : asArray(selectedCourse.potential_career_paths)).map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                    {selectedCourse.career_relevance && <p className="mt-3 text-gray-400 text-sm">{selectedCourse.career_relevance}</p>}
                  </div>
                </div>

                <div className="bg-gray-800 p-4 rounded-lg">
                  <h3 className="text-yellow-400 font-bold mb-3">{selectedCourseIsClub ? 'Club Pricing' : 'Program Options'}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {asArray(selectedCourse.program_options).filter(Boolean).map((option) => (
                      <div key={option.id} className="bg-gray-900 border border-yellow-400/15 rounded-xl p-4">
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <h4 className="text-white font-semibold">{option.title}</h4>
                          <span className="text-yellow-300 font-bold">${Number(option.price_cad || 0).toFixed(2)} CAD/{option.billing === 'hour' ? 'hr' : option.billing === 'club' ? 'club' : 'month'}</span>
                        </div>
                        <div className="text-xs text-gray-400 mb-3">{option.billing === 'club' ? `Fixed club tuition: $${Number(option.price_cad_with_tax || option.price_cad || 0).toFixed(2)} CAD` : `Tax-inclusive monthly reference: $${Number(option.price_cad_with_tax || 0).toFixed(2)} CAD/month`}</div>
                        {asArray(option.includes).length > 0 && (
                          <ul className="space-y-1 text-sm text-gray-300 mb-3">
                            {asArray(option.includes).map((item, index) => <li key={index}>• {item}</li>)}
                          </ul>
                        )}
                        {option.billing === 'club' ? <p className="text-sm text-gray-300 mb-2">Fixed-price club enrollment</p> : option.minimum_sessions_per_week ? <p className="text-sm text-gray-300 mb-2">Minimum {option.minimum_sessions_per_week} classes per week • {option.session_length || '1 Hour Per Session'}</p> : null}
                        <p className="text-sm text-yellow-200">{option.recommendation}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-800 p-4 rounded-lg">
                  <h3 className="text-yellow-400 font-bold mb-4">📚 Course Lessons</h3>
                  <div className="space-y-3">
                    {(asArray(selectedCourse.modules).length ? asArray(selectedCourse.modules).filter(Boolean).flatMap((module) => asArray(module.lessons).map((lesson) => `${module.title || 'Module'}: ${lesson?.title || 'Lesson'}`)) : [
                      "Introduction to the Course",
                      "Setting Up Your Environment", 
                      "Basic Concepts and Theory",
                      "Hands-on Practice Session",
                      "Advanced Topics",
                      "Real-world Projects",
                      "Final Assessment"
                    ]).map((lesson, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-900 p-3 rounded">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            index < 2 ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                          }`}>
                            {index < 2 ? '✓' : index + 1}
                          </div>
                          <span className={index < 2 ? 'text-white' : 'text-gray-400'}>
                            {lesson}
                          </span>
                        </div>
                        <button className={`px-4 py-2 rounded text-sm font-medium ${
                          index < 2 
                            ? 'bg-yellow-500 hover:bg-yellow-600 text-black' 
                            : index === 2
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-gray-600 text-gray-300 cursor-not-allowed'
                        }`}>
                          {index < 2 ? 'Review' : index === 2 ? 'Start' : 'Locked'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-800 p-4 rounded-lg">
                  <h3 className="text-yellow-400 font-bold mb-2">📊 Your Progress</h3>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-300">Course Completion</span>
                    <span className="text-yellow-400 font-bold">28%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-3">
                    <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 h-3 rounded-full" style={{width: '28%'}}></div>
                  </div>
                  <p className="text-sm text-gray-400 mt-2">2 of 7 lessons completed</p>
                </div>

                <div className="flex space-x-4">
                  <button 
                    onClick={() => {
                      openRegistrationFlow(selectedCourse);
                    }}
                    className="flex-1 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black py-3 px-6 rounded-lg font-bold transition-all"
                  >
                    Start Enrollment
                  </button>
                  <button 
                    onClick={() => setSelectedCourse(null)}
                    className="px-6 py-3 border border-gray-600 text-gray-300 hover:text-white hover:border-gray-400 rounded-lg transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {registrationCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60]">
          <div className="bg-gray-900 rounded-lg max-w-5xl w-full mx-4 max-h-[92vh] overflow-y-auto border border-yellow-400">
            <div className="p-6 space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm text-gray-400">Professional Course Enrollment</div>
                  <h2 className="text-2xl font-bold text-yellow-400">{registrationCourse.title}</h2>
                  <p className="text-gray-300 mt-1">Step {registrationStep} of 4</p>
                </div>
                <button onClick={closeRegistrationFlow} className="text-gray-400 hover:text-white text-2xl">✕</button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                {['Student Information', 'Educational Information', 'Program Selection', 'Payment & Submission'].map((label, index) => (
                  <div key={label} className={`rounded-lg border px-3 py-3 ${registrationStep === index + 1 ? 'border-yellow-400 bg-yellow-400/10 text-yellow-300' : 'border-gray-700 bg-gray-800 text-gray-400'}`}>
                    <div className="font-medium">Step {index + 1}</div>
                    <div>{label}</div>
                  </div>
                ))}
              </div>

              {registrationStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-yellow-400 mb-4">Student Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <input value={registrationForm.student_information.first_name} onChange={(e) => updateRegistrationSection('student_information', 'first_name', e.target.value)} className={registrationInputClass('student_information.first_name')} placeholder="First Name" />
                        {registrationFieldError('student_information.first_name')}
                      </div>
                      <div>
                        <input value={registrationForm.student_information.last_name} onChange={(e) => updateRegistrationSection('student_information', 'last_name', e.target.value)} className={registrationInputClass('student_information.last_name')} placeholder="Last Name" />
                        {registrationFieldError('student_information.last_name')}
                      </div>
                      <div>
                        <input value={registrationForm.student_information.date_of_birth} onChange={(e) => {
                        const age = calculateAge(e.target.value);
                        setRegistrationForm((current) => ({
                          ...current,
                          student_information: {
                            ...current.student_information,
                            date_of_birth: e.target.value,
                            age,
                          },
                        }));
                        }} type="date" className={registrationInputClass('student_information.date_of_birth')} />
                        {registrationFieldError('student_information.date_of_birth')}
                      </div>
                      <div>
                        <input value={registrationForm.student_information.age} onChange={(e) => updateRegistrationSection('student_information', 'age', e.target.value)} type="number" className={registrationInputClass('student_information.age')} placeholder="Age" />
                        {registrationFieldError('student_information.age')}
                      </div>
                      <input value={registrationForm.student_information.gender} onChange={(e) => updateRegistrationSection('student_information', 'gender', e.target.value)} className={registrationInputClass('student_information.gender')} placeholder="Gender (Optional)" />
                      <div>
                        <input value={registrationForm.student_information.email} onChange={(e) => updateRegistrationSection('student_information', 'email', e.target.value)} type="email" className={registrationInputClass('student_information.email')} placeholder="Email Address" />
                        {registrationFieldError('student_information.email')}
                      </div>
                      <div className="md:col-span-2">
                        <input value={registrationForm.student_information.phone_number} onChange={(e) => updateRegistrationSection('student_information', 'phone_number', e.target.value)} className={registrationInputClass('student_information.phone_number', 'w-full')} placeholder="Phone Number" />
                        {registrationFieldError('student_information.phone_number')}
                      </div>
                    </div>
                  </div>

                  {isMinorRegistration && (
                    <div>
                      <h3 className="text-xl font-bold text-yellow-400 mb-4">Parent / Guardian Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <input value={registrationForm.parent_guardian_information.name} onChange={(e) => updateRegistrationSection('parent_guardian_information', 'name', e.target.value)} className={registrationInputClass('parent_guardian_information.name')} placeholder="Parent / Guardian Name" />
                          {registrationFieldError('parent_guardian_information.name')}
                        </div>
                        <div>
                          <input value={registrationForm.parent_guardian_information.email} onChange={(e) => updateRegistrationSection('parent_guardian_information', 'email', e.target.value)} type="email" className={registrationInputClass('parent_guardian_information.email')} placeholder="Parent / Guardian Email" />
                          {registrationFieldError('parent_guardian_information.email')}
                        </div>
                        <div>
                          <input value={registrationForm.parent_guardian_information.phone_number} onChange={(e) => updateRegistrationSection('parent_guardian_information', 'phone_number', e.target.value)} className={registrationInputClass('parent_guardian_information.phone_number')} placeholder="Parent / Guardian Phone Number" />
                          {registrationFieldError('parent_guardian_information.phone_number')}
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="text-xl font-bold text-yellow-400 mb-4">Address Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <input value={registrationForm.address_information.street_address} onChange={(e) => updateRegistrationSection('address_information', 'street_address', e.target.value)} className={registrationInputClass('address_information.street_address', 'w-full')} placeholder="Street Address" />
                        {registrationFieldError('address_information.street_address')}
                      </div>
                      <div>
                        <input value={registrationForm.address_information.city} onChange={(e) => updateRegistrationSection('address_information', 'city', e.target.value)} className={registrationInputClass('address_information.city')} placeholder="City" />
                        {registrationFieldError('address_information.city')}
                      </div>
                      <div>
                        <input value={registrationForm.address_information.province_state} onChange={(e) => updateRegistrationSection('address_information', 'province_state', e.target.value)} className={registrationInputClass('address_information.province_state')} placeholder="Province / State" />
                        {registrationFieldError('address_information.province_state')}
                      </div>
                      <div>
                        <input value={registrationForm.address_information.postal_code} onChange={(e) => updateRegistrationSection('address_information', 'postal_code', e.target.value)} className={registrationInputClass('address_information.postal_code')} placeholder="Postal Code" />
                        {registrationFieldError('address_information.postal_code')}
                      </div>
                      <div>
                        <input value={registrationForm.address_information.country} onChange={(e) => updateRegistrationSection('address_information', 'country', e.target.value)} className={registrationInputClass('address_information.country')} placeholder="Country" />
                        {registrationFieldError('address_information.country')}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-yellow-400 mb-4">Emergency Contact</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <input value={registrationForm.emergency_contact.name} onChange={(e) => updateRegistrationSection('emergency_contact', 'name', e.target.value)} className={registrationInputClass('emergency_contact.name')} placeholder="Contact Name" />
                        {registrationFieldError('emergency_contact.name')}
                      </div>
                      <div>
                        <input value={registrationForm.emergency_contact.relationship} onChange={(e) => updateRegistrationSection('emergency_contact', 'relationship', e.target.value)} className={registrationInputClass('emergency_contact.relationship')} placeholder="Relationship" />
                        {registrationFieldError('emergency_contact.relationship')}
                      </div>
                      <div>
                        <input value={registrationForm.emergency_contact.phone_number} onChange={(e) => updateRegistrationSection('emergency_contact', 'phone_number', e.target.value)} className={registrationInputClass('emergency_contact.phone_number')} placeholder="Phone Number" />
                        {registrationFieldError('emergency_contact.phone_number')}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {registrationStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-yellow-400 mb-4">Educational Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <input value={registrationForm.educational_information.current_school_name} onChange={(e) => updateRegistrationSection('educational_information', 'current_school_name', e.target.value)} className={registrationInputClass('educational_information.current_school_name')} placeholder="Current School Name" />
                        {registrationFieldError('educational_information.current_school_name')}
                      </div>
                      <div>
                        <input value={registrationForm.educational_information.current_grade_level} onChange={(e) => updateRegistrationSection('educational_information', 'current_grade_level', e.target.value)} className={registrationInputClass('educational_information.current_grade_level')} placeholder="Current Grade Level" />
                        {registrationFieldError('educational_information.current_grade_level')}
                      </div>
                      <div>
                        <select value={registrationForm.educational_information.previous_experience} onChange={(e) => updateRegistrationSection('educational_information', 'previous_experience', e.target.value)} className={registrationInputClass('educational_information.previous_experience')}>
                          {experienceOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                        </select>
                        {registrationFieldError('educational_information.previous_experience')}
                      </div>
                      <div>
                        <input value={registrationForm.educational_information.educational_goals} onChange={(e) => updateRegistrationSection('educational_information', 'educational_goals', e.target.value)} className={registrationInputClass('educational_information.educational_goals')} placeholder="Educational Goals" />
                        {registrationFieldError('educational_information.educational_goals')}
                      </div>
                      <div className="md:col-span-2">
                        <textarea value={registrationForm.educational_information.program_expectations} onChange={(e) => updateRegistrationSection('educational_information', 'program_expectations', e.target.value)} rows={4} className={registrationInputClass('educational_information.program_expectations', 'w-full')} placeholder="What are you hoping to achieve from this program?" />
                        {registrationFieldError('educational_information.program_expectations')}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {registrationStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-yellow-400 mb-4">Program Selection</h3>
                    {registrationCourseIsClub ? (
                      <div className="rounded-xl border border-yellow-400/30 bg-gray-800 p-5">
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <h4 className="text-lg font-bold text-white">Club Enrollment</h4>
                          <div className="text-right">
                            <div className="text-yellow-300 font-bold">${getBeforeAfterSchoolClubPrice(registrationCourse).toFixed(2)} CAD/club</div>
                            <div className="text-xs text-gray-400">Fixed-price club tuition</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-300">This club uses a single flat registration fee stored with the course record. Monthly class pricing does not apply.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {['group', 'one_on_one'].map((programType) => {
                          const option = getProgramOption(registrationCourse, programType);
                          const selected = registrationForm.program_selection.program_type === programType;
                          return (
                            <button
                              key={programType}
                              type="button"
                              onClick={() => updateRegistrationSection('program_selection', 'program_type', programType)}
                              className={`text-left rounded-xl border p-5 transition-all ${selected ? 'border-yellow-400 bg-yellow-400/10' : 'border-gray-700 bg-gray-800 hover:border-yellow-400/40'}`}
                            >
                              <div className="flex items-center justify-between gap-3 mb-3">
                                <h4 className="text-lg font-bold text-white">{option.title}</h4>
                                {(() => {
                                  const livePricing = calculateEnrollmentPricing(programType, registrationForm.program_selection.sessions_per_week, registrationCourse);
                                  return (
                                    <div className="text-right">
                                      <div className="text-yellow-300 font-bold">${livePricing.perClassRate.toFixed(2)} CAD/class</div>
                                      <div className="text-xs text-gray-400">Monthly total: ${livePricing.total.toFixed(2)} CAD (incl. HST)</div>
                                    </div>
                                  );
                                })()}
                              </div>
                              {option.includes?.length > 0 && <div className="text-sm text-gray-300 mb-3">{option.includes.join(' • ')}</div>}
                              {option.minimum_sessions_per_week ? <div className="text-sm text-gray-300 mb-3">Minimum {option.minimum_sessions_per_week} classes per week • {option.session_length || '1 Hour Per Session'}</div> : <div className="text-sm text-gray-300 mb-3">Default 5 classes per week • 1 Hour Per Class</div>}
                              <div className="text-sm text-yellow-200">{option.recommendation}</div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className={`bg-gray-800 rounded-xl p-4 ${registrationErrors['program_selection.preferred_days'] ? 'ring-1 ring-red-500/50 border border-red-500/40' : ''}`}>
                      <h4 className="text-yellow-400 font-bold mb-3">Preferred Days</h4>
                      <div className="space-y-2">
                        {dayOptions.map((day) => (
                          <label key={day} className="flex items-center gap-2 text-gray-300 text-sm">
                            <input type="checkbox" checked={(registrationForm.program_selection.preferred_days || []).includes(day)} onChange={() => toggleProgramSelection('preferred_days', day)} />
                            <span>{day}</span>
                          </label>
                        ))}
                      </div>
                      {registrationFieldError('program_selection.preferred_days')}
                    </div>
                    <div className={`bg-gray-800 rounded-xl p-4 ${registrationErrors['program_selection.preferred_times'] ? 'ring-1 ring-red-500/50 border border-red-500/40' : ''}`}>
                      <h4 className="text-yellow-400 font-bold mb-3">Preferred Times</h4>
                      <div className="space-y-2">
                        {timeOptions.map((time) => (
                          <label key={time} className="flex items-center gap-2 text-gray-300 text-sm">
                            <input type="checkbox" checked={(registrationForm.program_selection.preferred_times || []).includes(time)} onChange={() => toggleProgramSelection('preferred_times', time)} />
                            <span>{time}</span>
                          </label>
                        ))}
                      </div>
                      {registrationFieldError('program_selection.preferred_times')}
                    </div>
                    <div className="bg-gray-800 rounded-xl p-4">
                      <h4 className="text-yellow-400 font-bold mb-3">{registrationCourseIsClub ? 'Enrollment Fee' : 'Classes Per Week'}</h4>
                      {registrationCourseIsClub ? (
                        <>
                          <div className="w-full rounded-lg border border-gray-600 bg-gray-900 px-4 py-3 text-white">${getBeforeAfterSchoolClubPrice(registrationCourse).toFixed(2)} CAD fixed club tuition</div>
                          <p className="text-gray-400 text-sm mt-3">Club registrations use a single stored fee instead of monthly class counts.</p>
                        </>
                      ) : (
                        <>
                          <select
                            value={registrationForm.program_selection.sessions_per_week}
                            onChange={(e) => updateRegistrationSection('program_selection', 'sessions_per_week', e.target.value)}
                            className={`w-full bg-gray-900 text-white px-4 py-3 rounded-lg border ${registrationErrors['program_selection.sessions_per_week'] ? 'border-red-500 ring-1 ring-red-500/50' : 'border-gray-600'}`}
                          >
                            {(registrationForm.program_selection.program_type === 'one_on_one' ? [3, 4, 5, 6, 7] : [5, 10, 15, 20]).map((count) => (
                              <option key={count} value={String(count)}>{count} classes / week</option>
                            ))}
                          </select>
                          <p className="text-gray-400 text-sm mt-3">
                            {registrationForm.program_selection.program_type === 'one_on_one' ? 'Minimum 3 classes/week. Monthly billing only.' : 'Default 5 classes/week. Monthly billing only.'}
                          </p>
                          {registrationFieldError('program_selection.sessions_per_week')}
                        </>
                      )}
                    </div>
                  </div>

                  <div className={`bg-gray-800 rounded-xl p-4 space-y-3 ${(registrationErrors.terms_and_conditions_accepted || registrationErrors.consent_forms_accepted) ? 'ring-1 ring-red-500/50 border border-red-500/40' : ''}`}>
                    <h4 className="text-yellow-400 font-bold">Terms & Conditions</h4>
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                      <input
                        type="checkbox"
                        checked={registrationForm.terms_and_conditions_accepted}
                        onChange={(e) => updateRegistrationRoot('terms_and_conditions_accepted', e.target.checked)}
                      />
                      <span>
                        I accept the <button type="button" onClick={() => onPageChange('terms')} className="text-yellow-400 hover:text-yellow-300 underline">Terms and Conditions</button>.
                      </span>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                      <input
                        type="checkbox"
                        checked={registrationForm.consent_forms_accepted}
                        onChange={(e) => updateRegistrationRoot('consent_forms_accepted', e.target.checked)}
                      />
                      <span>I consent to the course enrollment and payment review process.</span>
                    </label>
                    {registrationFieldError('terms_and_conditions_accepted')}
                    {registrationFieldError('consent_forms_accepted')}
                  </div>

                  <div className="bg-gray-800 rounded-xl p-4">
                    <h4 className="text-yellow-400 font-bold mb-3">Assigned Teacher</h4>
                    <select value={registrationForm.teacher_id} onChange={(e) => updateRegistrationRoot('teacher_id', e.target.value)} className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-600">
                      <option value={registrationCourse.teacher_id || ''}>Use assigned instructor</option>
                      {getAvailableTeachersForCourse(registrationCourse).map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                      ))}
                    </select>
                  </div>

                  <textarea value={registrationForm.note} onChange={(e) => updateRegistrationRoot('note', e.target.value)} rows={3} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Optional notes for the admissions team or instructor" />
                </div>
              )}

              {registrationStep === 4 && (() => {
                const selectedProgramType = registrationCourseIsClub ? 'club' : registrationForm.program_selection.program_type;
                const option = getProgramOption(registrationCourse, selectedProgramType);
                const pricing = calculateEnrollmentPricing(selectedProgramType, registrationForm.program_selection.sessions_per_week, registrationCourse);
                const selectedTeacher = getAvailableTeachersForCourse(registrationCourse).find((teacher) => teacher.id === registrationForm.teacher_id);
                return (
                  <div className="space-y-6">
                    <div className="bg-gray-800 rounded-xl p-5">
                      <h3 className="text-xl font-bold text-yellow-400 mb-4">Payment Before Submission</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                        <div>
                          <div className="text-gray-400 mb-2">Student Information</div>
                          <div className="text-white">{registrationForm.student_information.first_name} {registrationForm.student_information.last_name}</div>
                          <div className="text-gray-300">{registrationForm.student_information.email}</div>
                          <div className="text-gray-300">{registrationForm.student_information.phone_number}</div>
                          <div className="text-gray-300">Age: {registrationForm.student_information.age}</div>
                        </div>
                        <div>
                          <div className="text-gray-400 mb-2">Course & Program</div>
                          <div className="text-white">{registrationCourse.title}</div>
                          <div className="text-gray-300">{option.title}</div>
                          <div className="text-gray-300">Assigned Teacher: {(selectedTeacher || {}).name || registrationCourse.teacher_name || registrationCourse.instructor || 'K.R.A Instructor Team'}</div>
                          <div className="text-gray-300">{registrationCourseIsClub ? 'Club fee' : 'Rate per class'}: ${pricing.perClassRate.toFixed(2)} CAD</div>
                          <div className="text-gray-300">{registrationCourseIsClub ? 'Enrollment model' : 'Classes per week'}: {registrationCourseIsClub ? 'Fixed-price club registration' : pricing.sessionsPerWeek}</div>
                        </div>
                        <div>
                          <div className="text-gray-400 mb-2">Educational Information</div>
                          <div className="text-gray-300">School: {registrationForm.educational_information.current_school_name}</div>
                          <div className="text-gray-300">Grade: {registrationForm.educational_information.current_grade_level}</div>
                          <div className="text-gray-300">Experience: {registrationForm.educational_information.previous_experience}</div>
                        </div>
                        <div>
                          <div className="text-gray-400 mb-2">Learning Goals</div>
                          <div className="text-gray-300">{registrationForm.educational_information.educational_goals}</div>
                          <div className="text-gray-300 mt-2">{registrationForm.educational_information.program_expectations}</div>
                        </div>
                      </div>
                      <div className="mt-5 border-t border-gray-700 pt-4 text-sm text-gray-300">
                        Preferred Days: {(registrationForm.program_selection.preferred_days || []).join(', ')}
                        <br />
                        Preferred Times: {(registrationForm.program_selection.preferred_times || []).join(', ')}
                        <br />
                        {registrationCourseIsClub ? `Club Fee: $${getBeforeAfterSchoolClubPrice(registrationCourse).toFixed(2)} CAD` : `Classes Per Week: ${registrationForm.program_selection.sessions_per_week}`}
                      </div>
                    </div>

                    <div className="bg-gray-800 rounded-xl p-5">
                      <h4 className="text-lg font-bold text-yellow-400 mb-3">{registrationCourseIsClub ? 'Club Payment Summary' : 'Monthly Payment Summary'}</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between"><span className="text-gray-300">Subtotal</span><span className="text-white">${pricing.subtotal.toFixed(2)} CAD</span></div>
                        <div className="flex items-center justify-between"><span className="text-gray-300">{registrationCourseIsClub ? 'Tax' : 'HST (13%)'}</span><span className="text-white">${pricing.hstAmount.toFixed(2)} CAD</span></div>
                        <div className="flex items-center justify-between border-t border-gray-700 pt-2"><span className="text-gray-100 font-semibold">{registrationCourseIsClub ? 'Total Club Payment' : 'Total Monthly Payment'}</span><span className="text-yellow-300 font-bold">${pricing.total.toFixed(2)} CAD</span></div>
                      </div>
                      <p className="text-xs text-gray-400 mt-3">{registrationCourseIsClub ? 'Payment is required before your club application is submitted for admin review.' : 'We do not bill hourly. Payment is required before your application is submitted for admin review.'}</p>
                    </div>
                  </div>
                );
              })()}

              {Object.values(registrationErrors).filter(Boolean).length > 0 && (
                <div className="bg-red-900/40 border border-red-500/40 rounded-lg p-3 text-sm text-red-200">
                  Please complete all required fields for this step before continuing.
                </div>
              )}
              <InlineFeedback feedback={registrationFeedback} />

              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => registrationStep === 1 ? closeRegistrationFlow() : setRegistrationStep((current) => current - 1)}
                  className="px-5 py-3 border border-gray-600 text-gray-300 rounded-lg hover:text-white hover:border-gray-400"
                >
                  {registrationStep === 1 ? 'Cancel' : 'Back'}
                </button>
                <div className="flex items-center gap-3">
                  {registrationStep < 4 ? (
                    <button
                      onClick={() => {
                        if (validateRegistrationStep(registrationStep)) {
                          setRegistrationStep((current) => current + 1);
                        }
                      }}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black px-5 py-3 rounded-lg font-bold"
                    >
                      Continue
                    </button>
                  ) : (
                    <button
                      onClick={submitRegistration}
                      disabled={enrollingCourseId === registrationCourse.id}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black px-5 py-3 rounded-lg font-bold disabled:opacity-60"
                    >
                      {enrollingCourseId === registrationCourse.id ? 'Redirecting to Payment...' : 'Proceed to Payment'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedTeacherProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-yellow-400">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-yellow-400">Teacher Profile</h2>
                <button onClick={() => setSelectedTeacherProfile(null)} className="text-gray-400 hover:text-white text-2xl">✕</button>
              </div>
              <div className="flex flex-col md:flex-row gap-6">
                <img
                  src={resolveImageUrl(selectedTeacherProfile.profile_photo_url) || 'https://api.dicebear.com/7.x/initials/svg?seed=Teacher'}
                  alt={selectedTeacherProfile.name}
                  className="w-28 h-28 rounded-full object-cover border border-yellow-400/30"
                />
                <div className="flex-1 space-y-2">
                  <h3 className="text-2xl font-bold text-yellow-400">{selectedTeacherProfile.name}</h3>
                  <p className="text-gray-300">{selectedTeacherProfile.email}</p>
                  <p className="text-gray-300">{selectedTeacherProfile.bio || 'Teacher profile available.'}</p>
                  <p className="text-gray-400 text-sm">Expertise: {(selectedTeacherProfile.expertise || []).join(', ') || 'General'}</p>
                  <p className="text-gray-400 text-sm">Availability: {selectedTeacherProfile.availability_status}</p>
                  <p className="text-gray-400 text-sm">Total Students: {selectedTeacherProfile.students_count || 0}</p>
                  <p className="text-gray-400 text-sm">Rating: {selectedTeacherProfile.rating || 0}</p>
                </div>
              </div>
              <div className="mt-6">
                <h4 className="text-xl font-bold text-yellow-400 mb-3">Courses Offered</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(selectedTeacherProfile.courses || []).map((course) => (
                    <div key={course.id} className="bg-gray-800 rounded-xl p-4 border border-yellow-400/15">
                      <div className="text-yellow-400 font-semibold">{course.title}</div>
                      <div className="text-sm text-gray-400">{course.category}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={() => requestEnrollmentUnderTeacher(selectedTeacherProfile.id)} className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-bold">Request Enrollment</button>
                <button onClick={() => setSelectedTeacherProfile(null)} className="border border-gray-600 text-gray-300 hover:text-white hover:border-gray-400 px-4 py-2 rounded-lg">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {guestEnrollmentPromptCourse && (
        <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center px-4">
          <div className="w-full max-w-xl rounded-2xl border border-yellow-400/30 bg-gray-900 p-6">
            <div className="text-sm uppercase tracking-wide text-yellow-400 mb-2">Enrollment Access</div>
            <h3 className="text-2xl font-bold text-white mb-2">Create a Student Account to Enroll</h3>
            <p className="text-gray-300 mb-4">You must create a Student account before enrolling and making payment for a course.</p>
            <div className="rounded-xl border border-yellow-400/20 bg-gray-950/70 p-3 mb-5">
              <div className="text-xs text-gray-400">Selected course</div>
              <div className="text-yellow-300 font-semibold">{guestEnrollmentPromptCourse.title}</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => continueGuestEnrollmentAsStudent('register')}
                className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-3 rounded-lg font-bold"
              >
                Create Student Account
              </button>
              <button
                type="button"
                onClick={() => continueGuestEnrollmentAsStudent('login')}
                className="border border-yellow-400 text-yellow-300 hover:bg-yellow-400 hover:text-black px-4 py-3 rounded-lg font-bold"
              >
                Already have an account? Sign In
              </button>
            </div>
            <button
              type="button"
              onClick={() => setGuestEnrollmentPromptCourse(null)}
              className="w-full mt-3 text-gray-400 hover:text-white"
            >
              Continue browsing
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Shop Page Component  
const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { user } = useAuth();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      const productsData = response.data.products || response.data || [];
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const purchaseProduct = async (productId) => {
    if (!user) {
      console.warn('Please login to make a purchase');
      return;
    }
    setSelectedProductId(productId);
    setShowPaymentModal(true);
  };

  const handleProductPayment = async (provider) => {
    if (!selectedProductId) return;
    setPaymentLoading(true);
    setPaymentError(null);
    try {
      const response = await axios.post(`${API}/payments/create-checkout-session`, {
        product_id: selectedProductId,
        provider
      });
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      } else if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        setPaymentError('Unexpected payment response');
      }
    } catch (error) {
      setPaymentError(parseApiError(error, 'Failed to start payment'));
    } finally {
      setPaymentLoading(false);
      setShowPaymentModal(false);
      setSelectedProductId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-yellow-400 mb-4">🛍️ Tech Shop</h1>
          <p className="text-xl text-gray-300">Cutting-edge technology to accelerate your learning</p>
        </div>

        <div className="flex items-center justify-end mb-6">
          <label className="text-gray-300 mr-3">Filter by category:</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600 focus:border-yellow-400"
          >
            <option value="all">All</option>
            <option value="Robotics">Robotics</option>
            <option value="IoT">IoT</option>
            <option value="Accessories">Accessories</option>
            <option value="Software">Software</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products && products.length > 0 && products
            .filter(p => selectedCategory === 'all' ? true : (p.category?.toLowerCase?.() || '').includes(selectedCategory.toLowerCase()))
            .map((product) => (
            <div key={product.id} className="bg-gray-900 border border-yellow-400/30 rounded-lg overflow-hidden hover:border-yellow-400 transition-all">
              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="mb-4">
                    {product.images && product.images.length > 0 ? (
                      <div className="w-full h-48 bg-white flex items-center justify-center rounded-md border border-gray-200 overflow-hidden">
                        <img
                          src={`${BACKEND_URL}${product.images[0]}`}
                          alt={product.name}
                          className="max-h-48 w-auto object-contain"
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div className="text-6xl">
                        {product.name.includes('Glasses') ? '👓' : 
                         product.name.includes('Robotics') ? '🤖' : 
                         product.name.includes('IoT') ? '🔌' : '💻'}
                      </div>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-yellow-400">{product.name}</h3>
                </div>
                
                <p className="text-gray-400 mb-6 text-center">{product.description}</p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Category:</span>
                    <span className="text-yellow-400 capitalize">{product.category}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Stock:</span>
                    <span className="text-green-400">Available</span>
                  </div>
                  <div className="border-t border-gray-700 pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Price:</span>
                      <span className="text-3xl font-bold text-yellow-400">${product.price}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => purchaseProduct(product.id)}
                  className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black py-3 px-4 rounded-lg font-bold transition-all transform hover:scale-105"
                >
                  Pre-order
                </button>
              </div>
            </div>
          ))}
        </div>

        {(!products || products.length === 0) && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🛍️</div>
            <h3 className="text-2xl font-bold text-yellow-400 mb-2">No Products Available</h3>
            <p className="text-gray-400">Products are being loaded. Please check back soon!</p>
          </div>
        )}
      </div>
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-900 border border-yellow-400 rounded-lg shadow-2xl w-96 p-6">
            <h2 className="text-xl font-bold text-yellow-400 mb-4">Choose Payment Method</h2>
            {paymentError && (
              <div className="text-red-400 text-sm mb-3">{paymentError}</div>
            )}
            <div className="space-y-3">
              <button
                onClick={() => handleProductPayment('stripe')}
                disabled={paymentLoading}
                className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black py-2 px-4 rounded-lg font-bold transition-all disabled:opacity-50"
              >
                Pay with Card (Stripe)
              </button>
              <button
                onClick={() => handleProductPayment('paypal')}
                disabled={paymentLoading}
                className="w-full border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black py-2 px-4 rounded-lg font-bold transition-all disabled:opacity-50"
              >
                Pay with PayPal
              </button>
              <button
                onClick={() => { setShowPaymentModal(false); setSelectedProductId(null); }}
                disabled={paymentLoading}
                className="w-full bg-gray-800 hover:bg-gray-700 text-white py-2 px-4 rounded-lg font-medium transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Pricing Page Component
const PricingPage = () => {
  const [groupClassesPerWeek, setGroupClassesPerWeek] = useState(5);
  const [oneOnOneClassesPerWeek, setOneOnOneClassesPerWeek] = useState(3);

  const calcPrice = (rate, classesPerWeek) => {
    const subtotal = Number((rate * classesPerWeek * 4).toFixed(2));
    const hst = Number((subtotal * 0.13).toFixed(2));
    return { subtotal, hst, total: Number((subtotal + hst).toFixed(2)) };
  };

  const groupPricing = calcPrice(30, groupClassesPerWeek);
  const oneOnOnePricing = calcPrice(40, oneOnOneClassesPerWeek);

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-yellow-400 mb-4">Official K.R.A Pricing</h1>
          <p className="text-xl text-gray-300">Monthly billing model for all enrollment applications</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-gray-900 border border-yellow-400/30 rounded-2xl p-7">
            <h2 className="text-2xl font-bold text-yellow-400 mb-2">Group Learning Program</h2>
            <p className="text-sm text-gray-300 mb-4">$30 CAD per class, choose 5, 10, 15, or 20 classes/week, monthly billing only.</p>
            <label className="text-sm text-gray-400">Classes per week</label>
            <select value={groupClassesPerWeek} onChange={(e) => setGroupClassesPerWeek(Number(e.target.value))} className="w-full mt-2 bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600">
              {[5, 10, 15, 20].map((count) => <option key={count} value={count}>{count}</option>)}
            </select>
            <div className="mt-5 space-y-2 text-sm">
              <div className="flex items-center justify-between"><span className="text-gray-300">Formula</span><span className="text-white">Classes Selected × $30 × 4 Weeks</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-300">Monthly Subtotal</span><span className="text-white">${groupPricing.subtotal.toFixed(2)} CAD</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-300">HST (13%)</span><span className="text-white">${groupPricing.hst.toFixed(2)} CAD</span></div>
              <div className="flex items-center justify-between border-t border-gray-700 pt-2"><span className="text-gray-100 font-semibold">Total Monthly Payment</span><span className="text-yellow-300 font-bold">${groupPricing.total.toFixed(2)} CAD</span></div>
            </div>
            <p className="text-xs text-gray-400 mt-3">We do not bill hourly. Group classes are billed monthly.</p>
          </div>

          <div className="bg-gray-900 border border-yellow-400/30 rounded-2xl p-7">
            <h2 className="text-2xl font-bold text-yellow-400 mb-2">One-on-One Learning Program</h2>
            <p className="text-sm text-gray-300 mb-4">$40 CAD per class, minimum 3 classes/week, monthly billing only.</p>
            <label className="text-sm text-gray-400">Classes per week</label>
            <select value={oneOnOneClassesPerWeek} onChange={(e) => setOneOnOneClassesPerWeek(Number(e.target.value))} className="w-full mt-2 bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600">
              {[3, 4, 5, 6, 7].map((count) => <option key={count} value={count}>{count}</option>)}
            </select>
            <div className="mt-5 space-y-2 text-sm">
              <div className="flex items-center justify-between"><span className="text-gray-300">Monthly Subtotal</span><span className="text-white">${oneOnOnePricing.subtotal.toFixed(2)} CAD</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-300">HST (13%)</span><span className="text-white">${oneOnOnePricing.hst.toFixed(2)} CAD</span></div>
              <div className="flex items-center justify-between border-t border-gray-700 pt-2"><span className="text-gray-100 font-semibold">Total Monthly Payment</span><span className="text-yellow-300 font-bold">${oneOnOnePricing.total.toFixed(2)} CAD</span></div>
            </div>
            <p className="text-xs text-gray-400 mt-3">We do not bill hourly. One-on-one classes are billed monthly.</p>
          </div>
        </div>

      </div>
    </div>
  );
};

// Community Page Component (WhatsApp-style Chat)
const CommunityPage = () => {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() => window.innerWidth < 768);
  const [mobileView, setMobileView] = useState(() => {
    const stored = localStorage.getItem('kra_community_mobile_view');
    return stored === 'chat' ? 'chat' : 'groups';
  });
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [membersTitle, setMembersTitle] = useState('');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchGroups();
    }
  }, [user]);

  useEffect(() => {
    const onResize = () => {
      setIsMobileViewport(window.innerWidth < 768);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isMobileViewport) {
      setMobileView('groups');
      return;
    }
    if (selectedGroup) {
      const storedView = localStorage.getItem('kra_community_mobile_view');
      if (storedView === 'chat') {
        setMobileView('chat');
      }
    }
  }, [isMobileViewport, selectedGroup]);

  useEffect(() => {
    if (!selectedGroup?.id) return;
    localStorage.setItem('kra_community_selected_group_id', selectedGroup.id);
  }, [selectedGroup?.id]);

  useEffect(() => {
    if (!isMobileViewport) return;
    localStorage.setItem('kra_community_mobile_view', mobileView);
  }, [mobileView, isMobileViewport]);

  useEffect(() => {
    if (selectedGroup) {
      fetchMessages(selectedGroup.id);
      // Set up polling for new messages
      const interval = setInterval(() => {
        fetchMessages(selectedGroup.id);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [selectedGroup]);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/community/groups`);
      const groupsData = response.data.groups || response.data || [];
      setGroups(groupsData);
      if (groupsData.length > 0) {
        const savedGroupId = localStorage.getItem('kra_community_selected_group_id');
        const savedGroup = groupsData.find((group) => group.id === savedGroupId);
        setSelectedGroup(savedGroup || groupsData[0]);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (groupId) => {
    try {
      const response = await axios.get(`${API}/community/groups/${groupId}/messages`);
      const messagesData = response.data.messages || response.data || [];
      setMessages(messagesData);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const joinGroup = async (groupId) => {
    try {
      await axios.post(`${API}/community/groups/${groupId}/join`);
      // Update groups without changing the selected group
      const response = await axios.get(`${API}/community/groups`);
      const groupsData = response.data.groups || response.data || [];
      setGroups(groupsData);
    } catch (error) {
      console.error('Error joining group:', error);
    }
  };

  const viewMembers = async (group) => {
    try {
      setMembersLoading(true);
      // If not a member, join first
      if (!group.members?.includes(user.user_id)) {
        await joinGroup(group.id);
      }
      const response = await axios.get(`${API}/community/groups/${group.id}/members`);
      setMembers(response.data?.members || []);
      setMembersTitle(`${group.name} Members`);
      setMembersModalOpen(true);
    } catch (error) {
      if (error.response?.status === 403) {
        console.warn('Join the group to view members.');
      } else {
        console.warn(error.response?.data?.detail || 'Failed to load members');
      }
    } finally {
      setMembersLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedGroup) return;

    try {
      await axios.post(`${API}/community/groups/${selectedGroup.id}/messages`, {
        message: newMessage
      });
      setNewMessage('');
      fetchMessages(selectedGroup.id);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const getGroupIcon = (groupName = '') => (
    groupName.includes('AI') ? '🤖'
      : groupName.includes('Web') ? '🌐'
      : groupName.includes('Career') ? '💼'
      : groupName.includes('Robot') ? '🤖'
      : '👥'
  );

  const openGroupChat = async (group) => {
    setSelectedGroup(group);
    if (!group.members?.includes(user.user_id)) {
      await joinGroup(group.id);
    }
    if (isMobileViewport) {
      setMobileView('chat');
    }
  };

  if (!user) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-yellow-400 mb-4">🗣️ Community Access Required</h2>
          <p className="text-gray-300 mb-6">Please login to join our community discussions</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="flex flex-col md:flex-row md:h-screen">
        {/* Groups Sidebar */}
        <div className={`${isMobileViewport && mobileView === 'chat' ? 'hidden' : 'block'} w-full md:block md:w-1/3 md:max-w-sm bg-gray-900 border-b md:border-b-0 md:border-r border-yellow-400/30 p-4 md:sticky md:top-0 md:h-screen overflow-y-auto`}>
          <h2 className="text-xl font-bold text-yellow-400 mb-4">🗣️ Community Groups</h2>
          
          <div className="space-y-2">
            {groups && groups.length > 0 && groups.map((group) => (
              <div
                key={group.id}
                onClick={() => openGroupChat(group)}
                className={`relative p-4 rounded-lg cursor-pointer transition-all active:scale-[0.99] touch-manipulation min-h-[88px] ${
                  selectedGroup?.id === group.id
                    ? 'bg-yellow-400 text-black'
                    : 'bg-gray-800 hover:bg-gray-700'
                }`}
              >
                {/* Top-right members icon */}
                <button
                  aria-label="View members"
                  title="View members"
                  onClick={(e) => { e.stopPropagation(); viewMembers(group); }}
                  className={`absolute top-2 right-2 inline-flex items-center justify-center rounded-full border ${
                    selectedGroup?.id === group.id ? 'border-black/20 bg-black/10 text-black hover:bg-black/20' : 'border-yellow-400/40 bg-gray-900/40 text-white hover:bg-gray-900'
                  } px-2 py-2`}
                >
                  {/* Users icon (SVG) */}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M16 11c1.657 0 3-1.567 3-3.5S17.657 4 16 4s-3 1.567-3 3.5 1.343 3.5 3 3.5Zm-8 0c1.657 0 3-1.567 3-3.5S9.657 4 8 4 5 5.567 5 7.5 6.343 11 8 11Zm0 2c-2.761 0-5 1.79-5 4v1a1 1 0 0 0 1 1h8.05A5.978 5.978 0 0 1 12 17c0-1.517.566-2.9 1.5-4H8Zm8 0c-3.314 0-6 2.239-6 5v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-1c0-2.761-2.686-5-6-5Z" />
                  </svg>
                </button>

                <div className="flex items-center space-x-3 min-w-0 pr-10">
                  <div className="text-2xl">
                    {getGroupIcon(group.name)}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold break-words">{group.name}</h3>
                    <p className={`text-sm ${selectedGroup?.id === group.id ? 'text-gray-700' : 'text-gray-400'}`}>
                      {group.members?.length || 0} members
                    </p>
                  </div>
                </div>
                {!group.members?.includes(user.user_id) && (
                  <div className="mt-2 text-xs bg-green-600 text-white px-2 py-1 rounded">
                    Click to Join
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`${isMobileViewport && mobileView === 'groups' ? 'hidden' : 'flex'} flex-1 flex-col min-w-0 md:flex md:h-screen`}>
          {selectedGroup ? (
            <>
              {/* Chat Header */}
              <div className="bg-gray-900 border-b border-yellow-400/30 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {isMobileViewport && (
                      <button
                        onClick={() => setMobileView('groups')}
                        className="shrink-0 px-3 py-1.5 rounded-lg border border-yellow-400/30 text-yellow-300 hover:border-yellow-400"
                      >
                        ← Back
                      </button>
                    )}
                    <div className="text-3xl leading-none">{getGroupIcon(selectedGroup.name)}</div>
                    <div className="min-w-0">
                      <h3 className="text-xl font-bold text-yellow-400 break-words">{selectedGroup.name}</h3>
                      <p className="text-gray-400 break-words">{selectedGroup.description}</p>
                      <p className="text-xs text-gray-500 mt-1">{selectedGroup.members?.length || 0} members</p>
                    </div>
                  </div>
                  <button
                    aria-label="View members"
                    title="View members"
                    onClick={() => viewMembers(selectedGroup)}
                    className="shrink-0 inline-flex items-center justify-center rounded-full border border-yellow-400/40 bg-gray-800/80 text-white hover:bg-gray-700 px-2.5 py-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M16 11c1.657 0 3-1.567 3-3.5S17.657 4 16 4s-3 1.567-3 3.5 1.343 3.5 3 3.5Zm-8 0c1.657 0 3-1.567 3-3.5S9.657 4 8 4 5 5.567 5 7.5 6.343 11 8 11Zm0 2c-2.761 0-5 1.79-5 4v1a1 1 0 0 0 1 1h8.05A5.978 5.978 0 0 1 12 17c0-1.517.566-2.9 1.5-4H8Zm8 0c-3.314 0-6 2.239-6 5v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-1c0-2.761-2.686-5-6-5Z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[45vh] md:min-h-0 overflow-x-hidden">
                {messages && messages.length > 0 && messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.user_id === user.user_id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`w-fit max-w-[85vw] md:max-w-md px-4 py-2 rounded-lg break-words ${
                      message.user_id === user.user_id
                        ? 'bg-yellow-500 text-black'
                        : 'bg-gray-800 text-white'
                    }`}>
                      {message.user_id !== user.user_id && (
                        <div className="text-xs font-bold text-yellow-400 mb-1">
                          {message.username}
                        </div>
                      )}
                      <div>{message.message}</div>
                      <div className={`text-xs mt-1 ${
                        message.user_id === user.user_id ? 'text-gray-700' : 'text-gray-400'
                      }`}>
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Message Input */}
              <div className="bg-gray-900 border-t border-yellow-400/30 p-4">
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type your message..."
                    className="flex-1 min-w-0 bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                  />
                  <button
                    onClick={sendMessage}
                    className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 rounded-lg font-medium transition-colors"
                  >
                    Send
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl mb-4">💬</div>
                <h3 className="text-xl font-bold text-yellow-400 mb-2">Select a Group to Start Chatting</h3>
                <p className="text-gray-400">Join conversations with like-minded learners</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Members Modal */}
      {membersModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-yellow-400/40 rounded-lg p-6 w-full max-w-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-yellow-400">{membersTitle}</h3>
              <button onClick={() => setMembersModalOpen(false)} className="text-gray-300 hover:text-white">✕</button>
            </div>
            {membersLoading ? (
              <div className="text-gray-400">Loading...</div>
            ) : members.length === 0 ? (
              <div className="text-gray-400">No members yet.</div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {members.map(m => (
                  <div key={m.id} className="flex items-center gap-3 bg-gray-800 p-2 rounded">
                    {m.avatar && (
                      <img src={resolveImageUrl(m.avatar)} alt={m.name} className="w-8 h-8 rounded-full" />
                    )}
                    <div>
                      <div className="text-white font-medium">{m.name || 'Member'}</div>
                      <div className="text-xs text-gray-400">{m.email} • {m.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Mentorship Page Component
const MentorshipPage = () => {
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [requestData, setRequestData] = useState({
    studentName: '',
    studentEmail: '',
    preferredDate: '',
    preferredTime: '',
    topic: '',
    notes: '',
  });
  const { user } = useAuth();

  const normalizeText = (value, fallback = '') => {
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (Array.isArray(value)) return value.map((item) => normalizeText(item)).filter(Boolean).join(', ');
    if (value && typeof value === 'object') {
      return normalizeText(value.note ?? value.description ?? value.label ?? value.value ?? fallback, fallback);
    }
    return fallback;
  };

  const normalizeList = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => normalizeText(item)).filter(Boolean);
    }
    const text = normalizeText(value);
    return text ? [text] : [];
  };

  useEffect(() => {
    fetchMentors();
  }, []);

  useEffect(() => {
    if (user) {
      fetchMyRequests();
      setRequestData((prev) => ({
        ...prev,
        studentName: prev.studentName || `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.name || '',
        studentEmail: prev.studentEmail || user.email || '',
      }));
    }
  }, [user]);

  const fetchMentors = async () => {
    try {
      const response = await axios.get(`${API}/mentorship/mentors`);
      const mentorsData = response.data.mentors || response.data || [];
      setMentors(mentorsData);
    } catch (error) {
      console.error('Error fetching mentors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRequests = async () => {
    try {
      const response = await axios.get(`${API}/mentorship/sessions`);
      const sessions = response.data?.sessions || response.data || [];
      setMyRequests(Array.isArray(sessions) ? sessions : []);
    } catch (error) {
      console.error('Error fetching mentorship requests:', error);
    }
  };

  const submitRequest = async () => {
    if (!user) {
      setMessage('Please login to submit a mentorship request.');
      return;
    }

    if (!selectedMentor) {
      setMessage('Please choose a mentor first.');
      return;
    }

    if (!requestData.studentName || !requestData.studentEmail || !requestData.preferredDate || !requestData.preferredTime || !requestData.topic) {
      setMessage('Please complete all required fields.');
      return;
    }

    try {
      setSubmitting(true);
      setMessage('');
      const scheduledTime = new Date(`${requestData.preferredDate}T${requestData.preferredTime}`);
      await axios.post(`${API}/mentorship/sessions`, {
        mentor_id: selectedMentor.id || selectedMentor._id,
        title: requestData.topic,
        description: `Student Name: ${requestData.studentName}\nStudent Email: ${requestData.studentEmail}\nPreferred Date: ${requestData.preferredDate}\nPreferred Time: ${requestData.preferredTime}\nAdditional Notes: ${requestData.notes || 'N/A'}`,
        scheduled_time: scheduledTime.toISOString(),
        duration_minutes: 60,
      });

      setMessage('Mentorship request submitted successfully.');
      setSelectedMentor(null);
      setRequestData((prev) => ({ ...prev, preferredDate: '', preferredTime: '', topic: '', notes: '' }));
      await fetchMyRequests();
    } catch (error) {
      setMessage(parseApiError(error, 'Unable to submit mentorship request'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-yellow-400 mb-4">Find Your Mentor</h1>
          <p className="text-xl text-gray-300">Submit mentorship requests and track their approval status.</p>
        </div>

        {message && (
          <div className="mb-6 rounded-lg border border-yellow-400/30 bg-gray-900 p-4 text-sm text-gray-200">{message}</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {mentors && mentors.length > 0 && mentors.map((mentor) => (
            <div key={mentor.id || mentor._id} className="bg-gray-900 border border-yellow-400/30 rounded-lg overflow-hidden hover:border-yellow-400 transition-all">
              <div className="p-6">
                <div className="text-center mb-4">
                  <div className="w-20 h-20 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl">
                    👨‍💼
                  </div>
                  <h3 className="text-xl font-bold text-yellow-400">{mentor.name}</h3>
                </div>

                <div className="space-y-3 mb-6">
                  <div>
                    <h4 className="text-yellow-400 font-medium mb-2">Specialization:</h4>
                    <div className="flex flex-wrap gap-2">
                      {(normalizeList(mentor.expertise).length > 0 ? normalizeList(mentor.expertise) : [normalizeText(mentor.specialization, 'General Mentorship')]).map((skill, index) => (
                        <span key={index} className="bg-gray-800 text-yellow-400 px-2 py-1 rounded text-xs">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-yellow-400 font-medium mb-2">Bio:</h4>
                    <p className="text-gray-400 text-sm">{normalizeText(mentor.bio, 'No biography provided yet.')}</p>
                  </div>
                  <div>
                    <h4 className="text-yellow-400 font-medium mb-2">Availability:</h4>
                    <p className="text-gray-400 text-sm">{normalizeText(mentor.availability, 'Contact mentor for schedule details.')}</p>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedMentor(mentor)}
                  className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black py-3 px-4 rounded-lg font-bold transition-all transform hover:scale-105"
                >
                  Request Mentorship
                </button>
              </div>
            </div>
          ))}
        </div>

        {(!mentors || mentors.length === 0) && (
          <div className="text-center py-12">
            <h3 className="text-2xl font-bold text-yellow-400 mb-2">No Mentors Available</h3>
            <p className="text-gray-400">No mentors are currently available.</p>
          </div>
        )}

        {user && myRequests.length > 0 && (
          <div className="mt-10 bg-gray-900 border border-yellow-400/20 rounded-xl p-6">
            <h3 className="text-2xl font-bold text-yellow-400 mb-4">My Mentorship Requests</h3>
            <div className="space-y-3">
              {myRequests.map((request, index) => (
                <div key={request.id || index} className="border border-gray-700 rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <div className="font-semibold text-white">{request.title || 'Mentorship Request'}</div>
                    <div className="text-sm text-gray-400">Mentor: {request.mentor_name || request.mentor_id || 'Unassigned'}</div>
                  </div>
                  <div className="text-sm px-3 py-1 rounded-full bg-gray-800 border border-yellow-400/30 text-yellow-300 uppercase tracking-wide">
                    {String(request.status || 'pending')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Request Modal */}
      {selectedMentor && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-lg p-8 max-w-xl w-full mx-4 border border-yellow-400 max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-yellow-400 mb-2">Mentorship Request</h3>
              <p className="text-gray-400">Mentor: {selectedMentor.name}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-yellow-400 text-sm font-medium mb-2">Student Name *</label>
                <input
                  type="text"
                  value={requestData.studentName}
                  onChange={(e) => setRequestData({ ...requestData, studentName: e.target.value })}
                  className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-yellow-400 text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  value={requestData.studentEmail}
                  onChange={(e) => setRequestData({ ...requestData, studentEmail: e.target.value })}
                  className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                  placeholder="Enter your email"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-yellow-400 text-sm font-medium mb-2">Preferred Date *</label>
                  <input
                    type="date"
                    value={requestData.preferredDate}
                    onChange={(e) => setRequestData({ ...requestData, preferredDate: e.target.value })}
                    className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-yellow-400 text-sm font-medium mb-2">Preferred Time *</label>
                  <input
                    type="time"
                    value={requestData.preferredTime}
                    onChange={(e) => setRequestData({ ...requestData, preferredTime: e.target.value })}
                    className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-yellow-400 text-sm font-medium mb-2">Topic *</label>
                <input
                  type="text"
                  value={requestData.topic}
                  onChange={(e) => setRequestData({ ...requestData, topic: e.target.value })}
                  className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                  placeholder="What do you need help with?"
                />
              </div>

              <div>
                <label className="block text-yellow-400 text-sm font-medium mb-2">Additional Notes</label>
                <textarea
                  value={requestData.notes}
                  onChange={(e) => setRequestData({ ...requestData, notes: e.target.value })}
                  className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                  rows="4"
                  placeholder="Share any context for your request"
                />
              </div>
            </div>

            <div className="flex space-x-4 mt-8">
              <button
                onClick={() => setSelectedMentor(null)}
                className="flex-1 border border-gray-600 text-gray-300 py-3 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitRequest}
                disabled={submitting}
                className="flex-1 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black py-3 px-4 rounded-lg font-bold transition-all disabled:opacity-60"
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Code Editor Page Component
const CodeEditorPage = () => {
  const [code, setCode] = useState(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>K.R.A Code Lab</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      text-align: center;
      background: linear-gradient(to right, #FFD700, #FFA500);
      color: black;
      margin: 0;
      padding: 50px;
    }
    h1 {
      font-size: 3em;
      color: #000;
    }
    button {
      padding: 10px 20px;
      font-size: 1em;
      border: none;
      border-radius: 5px;
      background: black;
      color: #FFD700;
      cursor: pointer;
      transition: 0.3s;
    }
    button:hover {
      background: #333;
    }
  </style>
</head>
<body>
  <h1>Welcome to K.R.A Code Lab! 🚀</h1>
  <p>This is a fully functional code editor. Try editing this code and click "Run Code"!</p>
  <button onclick="sayHello()">Click Me!</button>

  <script>
    function sayHello() {
      console.log("Hello from K.R.A Code Lab! 🎉");
    }
  </script>
</body>
</html>`);
  const [language, setLanguage] = useState('html');
  const [output, setOutput] = useState('');
  const [theme, setTheme] = useState('dark');

  const runCode = () => {
    setOutput('Running...');
    
    setTimeout(() => {
      try {
        if (language === 'javascript') {
          // Real JavaScript execution environment
          let logs = [];
          const originalLog = console.log;
          const originalError = console.error;
          
          console.log = (...args) => {
            logs.push('> ' + args.join(' '));
          };
          console.error = (...args) => {
            logs.push('ERROR: ' + args.join(' '));
          };
          
          try {
            eval(code);
            setOutput(logs.join('\n') || 'JavaScript executed successfully!\n(No console output)');
          } catch (error) {
            setOutput(`JavaScript Runtime Error:\n${error.message}`);
          } finally {
            console.log = originalLog;
            console.error = originalError;
          }
          
        } else if (language === 'html') {
          // Live HTML preview
          setOutput('html-preview');
          
        } else if (language === 'css') {
          // CSS preview with sample HTML
          const cssPreview = `
            <!DOCTYPE html>
            <html>
            <head>
              <style>${code}</style>
            </head>
            <body>
              <h1>CSS Preview</h1>
              <p>This shows how your CSS styles look!</p>
              <div class="container">
                <button class="button">Sample Button</button>
              </div>
            </body>
            </html>
          `;
          setOutput('css-preview:' + cssPreview);
          
        } else if (language === 'python') {
          // Enhanced Python simulation
          let output = [];
          const lines = code.split('\n');
          
          try {
            lines.forEach((line, index) => {
              line = line.trim();
              
              // Handle print statements
              const printMatch = line.match(/print\((.+)\)/);
              if (printMatch) {
                let content = printMatch[1];
                
                // Handle f-strings
                const fStringMatch = content.match(/f["']([^"']*?)["']/);
                if (fStringMatch) {
                  let fString = fStringMatch[1];
                  // Simple variable substitution
                  fString = fString.replace(/\{(\w+)\}/g, (match, varName) => {
                    return `[${varName}]`; // Simplified - in real implementation would track variables
                  });
                  output.push(fString);
                } else if (content.includes('"') || content.includes("'")) {
                  // String literal
                  output.push(content.replace(/['"]/g, ''));
                } else {
                  // Variable or expression
                  output.push(`[${content}]`);
                }
              }
              
              // Handle variable assignments (simulate)
              if (line.includes('=') && !line.includes('==') && !line.includes('!=')) {
                const [varName] = line.split('=');
                output.push(`# Variable '${varName.trim()}' assigned`);
              }
            });
            
            if (output.length === 0) {
              output.push('Python code executed successfully!');
              output.push('(No print statements found)');
            }
            
            setOutput(output.join('\n'));
          } catch (error) {
            setOutput(`Python Error: ${error.message}`);
          }
          
        } else if (language === 'cpp') {
          // C++ compilation and execution simulation
          const output = [
            '=== C++ Compilation ===',
            'g++ -o program main.cpp',
            'Compilation successful!',
            '',
            '=== Program Output ===',
            'Hello, K.R.A!',
            'Welcome, K.R.A Student! Age: 20'
          ];
          
          // Extract cout statements for more realistic output
          const coutMatches = code.match(/cout\s*<<\s*"([^"]+)"/g);
          if (coutMatches) {
            const extracted = [];
            coutMatches.forEach(match => {
              const text = match.match(/"([^"]+)"/)[1];
              extracted.push(text);
            });
            output.splice(5, 2, ...extracted);
          }
          
          output.push('', 'Program finished with exit code 0');
          setOutput(output.join('\n'));
          
        } else if (language === 'rust') {
          // Rust compilation and execution simulation
          const output = [
            '=== Rust Compilation ===',
            'rustc main.rs',
            'Compiling main v0.1.0',
            'Finished dev [unoptimized + debuginfo] target(s)',
            '',
            '=== Program Output ==='
          ];
          
          // Extract println! statements
          const printlnMatches = code.match(/println!\s*\(\s*"([^"]+)"/g);
          if (printlnMatches) {
            printlnMatches.forEach(match => {
              const text = match.match(/"([^"]+)"/)[1];
              output.push(text);
            });
          } else {
            output.push('Hello, K.R.A!');
            output.push('Welcome, K.R.A Student! Age: 20');
          }
          
          output.push('', 'Process finished successfully');
          setOutput(output.join('\n'));
          
        } else if (language === 'kotlin') {
          // Kotlin compilation and execution simulation
          const output = [
            '=== Kotlin Compilation ===',
            'kotlinc main.kt -include-runtime -d program.jar',
            'Compilation successful!',
            '',
            '=== Program Output ==='
          ];
          
          // Extract println statements
          const printlnMatches = code.match(/println\s*\(\s*"([^"]+)"/g);
          if (printlnMatches) {
            printlnMatches.forEach(match => {
              const text = match.match(/"([^"]+)"/)[1];
              output.push(text);
            });
          } else {
            output.push('Hello, K.R.A!');
            output.push('Welcome, K.R.A Student! Age: 20');
          }
          
          output.push('', 'JVM process finished with exit code 0');
          setOutput(output.join('\n'));
          
        } else if (language === 'arduino') {
          const output = [
            '=== Arduino CLI ===',
            'arduino-cli compile --fqbn arduino:avr:uno sketch',
            'Compilation successful! Size: 2,364 bytes',
            'arduino-cli upload -p COM3 --fqbn arduino:avr:uno sketch',
            'Upload complete.',
            '',
            '=== Serial Monitor (simulation) ==='
          ];
          const delayMatches = [...code.matchAll(/delay\((\d+)\)/g)].map(m => parseInt(m[1], 10));
          const cycleDelay = delayMatches.length ? Math.min(...delayMatches) : 500;
          const cycles = 5;
          for (let i = 0; i < cycles; i++) {
            output.push(`[${i+1}] LED_BUILTIN: ON`);
            output.push(`... waiting ${cycleDelay} ms`);
            output.push(`[${i+1}] LED_BUILTIN: OFF`);
            output.push(`... waiting ${cycleDelay} ms`);
          }
          output.push('', 'Simulation finished.');
          setOutput(output.join('\n'));
        } else if (language === 'react-native') {
          const output = [
            'Starting Metro bundler on :8081 ...',
            'Transforming JavaScript...',
            'Bundle built in 1.8s',
            'Launching app in Expo Go (simulation)',
            '',
            '=== Console ==='
          ];
          const logMatches = [...code.matchAll(/console\.log\(([^\)]*)\)/g)];
          if (logMatches.length) {
            logMatches.forEach((m, idx) => {
              const text = m[1].replace(/[\'"`]/g, '').trim();
              output.push(`LOG ${idx+1}: ${text}`);
            });
          } else {
            output.push('LOG: Hello from React Native!');
          }
          output.push('', 'App running (simulation complete)');
          setOutput(output.join('\n'));
        } else if (language === 'raspberry-pi') {
          const lines = code.split('\n');
          const out = ['pi@raspberrypi:~ $ echo "Starting setup"', 'Starting setup'];
          lines.forEach((ln) => {
            const t = ln.trim();
            if (!t) return;
            if (t.startsWith('echo ')) {
              out.push(`pi@raspberrypi:~ $ ${t}`);
              out.push(t.replace(/^echo\s+/, '').replace(/[\'"`]/g, ''));
            } else if (/^sudo\s+apt(-get)?\s+/.test(t)) {
              out.push(`pi@raspberrypi:~ $ ${t}`);
              out.push('Reading package lists... Done');
              out.push('Building dependency tree... Done');
              out.push('Setting up packages... Done');
            } else if (/gpio|raspi-config|systemctl/.test(t)) {
              out.push(`pi@raspberrypi:~ $ ${t}`);
              out.push('Command executed (simulation)');
            } else {
              out.push(`pi@raspberrypi:~ $ ${t}`);
              out.push('Executed');
            }
          });
          out.push('pi@raspberrypi:~ $ # done');
          setOutput(out.join('\n'));
        } else {
          setOutput(`${language.toUpperCase()} code executed successfully!\nLanguage: ${language}\nLines of code: ${code.split('\n').length}`);
        }
      } catch (error) {
        setOutput(`Runtime Error: ${error.message}`);
      }
    }, 500); // Simulate processing time
  };

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="flex flex-col h-screen">
        {/* Header */}
        <div className="bg-gray-900 border-b border-yellow-400/30 p-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-yellow-400">💻 Code Lab</h1>
            <div className="flex items-center space-x-4">
              <select
                value={language}
                onChange={(e) => {
                  const newLang = e.target.value;
                  setLanguage(newLang);
                  setOutput('');
                  
                  // Provide starter code for each language
                  if (newLang === 'javascript') {
                    setCode('// Welcome to K.R.A Code Lab!\n// JavaScript execution environment\n\nconsole.log("Hello, K.R.A!");\n\n// Variables and functions\nconst greeting = "Welcome to JavaScript!";\nconsole.log(greeting);\n\n// Try arrays, objects, loops\nconst numbers = [1, 2, 3, 4, 5];\nnumbers.forEach(num => console.log(`Number: ${num}`));\n\n// Functions\nfunction calculate(a, b) {\n  return a + b;\n}\n\nconsole.log("5 + 3 =", calculate(5, 3));');
                  } else if (newLang === 'python') {
                    setCode('# Welcome to K.R.A Code Lab!\n# Python execution environment\n\nprint("Hello, K.R.A!")\n\n# Variables and data types\nname = "K.R.A Student"\nage = 20\nprint(f"Welcome, {name}! Age: {age}")\n\n# Lists and loops\nnumbers = [1, 2, 3, 4, 5]\nfor num in numbers:\n    print(f"Number: {num}")\n\n# Functions\ndef calculate(a, b):\n    return a + b\n\nresult = calculate(10, 5)\nprint(f"10 + 5 = {result}")');
                  } else if (newLang === 'css') {
                    setCode('/* Welcome to K.R.A Code Lab! */\n/* CSS styling environment */\n\nbody {\n  font-family: Arial, sans-serif;\n  background: linear-gradient(45deg, #FFD700, #000);\n  color: white;\n  text-align: center;\n  padding: 50px;\n  margin: 0;\n}\n\nh1 {\n  font-size: 3em;\n  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);\n  margin-bottom: 20px;\n}\n\n.container {\n  max-width: 800px;\n  margin: 0 auto;\n  background: rgba(255,255,255,0.1);\n  padding: 30px;\n  border-radius: 15px;\n  backdrop-filter: blur(10px);\n}\n\n.button {\n  background: #FFD700;\n  color: #000;\n  padding: 12px 24px;\n  border: none;\n  border-radius: 8px;\n  font-weight: bold;\n  cursor: pointer;\n  transition: all 0.3s ease;\n}\n\n.button:hover {\n  transform: scale(1.1);\n  box-shadow: 0 4px 15px rgba(255,215,0,0.4);\n}');
                  } else if (newLang === 'cpp') {
                    setCode('#include <iostream>\n#include <vector>\n#include <string>\n\nusing namespace std;\n\nint main() {\n    // Welcome to K.R.A Code Lab!\n    cout << "Hello, K.R.A!" << endl;\n    \n    // Variables and types\n    string name = "K.R.A Student";\n    int age = 20;\n    cout << "Welcome, " << name << "! Age: " << age << endl;\n    \n    // Vectors and loops\n    vector<int> numbers = {1, 2, 3, 4, 5};\n    for (int num : numbers) {\n        cout << "Number: " << num << endl;\n    }\n    \n    // Functions\n    auto calculate = [](int a, int b) -> int {\n        return a + b;\n    };\n    \n    int result = calculate(10, 5);\n    cout << "10 + 5 = " << result << endl;\n    \n    return 0;\n}');
                  } else if (newLang === 'rust') {
                    setCode('// Welcome to K.R.A Code Lab!\n// Rust execution environment\n\nfn main() {\n    println!("Hello, K.R.A!");\n    \n    // Variables and types\n    let name = "K.R.A Student";\n    let age = 20;\n    println!("Welcome, {}! Age: {}", name, age);\n    \n    // Vectors and loops\n    let numbers = vec![1, 2, 3, 4, 5];\n    for num in &numbers {\n        println!("Number: {}", num);\n    }\n    \n    // Functions\n    fn calculate(a: i32, b: i32) -> i32 {\n        a + b\n    }\n    \n    let result = calculate(10, 5);\n    println!("10 + 5 = {}", result);\n    \n    // Pattern matching\n    match result {\n        15 => println!("Perfect calculation!"),\n        _ => println!("Different result"),\n    }\n}');
                  } else if (newLang === 'kotlin') {
                    setCode('// Welcome to K.R.A Code Lab!\n// Kotlin execution environment\n\nfun main() {\n    println("Hello, K.R.A!")\n    \n    // Variables and types\n    val name = "K.R.A Student"\n    val age = 20\n    println("Welcome, $name! Age: $age")\n    \n    // Lists and loops\n    val numbers = listOf(1, 2, 3, 4, 5)\n    numbers.forEach { num ->\n        println("Number: $num")\n    }\n    \n    // Functions\n    fun calculate(a: Int, b: Int): Int {\n        return a + b\n    }\n    \n    val result = calculate(10, 5)\n    println("10 + 5 = $result")\n    \n    // Data classes\n    data class Person(val name: String, val age: Int)\n    val person = Person("Developer", 25)\n    println("Person: ${person.name}, Age: ${person.age}")\n}');
                  } else if (newLang === 'arduino') {
                    setCode('// Arduino Blink example\n// Runs on Arduino Uno (simulation)\n\nint led = 13;\n\nvoid setup() {\n  pinMode(led, OUTPUT);\n}\n\nvoid loop() {\n  digitalWrite(led, HIGH);   // turn the LED on\n  delay(500);                // wait for half a second\n  digitalWrite(led, LOW);    // turn the LED off\n  delay(500);                // wait for half a second\n}\n');
                  } else if (newLang === 'react-native') {
                    setCode('// React Native (Expo) simulation\nimport React from "react";\nimport { Text, View } from "react-native";\n\nexport default function App() {\n  console.log("Hello from React Native!");\n  return (\n    <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>\n      <Text>Welcome to K.R.A React Native!</Text>\n    </View>\n  );\n}\n');
                  } else if (newLang === 'raspberry-pi') {
                    setCode('# Raspberry Pi setup (simulation)\n\nsudo apt-get update\nsudo apt-get install -y python3-pip\necho "GPIO ready"\n# raspi-config nonint do_i2c 0\n');
                  } else if (newLang === 'html') {
                    setCode(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>K.R.A Code Lab</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      text-align: center;
      background: linear-gradient(to right, #FFD700, #FFA500);
      color: black;
      margin: 0;
      padding: 50px;
    }
    h1 {
      font-size: 3em;
      color: #000;
    }
    button {
      padding: 10px 20px;
      font-size: 1em;
      border: none;
      border-radius: 5px;
      background: black;
      color: #FFD700;
      cursor: pointer;
      transition: 0.3s;
    }
    button:hover {
      background: #333;
    }
  </style>
</head>
<body>
  <h1>Welcome to K.R.A Code Lab! 🚀</h1>
  <p>This is a fully functional HTML environment!</p>
  <button onclick="sayHello()">Click Me!</button>

  <script>
    function sayHello() {
      console.log("Hello from K.R.A Code Lab! 🎉");
    }
  </script>
</body>
</html>`);
                  }
                }}
                className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600 focus:border-yellow-400"
              >
                <option value="html">HTML</option>
                <option value="javascript">JavaScript</option>
                <option value="python">Python</option>
                <option value="css">CSS</option>
                <option value="cpp">C++</option>
                <option value="rust">Rust</option>
                <option value="kotlin">Kotlin</option>
                <option value="arduino">Arduino</option>
                <option value="react-native">React Native</option>
                <option value="raspberry-pi">Raspberry Pi</option>
              </select>
              <button
                onClick={runCode}
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black px-6 py-2 rounded-lg font-bold transition-all"
              >
                Run Code
              </button>
            </div>
          </div>
          {/* Connect Tools section removed per request */}
        </div>

        {/* Code Editor Area */}
        <div className="flex flex-1">
          {/* Editor */}
          <div className="flex-1 flex flex-col">
            <div className="bg-gray-800 px-4 py-2 border-b border-gray-700 flex items-center justify-between">
              <span className="text-yellow-400">
                main.{
                  language === 'javascript' ? 'js' : 
                  language === 'cpp' ? 'cpp' :
                  language === 'rust' ? 'rs' :
                  language === 'kotlin' ? 'kt' :
                  language === 'arduino' ? 'ino' :
                  language === 'react-native' ? 'jsx' :
                  language === 'raspberry-pi' ? 'sh' :
                  language === 'python' ? 'py' :
                  language
                }
              </span>
              <span className="text-xs text-gray-400">
                {
                  language === 'html' ? '🌐 Web Preview' :
                  language === 'css' ? '🎨 Style Preview' :
                  language === 'javascript' ? '⚡ Live Execution' :
                  language === 'python' ? '🐍 Interpreted' :
                  language === 'cpp' ? '⚙️ Compiled' :
                  language === 'rust' ? '🦀 Systems Programming' :
                  language === 'kotlin' ? '📱 JVM/Android' : '💻 Code'
                }
              </span>
            </div>
            <textarea
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="flex-1 bg-gray-900 text-white p-4 font-mono text-sm resize-none focus:outline-none border-none"
              style={{
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", consolas, "source-code-pro", monospace',
                fontSize: '14px',
                lineHeight: '1.5'
              }}
              placeholder="Write your code here..."
            />
          </div>

          {/* Output */}
          <div className="w-1/3 border-l border-gray-700 flex flex-col">
            <div className="bg-gray-800 px-4 py-2 border-b border-gray-700">
              <span className="text-yellow-400">Output</span>
            </div>
            <div className="flex-1 bg-black overflow-hidden">
              {output === 'html-preview' ? (
                <iframe
                  srcDoc={code}
                  className="w-full h-full border-none bg-white"
                  title="HTML Preview"
                  sandbox="allow-scripts"
                />
              ) : output && output.startsWith('css-preview:') ? (
                <iframe
                  srcDoc={output.replace('css-preview:', '')}
                  className="w-full h-full border-none bg-white"
                  title="CSS Preview"
                  sandbox="allow-scripts"
                />
              ) : (
                <div className="p-4 overflow-y-auto h-full">
                  <pre className="text-green-400 font-mono text-sm whitespace-pre-wrap">
                    {output || 'Click "Run Code" to see output...'}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Panel */}
        <div className="bg-gray-900 border-t border-gray-700 p-4">
          <div className="text-sm text-gray-400">
            <span className="text-yellow-400">💡 {language.toUpperCase()} Tip:</span> {
              language === 'html' ? 
                'HTML code runs in live preview! Edit content, styles, and JavaScript interactively.' :
              language === 'javascript' ? 
                'JavaScript executes in real-time. Use console.log() to see output results.' :
              language === 'python' ?
                'Python interpreter simulation. Use print() statements for output display.' :
              language === 'css' ?
                'CSS styles preview with sample HTML. Create responsive designs and animations.' :
              language === 'cpp' ?
                'C++ compiler simulation. Supports STL, classes, and modern C++ features.' :
              language === 'rust' ?
                'Rust compiler simulation. Memory-safe systems programming with ownership model.' :
              language === 'kotlin' ?
                'Kotlin compiler for JVM. Interoperable with Java, great for Android development.' :
              language === 'arduino' ?
                'Arduino sketch simulation (compile, upload, serial logs).' :
              language === 'react-native' ?
                'React Native (Expo) console simulation for quick feedback.' :
              language === 'raspberry-pi' ?
                'Raspberry Pi shell simulation for common setup commands.' :
                'Multi-language code execution environment with syntax highlighting.'
            }
          </div>
        </div>
      </div>
    </div>
  );
};

// Jobs Page Component
const JobsPage = ({ onPageChange }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicationJob, setApplicationJob] = useState(null);
  const [applicationStatus, setApplicationStatus] = useState('');
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [applicationData, setApplicationData] = useState({
    applicant_name: '',
    email: '',
    phone: '',
    location: '',
    portfolio: '',
    linked_in_url: '',
    website_url: '',
    social_media_accounts_managed: '',
    availability: '',
    cover_letter: '',
  });
  const [resumeFile, setResumeFile] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (user) {
      setApplicationData((current) => ({
        ...current,
        applicant_name: current.applicant_name || user.name || '',
        email: current.email || user.email || '',
        phone: current.phone || user.phone_number || '',
        location: current.location || user.location || '',
      }));
    }
  }, [user]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      const jobsData = response.data.jobs || response.data || [];
      setJobs(jobsData);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const isOpenJob = (applicationJob?.status || 'open') === 'open';

  const applyToJob = async () => {
    if (!applicationJob) {
      setApplicationStatus('Select a job first');
      return;
    }

    if (!isOpenJob) {
      setApplicationStatus('This position is closed');
      return;
    }

    if (!applicationData.cover_letter) {
      setApplicationStatus('Please write a cover letter');
      return;
    }

    if (!resumeFile) {
      setApplicationStatus('Please upload your resume');
      return;
    }

    try {
      setSubmittingApplication(true);
      setApplicationSubmitted(false);
      const formData = new FormData();
      formData.append('applicant_name', applicationData.applicant_name || '');
      formData.append('email', applicationData.email || '');
      formData.append('phone', applicationData.phone || '');
      formData.append('location', applicationData.location || '');
      formData.append('portfolio', applicationData.portfolio || '');
      formData.append('linked_in_url', applicationData.linked_in_url || '');
      formData.append('website_url', applicationData.website_url || '');
      formData.append('social_media_accounts_managed', applicationData.social_media_accounts_managed || '');
      formData.append('availability', applicationData.availability || '');
      formData.append('cover_letter', applicationData.cover_letter || '');
      formData.append('resume_file', resumeFile);

      const response = await axios.post(`${API}/jobs/${applicationJob.id}/apply`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setApplicationStatus(response.data?.message || 'Your application has been received. The K.R.A team will review your application and get back to you soon.');
      setApplicationSubmitted(true);
      setApplicationData({
        applicant_name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone_number || '',
        location: user?.location || '',
        portfolio: '',
        linked_in_url: '',
        website_url: '',
        social_media_accounts_managed: '',
        availability: '',
        cover_letter: '',
      });
      setResumeFile(null);
      setApplicationJob(null);
    } catch (error) {
      setApplicationSubmitted(false);
      setApplicationStatus(parseApiError(error, 'Submission failed. Please check all fields and try again.'));
    } finally {
      setSubmittingApplication(false);
    }
  };

  const openJobDetails = (job) => {
    setSelectedJob(job);
  };

  const openApplicationModal = () => {
    if (!selectedJob) return;
    setApplicationStatus('');
    setApplicationSubmitted(false);
    setApplicationJob(selectedJob);
  };

  const closeApplicationModal = () => {
    setApplicationJob(null);
    setApplicationStatus('');
    setApplicationSubmitted(false);
  };

  if (loading) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-yellow-400 mb-4">Jobs</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">Explore open opportunities at K.R.A Initiative LTD and submit a professional application directly through the platform.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {jobs && jobs.length > 0 && jobs.map((job) => {
            const jobOpen = (job.status || 'open') === 'open';
            return (
            <div key={job.id} className="bg-gray-900 border border-yellow-400/30 rounded-xl overflow-hidden hover:border-yellow-400 transition-all">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-yellow-400 mb-1">{job.title}</h3>
                    <p className="text-gray-200 font-medium">{job.company}</p>
                    <p className="text-gray-400 text-sm">{job.location}</p>
                    <p className="text-gray-500 text-xs mt-1">{job.employment_type} • {job.remote_hybrid_status || 'Remote / Hybrid'}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${jobOpen ? 'bg-green-800 text-green-200' : 'bg-red-800 text-red-200'}`}>
                      {jobOpen ? 'Open' : 'Closed'}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-800 text-gray-200">
                      {job.employment_type}
                    </span>
                  </div>
                </div>

                <p className="text-gray-300 mb-4 line-clamp-3">{job.description}</p>

                <div className="text-sm mb-4">
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="text-gray-400">Posted</div>
                    <div className="text-gray-200">{new Date(job.posted_date || job.created_at || Date.now()).toLocaleDateString()}</div>
                  </div>
                </div>

                <button
                  onClick={() => openJobDetails(job)}
                  className="w-full py-3 px-4 rounded-lg font-bold transition-all bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black"
                >
                  View Job Details
                </button>
              </div>
            </div>
            );
          })}
        </div>

        {(!jobs || jobs.length === 0) && (
          <div className="text-center py-12 mt-6 bg-gray-900 border border-yellow-400/20 rounded-xl">
            <h3 className="text-2xl font-bold text-yellow-400 mb-2">No Jobs Available</h3>
            <p className="text-gray-400">New opportunities will appear here when the admin posts them.</p>
          </div>
        )}

        {selectedJob && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-0 md:p-6">
            <div className="w-full h-full md:h-auto md:max-h-[90vh] md:max-w-4xl bg-gray-900 border border-yellow-400/30 md:rounded-2xl overflow-hidden flex flex-col">
              <div className="flex items-start justify-between p-5 border-b border-gray-700">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-yellow-400">{selectedJob.title}</h2>
                  <p className="text-gray-200 mt-1">{selectedJob.company}</p>
                  <p className="text-gray-400 text-sm">{selectedJob.location}</p>
                  <p className="text-gray-500 text-xs mt-1">{selectedJob.employment_type} • {selectedJob.remote_hybrid_status || 'Remote / Hybrid'}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${(selectedJob.status || 'open') === 'open' ? 'bg-green-800 text-green-200' : 'bg-red-800 text-red-200'}`}>
                    {(selectedJob.status || 'open') === 'open' ? 'OPEN' : 'CLOSED'}
                  </span>
                  <button onClick={() => setSelectedJob(null)} className="text-gray-300 hover:text-white text-2xl leading-none" aria-label="Close job details">×</button>
                </div>
              </div>

              <div className="overflow-y-auto p-5 md:p-6 space-y-5">
                <section className="bg-gray-800 rounded-xl p-4">
                  <h3 className="text-yellow-400 font-semibold mb-2">Job Description</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{selectedJob.description}</p>
                </section>

                <section className="bg-gray-800 rounded-xl p-4">
                  <h3 className="text-yellow-400 font-semibold mb-2">Role Description</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{selectedJob.role_description || selectedJob.description}</p>
                </section>

                {[
                  ['Responsibilities', selectedJob.responsibilities],
                  ['Qualifications', selectedJob.qualifications],
                  ['Preferred Qualifications', selectedJob.preferred_qualifications],
                  ['KPIs', selectedJob.kpis],
                  ['Why Join Us', selectedJob.benefits],
                ].map(([label, items]) => (
                  <section key={label} className="bg-gray-800 rounded-xl p-4">
                    <h3 className="text-yellow-400 font-semibold mb-2">{label}</h3>
                    <ul className="space-y-2 text-sm text-gray-300">
                      {((items && items.length ? items : [`${label} will be shared during interview and onboarding.`])).map((item, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-yellow-400 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}

                <section className="bg-gray-800 rounded-xl p-4">
                  <h3 className="text-yellow-400 font-semibold mb-2">Compensation</h3>
                  <p className="text-gray-300 text-sm">{selectedJob.compensation || 'Compensation details are shared based on experience and role scope.'}</p>
                </section>
              </div>

              <div className="border-t border-gray-700 p-4 md:p-5 bg-gray-900">
                {(selectedJob.status || 'open') === 'open' ? (
                  <button onClick={openApplicationModal} className="w-full md:w-auto bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black py-3 px-8 rounded-lg font-bold">
                    Apply Now
                  </button>
                ) : (
                  <button disabled className="w-full md:w-auto bg-gray-700 text-gray-300 py-3 px-8 rounded-lg font-bold cursor-not-allowed">
                    Position Closed
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {applicationJob && (
          <div className="fixed inset-0 z-[60] bg-black/75 flex items-center justify-center p-0 md:p-6">
            <div className="w-full h-full md:h-auto md:max-h-[90vh] md:max-w-3xl bg-gray-900 border border-yellow-400/30 md:rounded-2xl overflow-hidden flex flex-col">
              <div className="flex items-start justify-between p-5 border-b border-gray-700">
                <div>
                  <h3 className="text-2xl font-bold text-yellow-400">Apply for {applicationJob.title}</h3>
                  <p className="text-gray-400 text-sm mt-1">{applicationJob.company} • {applicationJob.location}</p>
                </div>
                <button onClick={closeApplicationModal} className="text-gray-300 hover:text-white text-2xl leading-none" aria-label="Close application form">×</button>
              </div>

              <div className="overflow-y-auto p-5 md:p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input value={applicationData.applicant_name} onChange={(e) => setApplicationData({ ...applicationData, applicant_name: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Full Name" />
                  <input value={applicationData.email} onChange={(e) => setApplicationData({ ...applicationData, email: e.target.value })} type="email" className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Email" />
                  <input value={applicationData.phone} onChange={(e) => setApplicationData({ ...applicationData, phone: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Phone Number" />
                  <input value={applicationData.location} onChange={(e) => setApplicationData({ ...applicationData, location: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Location" />
                  <input value={applicationData.portfolio} onChange={(e) => setApplicationData({ ...applicationData, portfolio: e.target.value })} type="url" className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 sm:col-span-2" placeholder="Portfolio URL" />
                  <input value={applicationData.linked_in_url} onChange={(e) => setApplicationData({ ...applicationData, linked_in_url: e.target.value })} type="url" className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="LinkedIn URL" />
                  <input value={applicationData.website_url} onChange={(e) => setApplicationData({ ...applicationData, website_url: e.target.value })} type="url" className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Website URL" />
                </div>
                <input value={applicationData.social_media_accounts_managed} onChange={(e) => setApplicationData({ ...applicationData, social_media_accounts_managed: e.target.value })} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Social media accounts managed, separated by commas" />
                <input value={applicationData.availability} onChange={(e) => setApplicationData({ ...applicationData, availability: e.target.value })} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Availability" />
                <textarea value={applicationData.cover_letter} onChange={(e) => setApplicationData({ ...applicationData, cover_letter: e.target.value })} rows={6} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" placeholder="Cover Letter / Introduction and why you want to join K.R.A Initiative LTD" />
                <div>
                  <label className="block text-yellow-400 text-sm font-medium mb-2">Resume Upload *</label>
                  <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setResumeFile(e.target.files?.[0] || null)} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600" />
                </div>
                {applicationStatus && <div className="text-sm text-gray-300 bg-gray-800 rounded-lg p-3">{applicationStatus}</div>}
                <button onClick={applyToJob} disabled={!isOpenJob || submittingApplication} className={`w-full py-3 px-4 rounded-lg font-bold transition-all ${isOpenJob && !submittingApplication ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black' : 'bg-gray-700 text-gray-300 cursor-not-allowed'}`}>
                  {submittingApplication ? 'Submitting...' : 'Submit Application'}
                </button>
                {applicationSubmitted && !user && (
                  <div className="text-xs text-gray-400">
                    You can create your account now to track future opportunities.
                    {onPageChange && (
                      <button
                        onClick={() => onPageChange('auth')}
                        className="ml-2 text-yellow-400 hover:text-yellow-300 underline"
                      >
                        Create Account
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Hackathons Page Component
const HackathonsPage = () => {
  const [hackathons, setHackathons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationForm, setApplicationForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    age: '',
    city: '',
    country: '',
    project_name: '',
    robot_concept: '',
    project_description: '',
    technologies_used: '',
    electronics_concept: '',
    software_ai_concept: '',
    cad_design_info: '',
    video_url: '',
  });
  const [submissionFiles, setSubmissionFiles] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const { user } = useAuth();
  const userRole = (user?.role || '').toLowerCase();
  const featuredHackathon = hackathons.find((entry) => entry.slug === 'robotics-innovation-hackathon') || hackathons[0] || null;
  const isAdmin = userRole === 'admin';

  useEffect(() => {
    fetchHackathons();
  }, []);

  const fetchHackathons = async () => {
    try {
      const response = await axios.get(`${API}/hackathons`);
      const hackathonsData = response.data.hackathons || response.data || [];
      setHackathons(hackathonsData);
    } catch (error) {
      setStatusMessage(parseApiError(error, 'Unable to load hackathon details right now.'));
    } finally {
      setLoading(false);
    }
  };

  const resetApplicationForm = () => {
    setApplicationForm({
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      age: '',
      city: '',
      country: '',
      project_name: '',
      robot_concept: '',
      project_description: '',
      technologies_used: '',
      electronics_concept: '',
      software_ai_concept: '',
      cad_design_info: '',
      video_url: '',
    });
    setSubmissionFiles([]);
    setVideoFile(null);
  };

  const submitApplication = async () => {
    if (!featuredHackathon?.id) {
      setStatusMessage('Hackathon details are not available right now.');
      return;
    }
    if (!applicationForm.first_name || !applicationForm.last_name || !applicationForm.email || !applicationForm.phone_number || !applicationForm.project_name || !applicationForm.robot_concept || !applicationForm.project_description) {
      setStatusMessage('Complete the required applicant and project fields before submitting.');
      return;
    }
    if (submissionFiles.length === 0 && !videoFile && !applicationForm.video_url.trim()) {
      setStatusMessage('Upload project materials or provide a demo video link before submitting.');
      return;
    }

    try {
      setIsApplying(true);
      const formData = new FormData();
      Object.entries(applicationForm).forEach(([key, value]) => {
        formData.append(key, value || '');
      });
      submissionFiles.forEach((file) => {
        formData.append('submission_files', file);
      });
      if (videoFile) {
        formData.append('video_file', videoFile);
      }
      const response = await axios.post(`${API}/hackathons/${featuredHackathon.id}/applications`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStatusMessage(response.data?.message || 'Application submitted successfully.');
      resetApplicationForm();
      setShowApplicationForm(false);
    } catch (error) {
      setStatusMessage(parseApiError(error, 'Unable to submit your application right now.'));
    } finally {
      setIsApplying(false);
    }
  };

  const formatDateTime = (value, options = {}) => {
    if (!value) return 'N/A';
    try {
      return new Date(value).toLocaleString([], options);
    } catch (error) {
      return value;
    }
  };

  const applicationClosed = !featuredHackathon || featuredHackathon.application_status === 'closed' || featuredHackathon.status === 'closed' || featuredHackathon.status === 'completed';
  const prizeList = featuredHackathon?.prizes?.length
    ? featuredHackathon.prizes
    : [
        featuredHackathon?.first_prize ? { label: 'Prize 1', value: featuredHackathon.first_prize } : null,
        featuredHackathon?.second_prize ? { label: 'Prize 2', value: featuredHackathon.second_prize } : null,
        featuredHackathon?.third_prize ? { label: 'Prize 3', value: featuredHackathon.third_prize } : null,
      ].filter(Boolean);

  if (loading) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <section className="relative overflow-hidden rounded-[32px] border border-yellow-400/20 bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.18),_transparent_35%),linear-gradient(135deg,_rgba(15,23,42,0.98),_rgba(3,7,18,0.98))] p-6 sm:p-10 lg:p-14">
          <div className="absolute inset-y-0 right-0 w-1/3 bg-[radial-gradient(circle_at_center,_rgba(250,204,21,0.10),_transparent_60%)] pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-yellow-400/25 bg-yellow-400/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.28em] text-yellow-300">
                K.R.A Initiative Hackathon
              </div>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-tight text-white">
                {featuredHackathon?.title || 'Robotics Innovation Hackathon'}
              </h1>
              <p className="mt-4 max-w-3xl text-lg sm:text-xl text-gray-300 leading-8">
                {featuredHackathon?.tagline || featuredHackathon?.description || 'Build a robotics solution that tackles a real-world problem and present it to the K.R.A Initiative team.'}
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-2xl border border-yellow-400/15 bg-black/35 p-4">
                  <div className="text-xs uppercase tracking-[0.22em] text-yellow-300">Apply By</div>
                  <div className="mt-2 text-xl font-bold text-white">{formatDateTime(featuredHackathon?.application_deadline, { month: 'long', day: 'numeric', year: 'numeric' })}</div>
                </div>
                <div className="rounded-2xl border border-yellow-400/15 bg-black/35 p-4">
                  <div className="text-xs uppercase tracking-[0.22em] text-yellow-300">Format</div>
                  <div className="mt-2 text-xl font-bold text-white">{featuredHackathon?.format || featuredHackathon?.location || 'Online'}</div>
                </div>
                <div className="rounded-2xl border border-yellow-400/15 bg-black/35 p-4">
                  <div className="text-xs uppercase tracking-[0.22em] text-yellow-300">Eligibility</div>
                  <div className="mt-2 text-xl font-bold text-white">{featuredHackathon?.age_requirement || 'Children & youth ages 8-18'}</div>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-4 items-center">
                <button
                  type="button"
                  onClick={() => setShowApplicationForm(true)}
                  disabled={!featuredHackathon || applicationClosed}
                  className="rounded-full bg-yellow-400 px-6 py-3 text-sm font-black uppercase tracking-[0.2em] text-black transition hover:bg-yellow-300 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {applicationClosed ? 'Applications Closed' : 'Apply Now'}
                </button>
                <div className="text-sm text-gray-400">
                  {isAdmin ? 'Manage this event and review applications from the Admin Portal hackathons tab.' : 'Submit your robotics concept, supporting files, and demo video link in one application.'}
                </div>
              </div>
              {statusMessage && <div className="mt-6 rounded-2xl border border-yellow-400/20 bg-black/40 px-4 py-3 text-sm text-gray-200">{statusMessage}</div>}
            </div>

            <div className="relative">
              <div className="absolute -inset-4 rounded-[28px] bg-yellow-400/10 blur-2xl" />
              <div className="relative rounded-[28px] border border-yellow-400/25 bg-gray-950/90 p-4 sm:p-5">
                {featuredHackathon?.image_url ? (
                  <img
                    src={resolveImageUrl(featuredHackathon.image_url)}
                    alt={featuredHackathon.title}
                    width={1080}
                    height={1350}
                    className="block h-auto w-full rounded-[22px] object-contain"
                  />
                ) : (
                  <div className="h-[340px] rounded-[22px] bg-[linear-gradient(135deg,_rgba(250,204,21,0.22),_rgba(8,47,73,0.45))] p-6 flex flex-col justify-between">
                    <div className="text-xs uppercase tracking-[0.3em] text-yellow-100">Robotics • Innovation • Prototype</div>
                    <div>
                      <div className="text-3xl font-black text-white">Build a robot for a real problem.</div>
                      <div className="mt-4 text-gray-200 leading-7">Show your concept, your engineering process, and your impact story.</div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-center text-xs uppercase tracking-[0.2em] text-yellow-100">
                      <div className="rounded-2xl border border-white/10 bg-black/25 px-3 py-4">Concept</div>
                      <div className="rounded-2xl border border-white/10 bg-black/25 px-3 py-4">Build</div>
                      <div className="rounded-2xl border border-white/10 bg-black/25 px-3 py-4">Pitch</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {featuredHackathon && (
          <section className="mt-10 grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-6">
            <div className="space-y-6">
              <div className="rounded-[28px] border border-yellow-400/15 bg-gray-950/90 p-6 sm:p-8">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <h2 className="text-2xl font-bold text-yellow-300">Challenge Brief</h2>
                  <span className="rounded-full border border-yellow-400/20 bg-yellow-400/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-yellow-200">
                    {featuredHackathon.status === 'registration_open' ? 'Applications Open' : featuredHackathon.status}
                  </span>
                </div>
                <p className="mt-4 text-gray-300 leading-8">{featuredHackathon.description}</p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-300">
                  <div className="rounded-2xl border border-gray-800 bg-black/40 p-4">
                    <div className="text-yellow-300 text-xs uppercase tracking-[0.2em]">Event Date</div>
                    <div className="mt-2 text-white text-lg font-semibold">{formatDateTime(featuredHackathon.event_date, { month: 'long', day: 'numeric', year: 'numeric' })}</div>
                  </div>
                  <div className="rounded-2xl border border-gray-800 bg-black/40 p-4">
                    <div className="text-yellow-300 text-xs uppercase tracking-[0.2em]">Location</div>
                    <div className="mt-2 text-white text-lg font-semibold">{featuredHackathon.location || 'Toronto, Canada'}</div>
                  </div>
                </div>
              </div>

              <div className="rounded-[28px] border border-yellow-400/15 bg-gray-950/90 p-6 sm:p-8">
                <h2 className="text-2xl font-bold text-yellow-300">How To Apply</h2>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(featuredHackathon.application_instructions || []).length > 0 ? (featuredHackathon.application_instructions || []).map((instruction, index) => (
                    <div key={`${instruction}-${index}`} className="rounded-2xl border border-gray-800 bg-black/40 p-5">
                      <div className="text-xs font-semibold uppercase tracking-[0.24em] text-yellow-200">Step {index + 1}</div>
                      <div className="mt-3 text-gray-200 leading-7">{instruction}</div>
                    </div>
                  )) : (
                    [
                      'Identify a real-world problem your robot can solve.',
                      'Prepare a short concept summary, diagrams, files, or prototype evidence.',
                      'Submit your application with supporting materials before the deadline.',
                    ].map((instruction, index) => (
                      <div key={`${instruction}-${index}`} className="rounded-2xl border border-gray-800 bg-black/40 p-5">
                        <div className="text-xs font-semibold uppercase tracking-[0.24em] text-yellow-200">Step {index + 1}</div>
                        <div className="mt-3 text-gray-200 leading-7">{instruction}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-[28px] border border-yellow-400/15 bg-gray-950/90 p-6 sm:p-8">
                <h2 className="text-2xl font-bold text-yellow-300">Prize Highlights</h2>
                <div className="mt-5 space-y-3">
                  {prizeList.length > 0 ? prizeList.map((prize, index) => (
                    <div key={`${prize.label}-${index}`} className="rounded-2xl border border-gray-800 bg-black/40 px-4 py-4">
                      <div className="text-xs uppercase tracking-[0.22em] text-yellow-200">{prize.label}</div>
                      <div className="mt-2 text-white text-lg font-semibold">{prize.value}</div>
                    </div>
                  )) : (
                    <div className="rounded-2xl border border-gray-800 bg-black/40 px-4 py-4 text-gray-300">Prizes will be announced soon.</div>
                  )}
                </div>
              </div>

              <div className="rounded-[28px] border border-yellow-400/15 bg-gray-950/90 p-6 sm:p-8">
                <h2 className="text-2xl font-bold text-yellow-300">What To Submit</h2>
                <ul className="mt-5 space-y-3 text-gray-300 leading-7">
                  <li className="rounded-2xl border border-gray-800 bg-black/40 px-4 py-4">Your applicant details and project title.</li>
                  <li className="rounded-2xl border border-gray-800 bg-black/40 px-4 py-4">A clear robotics concept describing the problem, approach, and intended impact.</li>
                  <li className="rounded-2xl border border-gray-800 bg-black/40 px-4 py-4">Supporting files such as slides, sketches, CAD files, photos, code exports, or short video material.</li>
                </ul>
              </div>
            </div>
          </section>
        )}

        {!featuredHackathon && (
          <div className="mt-12 rounded-[28px] border border-yellow-400/15 bg-gray-950/90 p-10 text-center">
            <h3 className="text-2xl font-bold text-yellow-300">No Hackathon Available</h3>
            <p className="mt-3 text-gray-400">The next public hackathon announcement will appear here.</p>
          </div>
        )}

        {showApplicationForm && featuredHackathon && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto">
            <div className="min-h-screen px-4 py-10 flex items-start justify-center">
              <div className="w-full max-w-4xl rounded-[28px] border border-yellow-400/20 bg-gray-950 p-6 sm:p-8 shadow-2xl">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-yellow-300">Application Form</div>
                    <h2 className="mt-2 text-3xl font-black text-white">{featuredHackathon.title}</h2>
                    <p className="mt-3 text-gray-400">Complete the form below and upload your supporting materials.</p>
                  </div>
                  <button type="button" onClick={() => setShowApplicationForm(false)} className="rounded-full border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:border-yellow-400 hover:text-white">Close</button>
                </div>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input value={applicationForm.first_name} onChange={(e) => setApplicationForm((prev) => ({ ...prev, first_name: e.target.value }))} placeholder="First name *" className="bg-black/40 border border-gray-700 rounded-2xl px-4 py-3 text-white outline-none focus:border-yellow-400" />
                  <input value={applicationForm.last_name} onChange={(e) => setApplicationForm((prev) => ({ ...prev, last_name: e.target.value }))} placeholder="Last name *" className="bg-black/40 border border-gray-700 rounded-2xl px-4 py-3 text-white outline-none focus:border-yellow-400" />
                  <input type="email" value={applicationForm.email} onChange={(e) => setApplicationForm((prev) => ({ ...prev, email: e.target.value }))} placeholder="Email *" className="bg-black/40 border border-gray-700 rounded-2xl px-4 py-3 text-white outline-none focus:border-yellow-400" />
                  <input value={applicationForm.phone_number} onChange={(e) => setApplicationForm((prev) => ({ ...prev, phone_number: e.target.value }))} placeholder="Phone number *" className="bg-black/40 border border-gray-700 rounded-2xl px-4 py-3 text-white outline-none focus:border-yellow-400" />
                  <input value={applicationForm.age} onChange={(e) => setApplicationForm((prev) => ({ ...prev, age: e.target.value }))} placeholder="Age" className="bg-black/40 border border-gray-700 rounded-2xl px-4 py-3 text-white outline-none focus:border-yellow-400" />
                  <input value={applicationForm.city} onChange={(e) => setApplicationForm((prev) => ({ ...prev, city: e.target.value }))} placeholder="City" className="bg-black/40 border border-gray-700 rounded-2xl px-4 py-3 text-white outline-none focus:border-yellow-400" />
                  <input value={applicationForm.country} onChange={(e) => setApplicationForm((prev) => ({ ...prev, country: e.target.value }))} placeholder="Country" className="bg-black/40 border border-gray-700 rounded-2xl px-4 py-3 text-white outline-none focus:border-yellow-400 md:col-span-2" />
                  <input value={applicationForm.project_name} onChange={(e) => setApplicationForm((prev) => ({ ...prev, project_name: e.target.value }))} placeholder="Project title *" className="bg-black/40 border border-gray-700 rounded-2xl px-4 py-3 text-white outline-none focus:border-yellow-400 md:col-span-2" />
                  <textarea value={applicationForm.robot_concept} onChange={(e) => setApplicationForm((prev) => ({ ...prev, robot_concept: e.target.value }))} rows={4} placeholder="What real-world problem does your robot solve? *" className="bg-black/40 border border-gray-700 rounded-2xl px-4 py-3 text-white outline-none focus:border-yellow-400 md:col-span-2" />
                  <textarea value={applicationForm.project_description} onChange={(e) => setApplicationForm((prev) => ({ ...prev, project_description: e.target.value }))} rows={5} placeholder="Describe your project, approach, and intended impact *" className="bg-black/40 border border-gray-700 rounded-2xl px-4 py-3 text-white outline-none focus:border-yellow-400 md:col-span-2" />
                  <textarea value={applicationForm.technologies_used} onChange={(e) => setApplicationForm((prev) => ({ ...prev, technologies_used: e.target.value }))} rows={3} placeholder="Technologies, tools, sensors, or platforms used" className="bg-black/40 border border-gray-700 rounded-2xl px-4 py-3 text-white outline-none focus:border-yellow-400 md:col-span-2" />
                  <textarea value={applicationForm.electronics_concept} onChange={(e) => setApplicationForm((prev) => ({ ...prev, electronics_concept: e.target.value }))} rows={3} placeholder="Electronics or hardware concept" className="bg-black/40 border border-gray-700 rounded-2xl px-4 py-3 text-white outline-none focus:border-yellow-400" />
                  <textarea value={applicationForm.software_ai_concept} onChange={(e) => setApplicationForm((prev) => ({ ...prev, software_ai_concept: e.target.value }))} rows={3} placeholder="Software, control, or AI concept" className="bg-black/40 border border-gray-700 rounded-2xl px-4 py-3 text-white outline-none focus:border-yellow-400" />
                  <textarea value={applicationForm.cad_design_info} onChange={(e) => setApplicationForm((prev) => ({ ...prev, cad_design_info: e.target.value }))} rows={3} placeholder="CAD, design, or structure notes" className="bg-black/40 border border-gray-700 rounded-2xl px-4 py-3 text-white outline-none focus:border-yellow-400 md:col-span-2" />
                  <input value={applicationForm.video_url} onChange={(e) => setApplicationForm((prev) => ({ ...prev, video_url: e.target.value }))} placeholder="Optional demo video link" className="bg-black/40 border border-gray-700 rounded-2xl px-4 py-3 text-white outline-none focus:border-yellow-400 md:col-span-2" />
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="rounded-2xl border border-dashed border-yellow-400/30 bg-black/30 p-4 text-sm text-gray-300">
                    <div className="font-semibold text-yellow-300 uppercase tracking-[0.2em] text-xs">Supporting Files</div>
                    <div className="mt-2">Upload slides, sketches, photos, CAD exports, code files, or documentation.</div>
                    <input type="file" multiple className="mt-4 block w-full text-sm text-gray-300 file:mr-4 file:rounded-full file:border-0 file:bg-yellow-400 file:px-4 file:py-2 file:text-black file:font-semibold" onChange={(e) => setSubmissionFiles(Array.from(e.target.files || []))} />
                    {submissionFiles.length > 0 && <div className="mt-3 text-xs text-gray-400">{submissionFiles.length} file(s) selected</div>}
                  </label>
                  <label className="rounded-2xl border border-dashed border-yellow-400/30 bg-black/30 p-4 text-sm text-gray-300">
                    <div className="font-semibold text-yellow-300 uppercase tracking-[0.2em] text-xs">Video Upload</div>
                    <div className="mt-2">Upload a short demo clip if you want to share a walkthrough instead of a link.</div>
                    <input type="file" accept="video/*" className="mt-4 block w-full text-sm text-gray-300 file:mr-4 file:rounded-full file:border-0 file:bg-yellow-400 file:px-4 file:py-2 file:text-black file:font-semibold" onChange={(e) => setVideoFile(e.target.files?.[0] || null)} />
                    {videoFile && <div className="mt-3 text-xs text-gray-400">{videoFile.name}</div>}
                  </label>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="text-sm text-gray-400">Fields marked with * are required. You only need one supporting upload or a video link to submit.</div>
                  <button
                    type="button"
                    onClick={submitApplication}
                    disabled={isApplying}
                    className="rounded-full bg-yellow-400 px-6 py-3 text-sm font-black uppercase tracking-[0.2em] text-black transition hover:bg-yellow-300 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isApplying ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Student Course Detail Page (standalone workspace for a single enrolled course)
const StudentCourseDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const courseId = params.get('course_id');

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <StudentCoursePage courseId={courseId} onBack={() => navigate(PAGE_TO_PATH['student-dashboard'])} />
      </div>
    </div>
  );
};

// Teacher Courses Page Component (standalone, course-content only — no dashboard chrome)
const TeacherCoursesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('');

  const queryParams = new URLSearchParams(location.search);
  const courseDetailId = queryParams.get('course_id');

  const goToPage = (page, params = {}) => {
    const targetPath = PAGE_TO_PATH[page] || PAGE_TO_PATH.home;
    const cleanParams = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== null && v !== undefined && v !== ''));
    const search = new URLSearchParams(cleanParams).toString();
    navigate(`${targetPath}${search ? `?${search}` : ''}`);
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/dashboard/teacher`);
        if (isMounted) setCourses(response.data?.teacher_courses || []);
      } catch (error) {
        if (isMounted) setCourses([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  if (courseDetailId) {
    return (
      <div className="bg-black min-h-screen text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <TeacherCourseManagement
            courseId={courseDetailId}
            courseTitle={courses.find((course) => course.id === courseDetailId)?.title}
            onBack={() => goToPage('teacher-courses')}
            onOpenGlobalTab={(tabId) => goToPage('teacher-dashboard', { tab: tabId })}
          />
        </div>
      </div>
    );
  }

  const filteredCourses = courses.filter((course) => {
    const term = search.trim().toLowerCase();
    const matchesSearch = !term || [course.title, course.description, course.category, course.difficulty_level, course.best_for].filter(Boolean).join(' ').toLowerCase().includes(term);
    const courseStatus = course.status || (course.published ? 'published' : 'draft');
    const matchesStatus = !statusFilter || courseStatus === statusFilter;
    const matchesAvailability = !availabilityFilter || (course.availability_status || '') === availabilityFilter;
    return matchesSearch && matchesStatus && matchesAvailability;
  });

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-900 border border-yellow-400/20 text-yellow-400 text-sm mb-4">
            My Courses
          </div>
          <h1 className="text-4xl font-bold text-yellow-400 mb-3">Your Assigned Courses</h1>
          <p className="text-gray-400 max-w-2xl">
            Everything related to the courses you teach — content, lectures, assignments, and students — in one focused place.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-3 mb-8">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search your courses by title, category, or description..."
            className="flex-1 bg-gray-900 border border-gray-700 focus:border-yellow-400 text-white px-4 py-3 rounded-xl outline-none transition-colors"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-900 border border-gray-700 focus:border-yellow-400 text-white px-4 py-3 rounded-xl outline-none transition-colors"
          >
            <option value="">All statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
          <select
            value={availabilityFilter}
            onChange={(e) => setAvailabilityFilter(e.target.value)}
            className="bg-gray-900 border border-gray-700 focus:border-yellow-400 text-white px-4 py-3 rounded-xl outline-none transition-colors"
          >
            <option value="">All availability</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="coming_soon">Coming Soon</option>
          </select>
        </div>

        {loading ? (
          <div className="text-gray-400">Loading your courses...</div>
        ) : filteredCourses.length === 0 ? (
          <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-10 text-center text-gray-400">
            {courses.length === 0
              ? 'No assigned courses yet. Courses assigned by K.R.A administrators will appear here.'
              : 'No courses match your search.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredCourses.map((course) => (
              <button
                key={course.id}
                onClick={() => goToPage('teacher-course-detail', { course_id: course.id })}
                className="text-left bg-gradient-to-b from-gray-900 to-black border border-yellow-400/20 hover:border-yellow-400 rounded-2xl p-6 transition-all hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(250,204,21,0.15)]"
              >
                <div className="h-36 rounded-xl overflow-hidden border border-yellow-400/20 mb-4 bg-gray-800">
                  <img
                    src={resolveImageUrl(course.thumbnail_url) || 'https://images.unsplash.com/photo-1581092921461-7d65ca45393a?auto=format&fit=crop&w=1200&q=60'}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs uppercase tracking-wide px-2 py-1 rounded-full bg-yellow-400/10 text-yellow-400 border border-yellow-400/30">
                    {course.status || (course.published ? 'published' : 'draft')}
                  </span>
                  <span className="text-xs text-gray-500">{(course.modules || []).length} modules</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-3 text-xs">
                  <span className="px-2 py-1 rounded-full bg-gray-800 border border-gray-700 text-gray-200">{course.category || 'General'}</span>
                  <span className="px-2 py-1 rounded-full bg-gray-800 border border-gray-700 text-gray-200">{course.difficulty_level || course.level || 'Beginner'}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{course.title}</h3>
                <p className="text-gray-400 text-sm line-clamp-3 mb-4">{course.description}</p>
                {course.best_for && <p className="text-gray-300 text-sm mb-2">Best For: <span className="text-yellow-300">{course.best_for}</span></p>}
                <div className="grid grid-cols-2 gap-2 mb-4 text-xs text-gray-300">
                  <div className="rounded-lg border border-gray-700 bg-gray-900 px-2 py-1">Students: {course.enrolled_students_count || 0}</div>
                  <div className="rounded-lg border border-gray-700 bg-gray-900 px-2 py-1">Availability: {course.availability_status || 'open'}</div>
                </div>
                <div className="text-sm text-yellow-400 font-medium">Open Course Management &rarr;</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TeacherMessagesPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [teacherAdmins, setTeacherAdmins] = useState([]);
  const [teacherConversationSearch, setTeacherConversationSearch] = useState('');
  const [teacherConversations, setTeacherConversations] = useState([]);
  const [selectedTeacherConversationId, setSelectedTeacherConversationId] = useState('');
  const [teacherConversationDetail, setTeacherConversationDetail] = useState(null);
  const [teacherConversationMessages, setTeacherConversationMessages] = useState([]);
  const [selectedTeacherAdminId, setSelectedTeacherAdminId] = useState('');
  const [teacherConversationReply, setTeacherConversationReply] = useState('');
  const [teacherAttachmentDraft, setTeacherAttachmentDraft] = useState([]);
  const [teacherAttachmentUploading, setTeacherAttachmentUploading] = useState(false);
  const [teacherMessageSubmitting, setTeacherMessageSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');

  const userRole = (user?.role || '').toLowerCase();
  const isTeacher = userRole === 'teacher' || userRole === 'mentor';

  const goToPage = (page, params = {}) => {
    const targetPath = PAGE_TO_PATH[page] || PAGE_TO_PATH.home;
    const cleanParams = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== null && v !== undefined && v !== ''));
    const search = new URLSearchParams(cleanParams).toString();
    navigate(`${targetPath}${search ? `?${search}` : ''}`);
  };

  const setFeedback = (value) => {
    setStatusMessage(value);
    window.clearTimeout(setFeedback._t);
    setFeedback._t = window.setTimeout(() => setStatusMessage(''), 4000);
  };

  const fetchTeacherMessageAdmins = async () => {
    try {
      const response = await axios.get(`${API}/teacher/admin-conversations/admins`);
      const admins = response.data?.admins || [];
      setTeacherAdmins(admins);
      if (!selectedTeacherAdminId && admins.length > 0) {
        setSelectedTeacherAdminId(admins[0].id || '');
      }
    } catch (error) {
      setTeacherAdmins([]);
      setFeedback(error.response?.data?.detail || 'Unable to load admin contacts');
    }
  };

  const fetchTeacherConversations = async (searchValue = teacherConversationSearch) => {
    try {
      const response = await axios.get(`${API}/teacher/admin-conversations`, {
        params: {
          search: (searchValue || '').trim() || undefined,
        },
      });
      setTeacherConversations(response.data?.conversations || []);
    } catch (error) {
      setTeacherConversations([]);
    }
  };

  const openTeacherConversation = async (conversationId) => {
    if (!conversationId) return;
    try {
      setSelectedTeacherConversationId(conversationId);
      const response = await axios.get(`${API}/teacher/admin-conversations/${conversationId}`);
      setTeacherConversationDetail(response.data?.conversation || null);
      setTeacherConversationMessages(response.data?.messages || []);
      await axios.post(`${API}/teacher/admin-conversations/${conversationId}/read`);
      await fetchTeacherConversations();
    } catch (error) {
      setFeedback(error.response?.data?.detail || 'Unable to open conversation');
    }
  };

  const startTeacherConversation = async () => {
    if (!selectedTeacherAdminId) {
      setFeedback('Select an admin first');
      return;
    }

    try {
      const response = await axios.post(`${API}/teacher/admin-conversations`, {
        admin_id: selectedTeacherAdminId,
      });
      const conversationId = response.data?.conversation?.id;
      await fetchTeacherConversations();
      if (conversationId) {
        await openTeacherConversation(conversationId);
      }
    } catch (error) {
      setFeedback(error.response?.data?.detail || 'Unable to start conversation');
    }
  };

  const uploadTeacherMessageAttachments = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    try {
      setTeacherAttachmentUploading(true);
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      if (selectedTeacherConversationId) {
        formData.append('conversation_id', selectedTeacherConversationId);
      }
      const response = await axios.post(`${API}/teacher/admin-conversations/attachments/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const attachments = response.data?.attachments || [];
      setTeacherAttachmentDraft((prev) => [...prev, ...attachments]);
    } catch (error) {
      setFeedback(error.response?.data?.detail || 'Unable to upload attachments');
    } finally {
      setTeacherAttachmentUploading(false);
    }
  };

  const sendTeacherMessage = async () => {
    const text = (teacherConversationReply || '').trim();
    const attachmentIds = (teacherAttachmentDraft || []).map((item) => item.id).filter(Boolean);
    if (!selectedTeacherConversationId) {
      setFeedback('Start or select a conversation first');
      return;
    }
    if (!text && attachmentIds.length === 0) {
      setFeedback('Type a message or attach a file');
      return;
    }

    try {
      setTeacherMessageSubmitting(true);
      await axios.post(`${API}/teacher/admin-conversations/${selectedTeacherConversationId}/messages`, {
        message: text,
        attachment_ids: attachmentIds,
      });
      setTeacherConversationReply('');
      setTeacherAttachmentDraft([]);
      await openTeacherConversation(selectedTeacherConversationId);
    } catch (error) {
      setFeedback(error.response?.data?.detail || 'Unable to send message');
    } finally {
      setTeacherMessageSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isTeacher) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    (async () => {
      await fetchTeacherMessageAdmins();
      await fetchTeacherConversations('');
      if (isMounted) setLoading(false);
    })();
    return () => {
      isMounted = false;
    };
  }, [isTeacher]);

  useEffect(() => {
    if (!isTeacher) return;
    const timer = window.setTimeout(() => {
      fetchTeacherConversations(teacherConversationSearch);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [isTeacher, teacherConversationSearch]);

  useEffect(() => {
    if (!isTeacher) return;
    const params = new URLSearchParams(location.search);
    const requestedConversationId = params.get('conversation_id');
    if (!requestedConversationId) return;
    if (requestedConversationId === selectedTeacherConversationId) return;
    openTeacherConversation(requestedConversationId);
  }, [isTeacher, location.search, selectedTeacherConversationId]);

  if (!user) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-yellow-400 mb-4">Access Denied</h2>
          <p className="text-gray-300 mb-6">Please login to view messages</p>
        </div>
      </div>
    );
  }

  if (!isTeacher) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center px-6">
        <div className="max-w-xl bg-gray-900 border border-red-500/40 rounded-2xl p-8 text-center">
          <h2 className="text-3xl font-bold text-red-400 mb-4">Teacher Access Required</h2>
          <p className="text-gray-300 mb-6">Only teacher accounts can access this page.</p>
          <button onClick={() => goToPage('home')} className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-3 rounded-lg font-bold">Back to Home</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-3xl font-bold text-yellow-400">Messages</h1>
          <button onClick={() => goToPage('teacher-dashboard')} className="border border-gray-600 hover:border-yellow-400 text-gray-200 hover:text-yellow-300 px-4 py-2 rounded-lg text-sm">Back to Dashboard</button>
        </div>

        {statusMessage && (
          <div className="bg-gray-900 border border-yellow-400/30 text-yellow-200 rounded-xl px-4 py-3 text-sm">
            {statusMessage}
          </div>
        )}

        <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-4 md:p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
            <select
              value={selectedTeacherAdminId}
              onChange={(e) => setSelectedTeacherAdminId(e.target.value)}
              className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
            >
              <option value="">Select admin to message</option>
              {(teacherAdmins || []).map((admin) => (
                <option key={admin.id} value={admin.id}>{admin.name || admin.email}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={startTeacherConversation}
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-3 rounded-lg font-bold"
            >
              Start or Open Chat
            </button>
          </div>

          <input
            value={teacherConversationSearch}
            onChange={(e) => setTeacherConversationSearch(e.target.value)}
            className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
            placeholder="Search by admin name or message content"
          />

          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-3">
            <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
              {(teacherConversations || []).length === 0 ? (
                <div className="text-sm text-gray-500">No conversations yet. Start one with an admin.</div>
              ) : (
                (teacherConversations || []).map((thread) => (
                  <button
                    key={thread.id}
                    type="button"
                    onClick={() => openTeacherConversation(thread.id)}
                    className={`w-full text-left bg-gray-800 rounded-xl p-3 border ${selectedTeacherConversationId === thread.id ? 'border-yellow-400' : 'border-gray-700 hover:border-yellow-500/60'}`}
                  >
                    <div className="flex justify-between gap-2 text-sm">
                      <div className="text-white font-medium line-clamp-1">{thread.display_name || thread.title || 'Conversation'}</div>
                      {thread.unread_count > 0 && <span className="text-[10px] bg-yellow-500 text-black px-2 py-0.5 rounded-full font-bold">{thread.unread_count}</span>}
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-yellow-300 mt-2">Subject</div>
                    <div className="text-xs text-gray-200 line-clamp-1">{thread.subject || thread.subject_display || 'No Subject'}</div>
                    <div className="text-xs text-gray-400 mt-1 line-clamp-2">{thread.last_message_preview || 'No messages yet'}</div>
                  </button>
                ))
              )}
            </div>

            <div className="bg-gray-800 rounded-xl border border-gray-700 p-3 space-y-3">
              {!selectedTeacherConversationId ? (
                <div className="text-sm text-gray-500">Select a conversation to view messages.</div>
              ) : (
                <>
                  <div>
                    <div className="text-white font-semibold">{teacherConversationDetail?.display_name || teacherConversationDetail?.title || 'Conversation'}</div>
                    <div className="text-[10px] uppercase tracking-wide text-yellow-300 mt-2">Subject</div>
                    <div className="text-sm text-gray-200">{teacherConversationDetail?.subject || teacherConversationDetail?.subject_display || 'No Subject'}</div>
                    <div className="text-xs text-gray-400 mt-1">{(teacherConversationDetail?.participant_profiles || []).map((p) => p.name || p.email).join(', ')}</div>
                  </div>

                  <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
                    {(teacherConversationMessages || []).length === 0 ? (
                      <div className="text-sm text-gray-500">No messages yet.</div>
                    ) : (
                      (teacherConversationMessages || []).map((msg) => (
                        <div key={msg.id} className="bg-gray-900 rounded-xl border border-gray-700 p-3">
                          <div className="text-xs text-yellow-300 font-semibold">{msg.sender_name || 'User'} • {new Date(msg.created_at).toLocaleString()}</div>
                          {msg.message && <div className="text-sm text-gray-100 mt-1 whitespace-pre-wrap">{msg.message}</div>}
                          {(msg.attachments || []).length > 0 && (
                            <div className="mt-2 space-y-1.5">
                              {(msg.attachments || []).map((attachment) => (
                                <div key={attachment.id} className="flex items-center gap-2 bg-gray-800/60 rounded-lg px-2.5 py-1.5">
                                  <span className="text-xs text-gray-200 truncate flex-1">{attachment.filename || 'Attachment'}</span>
                                  <div className="flex gap-1.5 shrink-0">
                                    {(isImageAttachment(attachment) || isVideoAttachment(attachment)) && resolveAttachmentUrl(attachment, true) && (
                                      <button type="button" onClick={() => downloadProtectedAttachment(attachment, true)} className="text-[10px] px-2 py-0.5 rounded border border-gray-600 text-gray-300 hover:border-yellow-400">Preview</button>
                                    )}
                                    <button type="button" onClick={() => downloadProtectedAttachment(attachment, false)} className="text-[10px] px-2 py-0.5 rounded border border-gray-600 text-yellow-300 hover:border-yellow-400">Download</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  {teacherAttachmentDraft.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {teacherAttachmentDraft.map((att) => (
                        <div key={att.id} className="flex items-center gap-1.5 bg-gray-700 rounded-lg px-2 py-1 text-xs text-gray-200">
                          <span className="truncate max-w-[120px]">{att.filename}</span>
                          <button type="button" onClick={() => setTeacherAttachmentDraft((current) => current.filter((item) => item.id !== att.id))} className="text-red-400 hover:text-red-300 ml-1">x</button>
                        </div>
                      ))}
                    </div>
                  )}

                  <textarea
                    value={teacherConversationReply}
                    onChange={(e) => setTeacherConversationReply(e.target.value)}
                    rows={3}
                    className="w-full bg-gray-900 text-white px-3 py-2 rounded-lg border border-gray-600 text-sm"
                    placeholder="Write your message to admin"
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={sendTeacherMessage}
                      disabled={teacherMessageSubmitting}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
                    >
                      {teacherMessageSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                    <label className="cursor-pointer text-xs text-gray-300 border border-gray-600 px-3 py-2 rounded-lg hover:border-yellow-400">
                      {teacherAttachmentUploading ? 'Uploading...' : '+ Attach File'}
                      <input type="file" multiple className="hidden" disabled={teacherAttachmentUploading} onChange={(e) => uploadTeacherMessageAttachments(e.target.files)} />
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StudentMessagesPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [studentAdmins, setStudentAdmins] = useState([]);
  const [conversationSearch, setConversationSearch] = useState('');
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState('');
  const [conversationDetail, setConversationDetail] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [replyDraft, setReplyDraft] = useState('');
  const [attachmentDraft, setAttachmentDraft] = useState([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [messageSubmitting, setMessageSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');

  const isStudent = (user?.role || '').toLowerCase() === 'student';

  const goToPage = (page, params = {}) => {
    const targetPath = PAGE_TO_PATH[page] || PAGE_TO_PATH.home;
    const cleanParams = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== null && v !== undefined && v !== ''));
    const search = new URLSearchParams(cleanParams).toString();
    navigate(`${targetPath}${search ? `?${search}` : ''}`);
  };

  const setFeedback = (value) => {
    setStatusMessage(value);
    window.clearTimeout(setFeedback._t);
    setFeedback._t = window.setTimeout(() => setStatusMessage(''), 4000);
  };

  const fetchStudentMessageAdmins = async () => {
    try {
      const response = await axios.get(`${API}/student/admin-conversations/admins`);
      const admins = response.data?.admins || [];
      setStudentAdmins(admins);
      if (!selectedAdminId && admins.length > 0) {
        setSelectedAdminId(admins[0].id || '');
      }
    } catch (error) {
      setStudentAdmins([]);
      setFeedback(error.response?.data?.detail || 'Unable to load admin contacts');
    }
  };

  const fetchConversations = async (searchValue = conversationSearch) => {
    try {
      const response = await axios.get(`${API}/student/admin-conversations`, {
        params: {
          search: (searchValue || '').trim() || undefined,
        },
      });
      setConversations(response.data?.conversations || []);
    } catch (error) {
      setConversations([]);
    }
  };

  const openConversation = async (conversationId) => {
    if (!conversationId) return;
    try {
      setSelectedConversationId(conversationId);
      const response = await axios.get(`${API}/student/admin-conversations/${conversationId}`);
      setConversationDetail(response.data?.conversation || null);
      setConversationMessages(response.data?.messages || []);
      await axios.post(`${API}/student/admin-conversations/${conversationId}/read`);
      await fetchConversations();
    } catch (error) {
      setFeedback(error.response?.data?.detail || 'Unable to open conversation');
    }
  };

  const startConversation = async () => {
    if (!selectedAdminId) {
      setFeedback('Select an admin first');
      return;
    }

    try {
      const response = await axios.post(`${API}/student/admin-conversations`, {
        admin_id: selectedAdminId,
      });
      const conversationId = response.data?.conversation?.id;
      await fetchConversations();
      if (conversationId) {
        await openConversation(conversationId);
      }
    } catch (error) {
      setFeedback(error.response?.data?.detail || 'Unable to start conversation');
    }
  };

  const uploadMessageAttachments = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    try {
      setAttachmentUploading(true);
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      if (selectedConversationId) {
        formData.append('conversation_id', selectedConversationId);
      }
      const response = await axios.post(`${API}/student/admin-conversations/attachments/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const attachments = response.data?.attachments || [];
      setAttachmentDraft((prev) => [...prev, ...attachments]);
    } catch (error) {
      setFeedback(error.response?.data?.detail || 'Unable to upload attachments');
    } finally {
      setAttachmentUploading(false);
    }
  };

  const sendMessage = async () => {
    const text = (replyDraft || '').trim();
    const attachmentIds = (attachmentDraft || []).map((item) => item.id).filter(Boolean);
    if (!selectedConversationId) {
      setFeedback('Start or select a conversation first');
      return;
    }
    if (!text && attachmentIds.length === 0) {
      setFeedback('Type a message or attach a file');
      return;
    }

    try {
      setMessageSubmitting(true);
      await axios.post(`${API}/student/admin-conversations/${selectedConversationId}/messages`, {
        message: text,
        attachment_ids: attachmentIds,
      });
      setReplyDraft('');
      setAttachmentDraft([]);
      await openConversation(selectedConversationId);
    } catch (error) {
      setFeedback(error.response?.data?.detail || 'Unable to send message');
    } finally {
      setMessageSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isStudent) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    (async () => {
      await fetchStudentMessageAdmins();
      await fetchConversations('');
      if (isMounted) setLoading(false);
    })();
    return () => {
      isMounted = false;
    };
  }, [isStudent]);

  useEffect(() => {
    if (!isStudent) return;
    const timer = window.setTimeout(() => {
      fetchConversations(conversationSearch);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [isStudent, conversationSearch]);

  useEffect(() => {
    if (!isStudent) return;
    const params = new URLSearchParams(location.search);
    const requestedConversationId = params.get('conversation_id');
    if (!requestedConversationId) return;
    if (requestedConversationId === selectedConversationId) return;
    openConversation(requestedConversationId);
  }, [isStudent, location.search, selectedConversationId]);

  if (!user) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-yellow-400 mb-4">Access Denied</h2>
          <p className="text-gray-300 mb-6">Please login to view messages</p>
        </div>
      </div>
    );
  }

  if (!isStudent) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center px-6">
        <div className="max-w-xl bg-gray-900 border border-red-500/40 rounded-2xl p-8 text-center">
          <h2 className="text-3xl font-bold text-red-400 mb-4">Student Access Required</h2>
          <p className="text-gray-300 mb-6">Only student accounts can access this page.</p>
          <button onClick={() => goToPage('home')} className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-3 rounded-lg font-bold">Back to Home</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-3xl font-bold text-yellow-400">Messages</h1>
          <button onClick={() => goToPage('student-dashboard')} className="border border-gray-600 hover:border-yellow-400 text-gray-200 hover:text-yellow-300 px-4 py-2 rounded-lg text-sm">Back to Dashboard</button>
        </div>

        {statusMessage && (
          <div className="bg-gray-900 border border-yellow-400/30 text-yellow-200 rounded-xl px-4 py-3 text-sm">
            {statusMessage}
          </div>
        )}

        <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-4 md:p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
            <select
              value={selectedAdminId}
              onChange={(e) => setSelectedAdminId(e.target.value)}
              className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
            >
              <option value="">Select admin to message</option>
              {(studentAdmins || []).map((admin) => (
                <option key={admin.id} value={admin.id}>{admin.name || admin.email}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={startConversation}
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-3 rounded-lg font-bold"
            >
              Start or Open Chat
            </button>
          </div>

          <input
            value={conversationSearch}
            onChange={(e) => setConversationSearch(e.target.value)}
            className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
            placeholder="Search by admin name or message content"
          />

          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-3">
            <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
              {(conversations || []).length === 0 ? (
                <div className="text-sm text-gray-500">No conversations yet. Start one with an admin.</div>
              ) : (
                (conversations || []).map((thread) => (
                  <button
                    key={thread.id}
                    type="button"
                    onClick={() => openConversation(thread.id)}
                    className={`w-full text-left bg-gray-800 rounded-xl p-3 border ${selectedConversationId === thread.id ? 'border-yellow-400' : 'border-gray-700 hover:border-yellow-500/60'}`}
                  >
                    <div className="flex justify-between gap-2 text-sm">
                      <div className="text-white font-medium line-clamp-1">{thread.display_name || thread.title || 'Conversation'}</div>
                      {thread.unread_count > 0 && <span className="text-[10px] bg-yellow-500 text-black px-2 py-0.5 rounded-full font-bold">{thread.unread_count}</span>}
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-yellow-300 mt-2">Subject</div>
                    <div className="text-xs text-gray-200 line-clamp-1">{thread.subject || thread.subject_display || 'No Subject'}</div>
                    <div className="text-xs text-gray-400 mt-1 line-clamp-2">{thread.last_message_preview || 'No messages yet'}</div>
                  </button>
                ))
              )}
            </div>

            <div className="bg-gray-800 rounded-xl border border-gray-700 p-3 space-y-3">
              {!selectedConversationId ? (
                <div className="text-sm text-gray-500">Select a conversation to view messages.</div>
              ) : (
                <>
                  <div>
                    <div className="text-white font-semibold">{conversationDetail?.display_name || conversationDetail?.title || 'Conversation'}</div>
                    <div className="text-[10px] uppercase tracking-wide text-yellow-300 mt-2">Subject</div>
                    <div className="text-sm text-gray-200">{conversationDetail?.subject || conversationDetail?.subject_display || 'No Subject'}</div>
                    <div className="text-xs text-gray-400 mt-1">{(conversationDetail?.participant_profiles || []).map((p) => p.name || p.email).join(', ')}</div>
                  </div>

                  <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
                    {(conversationMessages || []).length === 0 ? (
                      <div className="text-sm text-gray-500">No messages yet.</div>
                    ) : (
                      (conversationMessages || []).map((msg) => (
                        <div key={msg.id} className="bg-gray-900 rounded-xl border border-gray-700 p-3">
                          <div className="text-xs text-yellow-300 font-semibold">{msg.sender_name || 'User'} • {new Date(msg.created_at).toLocaleString()}</div>
                          {msg.message && <div className="text-sm text-gray-100 mt-1 whitespace-pre-wrap">{msg.message}</div>}
                          {(msg.attachments || []).length > 0 && (
                            <div className="mt-2 space-y-1.5">
                              {(msg.attachments || []).map((attachment) => (
                                <div key={attachment.id} className="flex items-center gap-2 bg-gray-800/60 rounded-lg px-2.5 py-1.5">
                                  <span className="text-xs text-gray-200 truncate flex-1">{attachment.filename || 'Attachment'}</span>
                                  <div className="flex gap-1.5 shrink-0">
                                    {(isImageAttachment(attachment) || isVideoAttachment(attachment)) && resolveAttachmentUrl(attachment, true) && (
                                      <button type="button" onClick={() => downloadProtectedAttachment(attachment, true)} className="text-[10px] px-2 py-0.5 rounded border border-gray-600 text-gray-300 hover:border-yellow-400">Preview</button>
                                    )}
                                    <button type="button" onClick={() => downloadProtectedAttachment(attachment, false)} className="text-[10px] px-2 py-0.5 rounded border border-gray-600 text-yellow-300 hover:border-yellow-400">Download</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  {attachmentDraft.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {attachmentDraft.map((att) => (
                        <div key={att.id} className="flex items-center gap-1.5 bg-gray-700 rounded-lg px-2 py-1 text-xs text-gray-200">
                          <span className="truncate max-w-[120px]">{att.filename}</span>
                          <button type="button" onClick={() => setAttachmentDraft((current) => current.filter((item) => item.id !== att.id))} className="text-red-400 hover:text-red-300 ml-1">x</button>
                        </div>
                      ))}
                    </div>
                  )}

                  <textarea
                    value={replyDraft}
                    onChange={(e) => setReplyDraft(e.target.value)}
                    rows={3}
                    className="w-full bg-gray-900 text-white px-3 py-2 rounded-lg border border-gray-600 text-sm"
                    placeholder="Write your message to admin"
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={sendMessage}
                      disabled={messageSubmitting}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
                    >
                      {messageSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                    <label className="cursor-pointer text-xs text-gray-300 border border-gray-600 px-3 py-2 rounded-lg hover:border-yellow-400">
                      {attachmentUploading ? 'Uploading...' : '+ Attach File'}
                      <input type="file" multiple className="hidden" disabled={attachmentUploading} onChange={(e) => uploadMessageAttachments(e.target.files)} />
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dashboard Page Component
const DashboardPage = ({ initialTab = null }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({});
  const [activeTab, setActiveTab] = useState('dashboard');
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    category: 'software',
    duration: 'Approximately 4 Months',
    level: 'Beginner',
    price: '0',
    instructor_bio: '',
    thumbnail_url: '',
    learning_objectives_text: '',
    attachments_text: '',
    status: 'draft',
  });
  const [courseStatus, setCourseStatus] = useState('');
  const [creatingCourse, setCreatingCourse] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [classForm, setClassForm] = useState({ name: '', course_id: '', date: '', start_time: '', end_time: '', meeting_link: '' });
  const [meetingForm, setMeetingForm] = useState({ title: '', class_id: '', provider: 'google_meet', meeting_url: '', date: '', start_time: '', end_time: '' });
  const [lectureForm, setLectureForm] = useState({ course_id: '', title: '', resource_type: 'video', resource_url: '', description: '' });
  const [lectureUploadFile, setLectureUploadFile] = useState(null);
  const [lectureFileInputKey, setLectureFileInputKey] = useState(0);
  const [assignmentForm, setAssignmentForm] = useState({ course_id: '', title: '', description: '', due_date: '' });
  const [teacherRequest, setTeacherRequest] = useState({ teacher_id: '', note: '' });
  const [teacherDirectory, setTeacherDirectory] = useState([]);
  const [browseCourses, setBrowseCourses] = useState([]);
  const [browseTeacherSearch, setBrowseTeacherSearch] = useState('');
  const [browseTeacherCategory, setBrowseTeacherCategory] = useState('');
  const [browseTeacherCourse, setBrowseTeacherCourse] = useState('');
  const [courseRequestingId, setCourseRequestingId] = useState(null);
  const [attendanceDraft, setAttendanceDraft] = useState({});
  const [attendanceClassId, setAttendanceClassId] = useState('');
  const [attendanceDate, setAttendanceDate] = useState('');
  const [attendanceSession, setAttendanceSession] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [closeAttendanceAfterSave, setCloseAttendanceAfterSave] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [classSearch, setClassSearch] = useState('');
  const [meetingSearch, setMeetingSearch] = useState('');
  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [studentAssignmentSearch, setStudentAssignmentSearch] = useState('');
  const [scheduleSearch, setScheduleSearch] = useState('');
  const [myCoursesSearch, setMyCoursesSearch] = useState('');
  const [attendanceStudentSearch, setAttendanceStudentSearch] = useState('');
  const [attendanceHistorySearch, setAttendanceHistorySearch] = useState('');
  const [lectureSearch, setLectureSearch] = useState('');
  const [lectureResults, setLectureResults] = useState([]);
  const [actionMessage, setActionMessage] = useState('');
  const [clocking, setClocking] = useState(false);
  const [liveSessionSeconds, setLiveSessionSeconds] = useState(0);

  const userRole = (user?.role || 'student').toLowerCase();
  const isTeacher = userRole === 'teacher' || userRole === 'mentor';

  useEffect(() => {
    if (!user) {
      return;
    }
    setActiveTab(isTeacher ? 'dashboard' : 'dashboard');
    fetchDashboard();
    if (!isTeacher) {
      fetchTeacherDirectory();
      fetchBrowseCourses();
    }
  }, [user, isTeacher]);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryTab = params.get('tab');
    if (queryTab && initialTab !== 'course-detail') {
      setActiveTab(queryTab);
    }
  }, [location.search, initialTab]);

  const goToPage = (page, params = {}) => {
    const targetPath = PAGE_TO_PATH[page] || PAGE_TO_PATH.home;
    const cleanParams = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== null && v !== undefined && v !== ''));
    const search = new URLSearchParams(cleanParams).toString();
    navigate(`${targetPath}${search ? `?${search}` : ''}`);
  };

  const handleNotificationClick = async (notification) => {
    if (notification?.id && !notification.is_read) {
      try { await axios.post(`${API}/notifications/${notification.id}/read`); } catch (_) { /* ignore */ }
    }
    const link = notification?.link;
    if (link?.page) {
      goToPage(link.page, link.params || {});
    }
  };

  const courseDetailParams = new URLSearchParams(location.search);
  const courseDetailId = courseDetailParams.get('course_id');
  const courseDetailSubTab = courseDetailParams.get('tab') || 'overview';
  const courseDetailHighlights = {
    student_id: courseDetailParams.get('student_id'),
    assignment_id: courseDetailParams.get('assignment_id'),
    discussion_id: courseDetailParams.get('discussion_id'),
    request_id: courseDetailParams.get('request_id'),
  };

  useEffect(() => {
    if (!isTeacher) return;
    if (attendanceClassId) return;
    const firstClass = (dashboardData.class_sessions || [])[0];
    if (firstClass?.id) {
      setAttendanceClassId(firstClass.id);
      setAttendanceDate(firstClass.date || '');
    }
  }, [dashboardData.class_sessions, isTeacher, attendanceClassId]);

  useEffect(() => {
    if (!isTeacher) return;
    const activeClockIn = dashboardData?.clock?.active_record?.clock_in_at;
    if (!activeClockIn) {
      setLiveSessionSeconds(0);
      return;
    }

    const updateTimer = () => {
      const startTime = new Date(activeClockIn).getTime();
      const now = Date.now();
      const seconds = Math.max(0, Math.floor((now - startTime) / 1000));
      setLiveSessionSeconds(seconds);
    };

    updateTimer();
    const timerId = window.setInterval(updateTimer, 1000);
    return () => window.clearInterval(timerId);
  }, [dashboardData?.clock?.active_record?.clock_in_at, isTeacher]);

  useEffect(() => {
    if (!isTeacher || activeTab !== 'attendance-management' || !attendanceClassId) return;
    loadAttendanceSession(attendanceClassId, attendanceDate);
  }, [isTeacher, activeTab, attendanceClassId]);

  useEffect(() => {
    if (!isTeacher || activeTab !== 'lectures') return;
    searchTeacherLectures();
  }, [isTeacher, activeTab, lectureSearch, lectureForm.course_id]);

  const fetchDashboard = async () => {
    try {
      const endpoint = isTeacher ? '/dashboard/teacher' : '/dashboard/student';
      const response = await axios.get(`${API}${endpoint}`);
      setDashboardData(response.data || {});
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData({});
    } finally {
      setLoading(false);
    }
  };

  const fetchTeacherDirectory = async () => {
    try {
      const response = await axios.get(`${API}/teachers`);
      setTeacherDirectory(response.data?.teachers || []);
    } catch (error) {
      setTeacherDirectory([]);
    }
  };

  const fetchBrowseCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`, { params: { published: true, limit: 500 } });
      const coursesData = response.data?.courses || response.data || [];
      setBrowseCourses(coursesData);
    } catch (error) {
      setBrowseCourses([]);
    }
  };

  const setStatusMessage = (value) => {
    setActionMessage(value);
    window.clearTimeout(setStatusMessage._t);
    setStatusMessage._t = window.setTimeout(() => setActionMessage(''), 4000);
  };

  const formatSessionDuration = (seconds) => {
    const hrs = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${hrs}:${mins}:${secs}`;
  };

  const uploadCourseThumbnail = async (file) => {
    if (!file) return;
    try {
      setUploadingThumbnail(true);
      const formData = new FormData();
      formData.append('thumbnail', file);
      const response = await axios.post(`${API}/teacher/courses/thumbnail/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCourseForm((prev) => ({ ...prev, thumbnail_url: response.data?.thumbnail_url || '' }));
      setCourseStatus('Thumbnail uploaded');
    } catch (error) {
      setCourseStatus(error.response?.data?.detail || 'Unable to upload thumbnail');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const clockIn = async () => {
    try {
      setClocking(true);
      await axios.post(`${API}/teacher/clock-in`);
      setStatusMessage('Clocked in successfully');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to clock in');
    } finally {
      setClocking(false);
    }
  };

  const clockOut = async () => {
    try {
      setClocking(true);
      const response = await axios.post(`${API}/teacher/clock-out`);
      setStatusMessage(`Clocked out. Total hours: ${response.data?.total_hours || 0}`);
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to clock out');
    } finally {
      setClocking(false);
    }
  };

  const createClass = async () => {
    try {
      if (!classForm.course_id) {
        setStatusMessage('Select a linked course before creating a class');
        return;
      }
      await axios.post(`${API}/teacher/classes`, {
        ...classForm,
        student_ids: (dashboardData.students || []).map((s) => s.id),
      });
      setClassForm({ name: '', course_id: '', date: '', start_time: '', end_time: '', meeting_link: '' });
      setStatusMessage('Class created successfully');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to create class');
    }
  };

  const cancelClass = async (classId) => {
    try {
      await axios.delete(`${API}/teacher/classes/${classId}`);
      setStatusMessage('Class cancelled');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to cancel class');
    }
  };

  const loadAttendanceSession = async (classId, targetDate = '') => {
    if (!classId) return;
    try {
      setAttendanceLoading(true);
      const response = await axios.get(`${API}/teacher/classes/${classId}/attendance`, {
        params: {
          attendance_date: targetDate || undefined,
        },
      });
      const payload = response.data || {};
      setAttendanceSession(payload);
      setAttendanceDate(payload.date || targetDate || '');

      const draft = {};
      (payload.students || []).forEach((student) => {
        draft[`${classId}_${student.student_id}`] = student.status || 'present';
      });
      setAttendanceDraft((prev) => ({ ...prev, ...draft }));
    } catch (error) {
      setAttendanceSession(null);
      setStatusMessage(error.response?.data?.detail || 'Unable to load attendance session');
    } finally {
      setAttendanceLoading(false);
    }
  };

  const reopenAttendance = async () => {
    if (!attendanceClassId) {
      setStatusMessage('Select a class first');
      return;
    }
    try {
      await axios.post(`${API}/teacher/classes/${attendanceClassId}/attendance/reopen`);
      setStatusMessage('Attendance reopened for editing');
      await loadAttendanceSession(attendanceClassId, attendanceDate);
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to reopen attendance');
    }
  };

  const saveAttendance = async (classItem) => {
    const records = (classItem.student_ids || []).map((studentId) => ({
      student_id: studentId,
      status: attendanceDraft[`${classItem.id}_${studentId}`] || 'present',
    }));
    if (!records.length) {
      setStatusMessage('No students enrolled for this class');
      return;
    }
    try {
      await axios.post(`${API}/teacher/classes/${classItem.id}/attendance`, { records });
      setStatusMessage('Attendance saved');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to save attendance');
    }
  };

  const saveManagedAttendance = async (closeAfterSave = false) => {
    if (!attendanceClassId || !attendanceSession) {
      setStatusMessage('Select a class and load attendance first');
      return;
    }
    const records = (attendanceSession.students || []).map((student) => ({
      student_id: student.student_id,
      status: attendanceDraft[`${attendanceClassId}_${student.student_id}`] || 'present',
      note: student.note || null,
    }));
    if (!records.length) {
      setStatusMessage('No students found in selected class');
      return;
    }
    try {
      await axios.post(`${API}/teacher/classes/${attendanceClassId}/attendance`, {
        records,
        attendance_date: attendanceDate || undefined,
        session_id: attendanceSession.session_id || undefined,
        close_after_save: closeAfterSave,
      });
      setStatusMessage(closeAfterSave ? 'Attendance saved and closed' : 'Attendance saved');
      await loadAttendanceSession(attendanceClassId, attendanceDate);
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to save attendance');
    }
  };

  const searchTeacherLectures = async () => {
    try {
      const response = await axios.get(`${API}/search/lectures`, {
        params: {
          q: lectureSearch || undefined,
          course_id: lectureForm.course_id || undefined,
        },
      });
      setLectureResults(response.data?.lectures || []);
    } catch (error) {
      setLectureResults([]);
    }
  };

  const createMeeting = async () => {
    try {
      await axios.post(`${API}/teacher/meetings`, meetingForm);
      setMeetingForm({ title: '', class_id: '', provider: 'google_meet', meeting_url: '', date: '', start_time: '', end_time: '' });
      setStatusMessage('Meeting created');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to create meeting');
    }
  };

  const cancelMeeting = async (meetingId) => {
    try {
      await axios.delete(`${API}/teacher/meetings/${meetingId}`);
      setStatusMessage('Meeting cancelled');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to cancel meeting');
    }
  };

  const createLecture = async () => {
    try {
      if (!lectureForm.course_id) {
        setStatusMessage('Select a course first');
        return;
      }
      if (!lectureForm.title.trim()) {
        setStatusMessage('Lecture title is required');
        return;
      }

      if (lectureUploadFile) {
        const formData = new FormData();
        formData.append('title', lectureForm.title);
        formData.append('description', lectureForm.description || '');
        formData.append('resource_type', lectureForm.resource_type);
        formData.append('lecture_file', lectureUploadFile);
        await axios.post(`${API}/teacher/courses/${lectureForm.course_id}/lectures/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        if (!lectureForm.resource_url.trim()) {
          setStatusMessage('Provide a resource URL or choose a file to upload');
          return;
        }
        await axios.post(`${API}/teacher/courses/${lectureForm.course_id}/lectures`, {
          title: lectureForm.title,
          resource_type: lectureForm.resource_type,
          resource_url: lectureForm.resource_url,
          description: lectureForm.description,
        });
      }
      setLectureForm({ ...lectureForm, title: '', resource_url: '', description: '' });
      setLectureUploadFile(null);
      setLectureFileInputKey((prev) => prev + 1);
      setStatusMessage('Lecture uploaded/linked successfully');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to upload lecture');
    }
  };

  const createAssignment = async () => {
    try {
      if (!assignmentForm.course_id) {
        setStatusMessage('Select a course first');
        return;
      }
      await axios.post(`${API}/teacher/courses/${assignmentForm.course_id}/assignments`, {
        title: assignmentForm.title,
        description: assignmentForm.description,
        due_date: assignmentForm.due_date || null,
      });
      setAssignmentForm({ ...assignmentForm, title: '', description: '', due_date: '' });
      setStatusMessage('Assignment created');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to create assignment');
    }
  };

  const publishCourse = async (courseId) => {
    try {
      await axios.post(`${API}/courses/${courseId}/publish`);
      setStatusMessage('Course published');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to publish course');
    }
  };

  const editCourse = async (course) => {
    const nextTitle = window.prompt('Edit course title', course.title || '');
    if (!nextTitle) return;
    const nextDescription = window.prompt('Edit course description', course.description || '');
    if (!nextDescription) return;
    const nextLevel = window.prompt('Edit difficulty level', course.level || 'Beginner');
    const nextStatus = window.prompt('Set status: draft or published', course.status || (course.published ? 'published' : 'draft'));
    try {
      await axios.put(`${API}/courses/${course.id}`, {
        title: nextTitle,
        description: nextDescription,
        level: nextLevel || 'Beginner',
        status: (nextStatus || 'draft').toLowerCase() === 'published' ? 'published' : 'draft',
      });
      setStatusMessage('Course updated');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to update course');
    }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm('Delete this course permanently?')) return;
    try {
      await axios.delete(`${API}/courses/${courseId}`);
      setStatusMessage('Course deleted');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to delete course');
    }
  };

  const approveStudentRequest = async (studentId) => {
    try {
      await axios.post(`${API}/teacher/assignment-requests/${studentId}/approve`);
      setStatusMessage('Student linked successfully');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to approve request');
    }
  };

  const rejectStudentRequest = async (studentId) => {
    try {
      await axios.post(`${API}/teacher/assignment-requests/${studentId}/reject`);
      setStatusMessage('Student request rejected');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to reject request');
    }
  };

  const requestCourseEnrollment = async (courseId, teacherId = null) => {
    try {
      setCourseRequestingId(courseId);
      await axios.post(`${API}/student/course-enrollment-requests`, {
        course_id: courseId,
        teacher_id: teacherId || undefined,
      });
      setStatusMessage('Course enrollment request sent');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to request enrollment');
    } finally {
      setCourseRequestingId(null);
    }
  };

  const approveCourseEnrollmentRequest = async (requestId) => {
    try {
      await axios.post(`${API}/teacher/course-enrollment-requests/${requestId}/approve`);
      setStatusMessage('Course enrollment approved');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to approve enrollment request');
    }
  };

  const rejectCourseEnrollmentRequest = async (requestId) => {
    try {
      await axios.post(`${API}/teacher/course-enrollment-requests/${requestId}/reject`);
      setStatusMessage('Course enrollment rejected');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to reject enrollment request');
    }
  };

  const waitlistCourseEnrollmentRequest = async (requestId) => {
    try {
      await axios.post(`${API}/teacher/course-enrollment-requests/${requestId}/waitlist`);
      setStatusMessage('Course enrollment waitlisted');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to waitlist enrollment request');
    }
  };

  const gradeStudentAssignment = async (assignmentId, studentId) => {
    const gradeValue = window.prompt('Enter grade (0-100)');
    if (gradeValue === null) return;
    const feedbackValue = window.prompt('Enter feedback (optional)') || '';
    const numericGrade = Number(gradeValue);
    if (Number.isNaN(numericGrade) || numericGrade < 0) {
      setStatusMessage('Grade must be a valid number');
      return;
    }
    try {
      await axios.post(`${API}/teacher/assignments/${assignmentId}/grade/${studentId}`, {
        grade: numericGrade,
        feedback: feedbackValue,
        status: 'graded',
      });
      setStatusMessage('Assignment graded successfully');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to grade assignment');
    }
  };

  const requestTeacher = async () => {
    try {
      await axios.post(`${API}/student/teacher-assignment/request`, teacherRequest);
      setStatusMessage('Teacher request submitted');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to request teacher');
    }
  };

  const requestTeacherById = async (teacherId) => {
    try {
      await axios.post(`${API}/student/teacher-assignment/request`, { teacher_id: teacherId, note: '' });
      setStatusMessage('Teacher request submitted');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to request teacher');
    }
  };

  const submitAssignment = async (assignmentId) => {
    const submissionText = window.prompt('Enter assignment submission text or link');
    if (!submissionText) return;
    try {
      await axios.post(`${API}/student/assignments/${assignmentId}/submit`, { submission_text: submissionText });
      setStatusMessage('Assignment submitted');
      await fetchDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.detail || 'Unable to submit assignment');
    }
  };

  if (!user) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-yellow-400 mb-4">📊 Access Denied</h2>
          <p className="text-gray-300 mb-6">Please login to view your dashboard</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  const tabs = isTeacher
    ? [
        { id: 'dashboard', label: 'Dashboard' },
        { id: 'students', label: 'My Students' },
        { id: 'attendance-management', label: 'Attendance' },
        { id: 'classes', label: 'Classes' },
        { id: 'meetings', label: 'Meetings' },
        { id: 'courses', label: 'Courses' },
        { id: 'lectures', label: 'Upload Lectures' },
        { id: 'assignments', label: 'Assignments' },
        { id: 'analytics', label: 'Analytics' },
        { id: 'clock', label: 'Clock In/Out' },
        { id: 'notifications', label: 'Notifications' },
        { id: 'profile', label: 'Profile' },
      ]
    : [
        { id: 'dashboard', label: 'Dashboard' },
        { id: 'browse-teachers', label: 'Browse Teachers' },
        { id: 'browse-courses', label: 'Browse Courses' },
        { id: 'my-teacher', label: 'My Teacher' },
        { id: 'my-courses', label: 'My Courses' },
        { id: 'applications', label: 'My Applications' },
        { id: 'attendance', label: 'Attendance' },
        { id: 'class-schedule', label: 'Class Schedule' },
        { id: 'assignments', label: 'Assignments' },
        { id: 'progress', label: 'Progress Tracking' },
        { id: 'certificates', label: 'Certificates' },
        { id: 'notifications', label: 'Notifications' },
        { id: 'profile', label: 'Profile' },
      ];

  const activeStudents = dashboardData.stats?.active_students || 0;
  const teacherClock = dashboardData.clock || {};
  const attendance = dashboardData.attendance || {};
  const studentAssignments = dashboardData.assignments || [];
  const learningProgress = dashboardData.learning_progress || [];
  const browseTeacherCategories = [...new Set((teacherDirectory || []).flatMap((teacher) => teacher.categories || []))];
  const browseTeacherCourses = [...new Set((teacherDirectory || []).flatMap((teacher) => teacher.courses || []).map((course) => course.id).filter(Boolean))];
  const browseTeacherCourseMap = Object.fromEntries((browseCourses || []).map((course) => [course.id, course.title]));
  const filteredTeachers = (teacherDirectory || []).filter((teacher) => {
    const searchValue = browseTeacherSearch.trim().toLowerCase();
    const matchesSearch = !searchValue || [
      teacher.name,
      teacher.bio,
      ...(teacher.expertise || []),
      ...(teacher.courses || []).map((item) => item.title),
    ].filter(Boolean).join(' ').toLowerCase().includes(searchValue);
    const matchesCategory = !browseTeacherCategory || (teacher.categories || []).includes(browseTeacherCategory);
    const matchesCourse = !browseTeacherCourse || (teacher.courses || []).some((item) => item.id === browseTeacherCourse);
    return matchesSearch && matchesCategory && matchesCourse;
  });
  const browseCoursesList = Array.from(new Map((browseCourses || []).map((course) => [course.id, course])).values());
  const teacherStudentsFiltered = (dashboardData.students || []).filter((student) => {
    const searchValue = studentSearch.trim().toLowerCase();
    return !searchValue || [student.name, student.email, student.id].filter(Boolean).join(' ').toLowerCase().includes(searchValue);
  });
  const teacherClassesFiltered = (dashboardData.class_sessions || []).filter((classItem) => {
    const searchValue = classSearch.trim().toLowerCase();
    return !searchValue || [classItem.name, classItem.course_title, classItem.date, classItem.status].filter(Boolean).join(' ').toLowerCase().includes(searchValue);
  });
  const teacherMeetingsFiltered = (dashboardData.meetings || []).filter((meeting) => {
    const searchValue = meetingSearch.trim().toLowerCase();
    return !searchValue || [meeting.title, meeting.provider, meeting.date, meeting.status].filter(Boolean).join(' ').toLowerCase().includes(searchValue);
  });
  const teacherAssignmentsFiltered = (dashboardData.teacher_assignments || []).filter((assignment) => {
    const searchValue = assignmentSearch.trim().toLowerCase();
    return !searchValue || [assignment.title, assignment.description, assignment.course_title, assignment.status, assignment.due_date].filter(Boolean).join(' ').toLowerCase().includes(searchValue);
  });
  const studentAssignmentsFiltered = (studentAssignments || []).filter((assignment) => {
    const searchValue = studentAssignmentSearch.trim().toLowerCase();
    return !searchValue || [assignment.title, assignment.description, assignment.course_title, assignment.class_name, assignment.due_date, assignment.status].filter(Boolean).join(' ').toLowerCase().includes(searchValue);
  });
  const studentScheduleClassesFiltered = (dashboardData.upcoming_classes || []).filter((classItem) => {
    const searchValue = scheduleSearch.trim().toLowerCase();
    return !searchValue || [classItem.name, classItem.course_title, classItem.teacher_name, classItem.date, classItem.status].filter(Boolean).join(' ').toLowerCase().includes(searchValue);
  });
  const studentScheduleMeetingsFiltered = (dashboardData.upcoming_meetings || []).filter((meeting) => {
    const searchValue = scheduleSearch.trim().toLowerCase();
    return !searchValue || [meeting.title, meeting.date, meeting.provider, meeting.status].filter(Boolean).join(' ').toLowerCase().includes(searchValue);
  });
  const myCoursesFiltered = (dashboardData.enrolled_courses || []).filter((course) => {
    const searchValue = myCoursesSearch.trim().toLowerCase();
    return !searchValue || [course.title, course.description, course.teacher_name, course.instructor, course.category].filter(Boolean).join(' ').toLowerCase().includes(searchValue);
  });
  const myApplicationsFiltered = (dashboardData.enrollment_requests || []).filter((item) => {
    const searchValue = myCoursesSearch.trim().toLowerCase();
    return !searchValue || [item.course_title, item.program_label, item.status, item.review_reason, item.student_email].filter(Boolean).join(' ').toLowerCase().includes(searchValue);
  });
  const attendanceStudentsFiltered = (attendanceSession?.students || []).filter((student) => {
    const searchValue = attendanceStudentSearch.trim().toLowerCase();
    return !searchValue || [student.student_name, student.student_id, student.status].filter(Boolean).join(' ').toLowerCase().includes(searchValue);
  });
  const attendanceHistoryFiltered = (attendanceSession?.history || []).filter((row) => {
    const searchValue = attendanceHistorySearch.trim().toLowerCase();
    return !searchValue || [row.date, row.status, row.class_name, row.course_title, row.student_id].filter(Boolean).join(' ').toLowerCase().includes(searchValue);
  });

  const stats = dashboardData.stats || {};
  const roleTitle = isTeacher ? 'Teacher Dashboard' : 'Student Dashboard';
  const roleSubtitle = isTeacher
    ? 'Course management, student performance, and analytics in one place.'
    : 'Your courses, assignments, saved items, and notifications in one place.';

  const renderCardGrid = (items, emptyTitle, emptyMessage, renderItem) => {
    if (!items || items.length === 0) {
      return (
        <div className="text-center py-12 bg-gray-900 rounded-2xl border border-yellow-400/20">
          <div className="text-5xl mb-4">{isTeacher ? '🧑‍🏫' : '📚'}</div>
          <h3 className="text-xl font-bold text-yellow-400 mb-2">{emptyTitle}</h3>
          <p className="text-gray-400">{emptyMessage}</p>
        </div>
      );
    }

    return <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">{items.map(renderItem)}</div>;
  };

  const handleCreateCourse = async () => {
    if (!courseForm.title || !courseForm.description) {
      setCourseStatus('Title and description are required');
      return;
    }

    try {
      let parsedAttachments = [];
      if (courseForm.attachments_text.trim()) {
        parsedAttachments = JSON.parse(courseForm.attachments_text);
      }

      const learningObjectives = courseForm.learning_objectives_text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

      setCreatingCourse(true);
      setCourseStatus('Creating course...');
      await axios.post(`${API}/courses`, {
        title: courseForm.title,
        description: courseForm.description,
        category: courseForm.category,
        duration: courseForm.duration,
        level: courseForm.level,
        price: Number(courseForm.price || 0),
        is_free: Number(courseForm.price || 0) === 0,
        instructor_bio: courseForm.instructor_bio,
        thumbnail_url: courseForm.thumbnail_url || null,
        learning_objectives: learningObjectives,
        attachments: parsedAttachments,
        status: courseForm.status,
      });
      setCourseStatus('Course created successfully');
      setCourseForm({
        title: '',
        description: '',
        category: 'software',
        duration: 'Approximately 4 Months',
        level: 'Beginner',
        price: '0',
        instructor_bio: '',
        thumbnail_url: '',
        learning_objectives_text: '',
        attachments_text: '',
        status: 'draft',
      });
      await fetchDashboard();
    } catch (error) {
      if (error instanceof SyntaxError) {
        setCourseStatus('Attachments JSON is invalid');
      } else {
        setCourseStatus(error.response?.data?.detail || 'Unable to create course');
      }
    } finally {
      setCreatingCourse(false);
    }
  };

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <AnnouncementBanner placement={isTeacher ? 'teacher-dashboard' : 'student-dashboard'} />
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-900 border border-yellow-400/20 text-yellow-400 text-sm mb-4 capitalize">
            {userRole}
          </div>
          <h1 className="text-4xl font-bold text-yellow-400 mb-3">{roleTitle}</h1>
          <p className="text-xl text-gray-300">Welcome back, {user.firstname || user.name}!</p>
          <p className="text-gray-400 mt-2 max-w-3xl">{roleSubtitle}</p>
        </div>

        {!isTeacher && activeTab === 'dashboard' && (
          <section className="mb-10">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="text-2xl font-bold text-yellow-400">My Enrolled Courses</h2>
            </div>
            {(dashboardData.enrolled_courses || []).length === 0 ? (
              <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-10 text-center">
                <p className="text-gray-300 mb-4">You are not enrolled in any courses yet.</p>
                <button onClick={() => setActiveTab('browse-courses')} className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-3 rounded-lg font-bold">Browse Courses</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {(dashboardData.enrolled_courses || []).map((course) => {
                  const progress = course.progress_percent ?? (learningProgress.find((item) => item.course_id === course.id)?.progress_percent || 0);
                  const nextClass = (dashboardData.upcoming_classes || [])
                    .filter((item) => item.course_id === course.id)
                    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))[0];
                  return (
                    <div
                      key={course.id}
                      onClick={() => navigate(`/student/my-courses/${course.id}`)}
                      className="cursor-pointer bg-gradient-to-b from-gray-900 to-black border border-yellow-400/20 hover:border-yellow-400 rounded-2xl overflow-hidden transition-all hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(250,204,21,0.15)]"
                    >
                      <div className="h-36 bg-gray-800">
                        {course.thumbnail_url ? (
                          <img src={resolveImageUrl(course.thumbnail_url)} alt={course.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-4xl">
                            {course.category === 'software' ? '💻' : course.category === 'ai' ? '🤖' : course.category === 'web' ? '🌐' : '📘'}
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs uppercase tracking-wide px-2 py-1 rounded-full bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 capitalize">{course.category || 'General'}</span>
                          <span className="text-xs bg-green-900 text-green-200 px-2 py-1 rounded-full font-bold capitalize">{String(course.enrollment_status || 'enrolled').replaceAll('_', ' ')}</span>
                        </div>
                        <h3 className="text-lg font-bold text-white mb-1">{course.title}</h3>
                        <p className="text-gray-400 text-xs mb-1">Teacher: {course.teacher_name || course.instructor || 'K.R.A Instructor Team'}</p>
                        <p className="text-gray-400 text-xs mb-3">Duration: {course.duration || 'Approximately 4 Months'}</p>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-[11px] px-2 py-1 rounded-full border border-blue-400/30 text-blue-300 bg-blue-900/30 capitalize">Payment: {String(course.payment_status || 'unknown').replaceAll('_', ' ')}</span>
                          <span className="text-[11px] px-2 py-1 rounded-full border border-gray-600 text-gray-300">Last activity: {course.last_activity_label || 'No activity yet'}</span>
                        </div>
                        <div className="w-full bg-gray-800 rounded-full h-2 mb-2">
                          <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 h-2 rounded-full" style={{ width: `${progress}%` }}></div>
                        </div>
                        <p className="text-gray-400 text-xs mb-4">Progress: {progress}% {nextClass ? `• Next Class: ${nextClass.date}` : (course.next_class_date ? `• Next Class: ${course.next_class_date}` : '')}</p>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/student/my-courses/${course.id}`); }}
                          className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black py-2 px-4 rounded-lg font-medium transition-all"
                        >
                          Continue Learning
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
          <div className="bg-gray-900 border border-yellow-400/25 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-2">{isTeacher ? 'Courses' : 'Enrolled Courses'}</div>
            <div className="text-3xl font-bold text-yellow-400">{isTeacher ? (stats.course_count || 0) : (stats.enrolled_count || dashboardData.enrolled_courses?.length || 0)}</div>
          </div>
          <div className="bg-gray-900 border border-yellow-400/25 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-2">{isTeacher ? 'Students' : 'Attendance %'}</div>
            <div className="text-3xl font-bold text-yellow-400">{isTeacher ? (stats.student_count || 0) : (stats.attendance_percent || 0)}</div>
          </div>
          <div className="bg-gray-900 border border-yellow-400/25 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-2">{isTeacher ? 'Attendance Rate' : 'Assignments'}</div>
            <div className="text-3xl font-bold text-yellow-400">{isTeacher ? (stats.attendance_rate || 0) : (stats.assignment_count || 0)}</div>
          </div>
          <div className="bg-gray-900 border border-yellow-400/25 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-2">{isTeacher ? 'Hours (Week)' : 'Teacher Status'}</div>
            <div className="text-3xl font-bold text-yellow-400 capitalize">{isTeacher ? (stats.hours_week || 0) : (dashboardData.assigned_teacher ? 'Assigned' : 'Pending')}</div>
          </div>
        </div>

        {actionMessage && <div className="mb-6 bg-gray-900 border border-yellow-400/30 rounded-xl p-3 text-yellow-300">{actionMessage}</div>}

        <div className="flex flex-wrap gap-3 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full border transition-all ${
                activeTab === tab.id
                  ? 'bg-yellow-500 text-black border-yellow-400'
                  : 'bg-gray-900 text-gray-300 border-gray-700 hover:border-yellow-400 hover:text-yellow-400'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {!isTeacher && activeTab === 'my-teacher' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">My Teacher</h2>
            {dashboardData.assigned_teacher ? (
              <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-yellow-400">{dashboardData.assigned_teacher.name}</h3>
                <p className="text-gray-300">{dashboardData.assigned_teacher.email}</p>
                <p className="text-gray-400 mt-2">{dashboardData.assigned_teacher.bio || 'Teacher profile available.'}</p>
              </div>
            ) : (
              <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 space-y-4">
                <p className="text-gray-300">Select a teacher to request enrollment under them.</p>
                <select value={teacherRequest.teacher_id} onChange={(e) => setTeacherRequest({ ...teacherRequest, teacher_id: e.target.value })} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none">
                  <option value="">Select teacher</option>
                  {teacherDirectory.map((teacher) => <option key={teacher.id} value={teacher.id}>{teacher.name}</option>)}
                </select>
                <textarea value={teacherRequest.note} onChange={(e) => setTeacherRequest({ ...teacherRequest, note: e.target.value })} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none min-h-[90px]" placeholder="Optional note to teacher" />
                <button onClick={requestTeacher} className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-bold">Request Teacher</button>
              </div>
            )}
          </section>
        )}

        {!isTeacher && activeTab === 'browse-teachers' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Browse Teachers</h2>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                value={browseTeacherSearch}
                onChange={(e) => setBrowseTeacherSearch(e.target.value)}
                className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                placeholder="Search by name, expertise, or course"
              />
              <select
                value={browseTeacherCategory}
                onChange={(e) => setBrowseTeacherCategory(e.target.value)}
                className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
              >
                <option value="">All categories</option>
                {browseTeacherCategories.map((category) => <option key={category} value={category}>{category}</option>)}
              </select>
              <select
                value={browseTeacherCourse}
                onChange={(e) => setBrowseTeacherCourse(e.target.value)}
                className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
              >
                <option value="">All courses</option>
                {browseTeacherCourses.map((courseId) => <option key={courseId} value={courseId}>{browseTeacherCourseMap[courseId] || courseId}</option>)}
              </select>
            </div>
            {renderCardGrid(filteredTeachers || [], 'No teachers found', 'Try a different search or filter.', (teacher) => (
              <div key={teacher.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <img src={resolveImageUrl(teacher.profile_photo_url) || 'https://api.dicebear.com/7.x/initials/svg?seed=Teacher'} alt={teacher.name} className="h-14 w-14 rounded-full object-cover border border-yellow-400/30" />
                  <div>
                    <h3 className="text-lg font-bold text-yellow-400">{teacher.name}</h3>
                    <p className="text-xs text-gray-400">{teacher.email}</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${teacher.availability_status === 'online' ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-300'}`}>
                      {teacher.availability_status === 'online' ? 'Available' : 'Offline'}
                    </span>
                  </div>
                </div>
                <p className="text-gray-300 text-sm mb-2">{teacher.bio || 'Teacher profile available.'}</p>
                <p className="text-gray-400 text-xs mb-1">Expertise: {(teacher.expertise || []).slice(0, 4).join(', ') || 'General'}</p>
                <p className="text-gray-400 text-xs mb-1">Courses: {teacher.courses_count || 0} • Students: {teacher.students_count || 0}</p>
                <p className="text-gray-400 text-xs mb-3">Active Classes: {teacher.active_classes_count || 0}</p>
                <div className="flex gap-2">
                  <button onClick={() => setTeacherRequest({ ...teacherRequest, teacher_id: teacher.id })} className="flex-1 border border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black px-3 py-2 rounded-lg text-sm">Select</button>
                  <button onClick={() => requestTeacherById(teacher.id)} className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black px-3 py-2 rounded-lg text-sm font-bold">Request</button>
                </div>
              </div>
            ))}
          </section>
        )}

        {!isTeacher && activeTab === 'browse-courses' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Browse Courses</h2>
            {renderCardGrid(browseCoursesList || [], 'No courses found.', 'Courses will appear here once they are published.', (course) => (
              <div key={course.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                {course.thumbnail_url && <img src={resolveImageUrl(course.thumbnail_url)} alt={course.title} className="w-full h-36 object-cover rounded-lg border border-yellow-400/20 mb-3" />}
                <h3 className="text-lg font-bold text-yellow-400 mb-1">{course.title}</h3>
                <p className="text-gray-400 text-xs mb-1">Teacher: {course.teacher_name || course.instructor || 'K.R.A Instructor Team'}</p>
                <p className="text-gray-400 text-xs mb-1">Category: {course.category || 'General'} • Lessons: {course.total_lessons || course.lessons?.length || 0}</p>
                <p className="text-gray-400 text-xs mb-1">Duration: {formatCourseDuration(course.duration)}</p>
                <p className="text-gray-400 text-xs mb-1">Pricing: Monthly application billing (official class-based rates apply)</p>
                {course.featured && <p className="text-purple-300 text-xs mb-1 font-bold uppercase">Flagship Program</p>}
                <p className="text-gray-400 text-xs mb-2">Status: {course.status || (course.published ? 'published' : 'draft')}</p>
                <p className="text-gray-300 text-sm mb-3 line-clamp-3">{course.description}</p>
                <button
                  onClick={() => requestCourseEnrollment(course.id, course.teacher_id || null)}
                  disabled={courseRequestingId === course.id}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-bold disabled:opacity-50"
                >
                  {courseRequestingId === course.id ? 'Sending Request...' : 'Request Enrollment'}
                </button>
              </div>
            ))}
          </section>
        )}

        {!isTeacher && activeTab === 'my-courses' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">My Courses</h2>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-4">
              <input
                value={myCoursesSearch}
                onChange={(e) => setMyCoursesSearch(e.target.value)}
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                placeholder="Search my courses by title, description, teacher, or category"
              />
            </div>
            {renderCardGrid(myCoursesFiltered || [], 'No enrolled courses', 'Enroll in courses to track progress and continue learning.', (course) => {
              const progress = course.progress_percent ?? (learningProgress.find((item) => item.course_id === course.id)?.progress_percent || 0);
              const nextClass = (dashboardData.upcoming_classes || [])
                .filter((item) => item.course_id === course.id)
                .sort((a, b) => (a.date || '').localeCompare(b.date || ''))[0];
              return (
                <div key={course.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                  {course.thumbnail_url && <img src={resolveImageUrl(course.thumbnail_url)} alt={course.title} className="w-full h-40 object-cover rounded-xl border border-yellow-400/20 mb-4" />}
                  <h3 className="text-xl font-bold text-yellow-400 mb-2">{course.title}</h3>
                  <p className="text-gray-400 text-xs mb-1">Teacher: {course.instructor_name || course.teacher_name || course.instructor || 'K.R.A Instructor Team'}</p>
                  <p className="text-gray-400 text-xs mb-1">Enrollment: <span className="capitalize">{String(course.enrollment_status || 'active').replaceAll('_', ' ')}</span> • Payment: <span className="capitalize">{String(course.payment_status || 'unknown').replaceAll('_', ' ')}</span></p>
                  <p className="text-gray-400 text-xs mb-2">Category: {course.course_category || course.category || 'General'} • Duration: {course.course_duration || course.duration || 'Approximately 4 Months'}</p>
                  <p className="text-gray-400 text-sm mb-3">{course.description}</p>
                  <p className="text-gray-400 text-xs mb-1">Class: {nextClass?.name || 'No scheduled class yet'}</p>
                  <p className="text-gray-400 text-xs mb-1">Next Class Date: {nextClass?.date || course.next_class_date || 'Not scheduled'}</p>
                  <p className="text-gray-400 text-xs mb-3">Next Lesson: {course.modules?.[0]?.lessons?.[0]?.title || course.lessons?.[0] || 'Course intro'}</p>
                  <div className="w-full bg-gray-800 rounded-full h-2 mb-3"><div className="bg-yellow-500 h-2 rounded-full" style={{ width: `${progress}%` }}></div></div>
                  <div className="text-sm text-gray-300 mb-3">Progress: {progress}% • Last Activity: {course.last_activity_label || 'No activity yet'}</div>
                  <button
                    onClick={() => navigate(`/student/my-courses/${course.id}`)}
                    className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black py-2 px-4 rounded-lg font-medium transition-all"
                  >
                    Continue Learning
                  </button>
                </div>
              );
            })}
          </section>
        )}

        {!isTeacher && activeTab === 'applications' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">My Applications</h2>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-4">
              <input
                value={myCoursesSearch}
                onChange={(e) => setMyCoursesSearch(e.target.value)}
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                placeholder="Search applications by course, status, or program"
              />
            </div>
            {renderCardGrid(myApplicationsFiltered || [], 'No applications found', 'Your enrollment applications will appear here after payment and submission.', (item) => (
              <div key={item.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-yellow-400 mb-2">{item.course_title || 'Course Application'}</h3>
                <p className="text-gray-300 text-sm mb-1">Program: {item.program_label || item.program_type || 'N/A'}</p>
                <p className="text-gray-300 text-sm mb-1 capitalize">Status: {String(item.status || 'pending_review').replaceAll('_', ' ')}</p>
                <p className="text-gray-400 text-xs mb-1">Submitted: {item.created_at ? new Date(item.created_at).toLocaleString() : 'recently'}</p>
                {item.review_reason && <p className="text-gray-300 text-xs mt-2">Admin Comment: {item.review_reason}</p>}
                {item.payment_amount ? <p className="text-gray-400 text-xs mt-1">Payment: ${Number(item.payment_amount || 0).toFixed(2)} CAD • {item.payment_status || 'paid'}</p> : null}
              </div>
            ))}
          </section>
        )}

        {!isTeacher && activeTab === 'attendance' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Attendance</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-xs text-gray-400">Attendance</div><div className="text-2xl font-bold text-yellow-400">{attendance.attendance_percent || 0}%</div></div>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-xs text-gray-400">Present</div><div className="text-2xl font-bold text-green-400">{attendance.present_count || 0}</div></div>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-xs text-gray-400">Late</div><div className="text-2xl font-bold text-yellow-300">{attendance.late_count || 0}</div></div>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-xs text-gray-400">Absent</div><div className="text-2xl font-bold text-red-400">{attendance.absent_count || 0}</div></div>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-xs text-gray-400">Excused</div><div className="text-2xl font-bold text-blue-300">{attendance.excused_count || 0}</div></div>
            </div>
            {renderCardGrid(attendance.history || [], 'No attendance records', 'Your class attendance records will appear here.', (item) => (
              <div key={item.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-yellow-400 mb-2">{item.date}</h3>
                <p className="text-gray-400 text-xs mb-1">Course: {item.course_title || 'Unlinked course'}</p>
                <p className="text-gray-400 text-xs mb-1">Class: {item.class_name || 'N/A'}</p>
                <p className="text-gray-300 capitalize">Status: {item.status}</p>
              </div>
            ))}
          </section>
        )}

        {!isTeacher && activeTab === 'class-schedule' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Class Schedule</h2>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-4">
              <input
                value={scheduleSearch}
                onChange={(e) => setScheduleSearch(e.target.value)}
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                placeholder="Search classes and meetings"
              />
            </div>
            {renderCardGrid(studentScheduleClassesFiltered || [], 'No upcoming classes', 'Scheduled classes will appear here.', (classItem) => (
              <div key={classItem.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-yellow-400 mb-2">{classItem.name}</h3>
                <p className="text-gray-400 text-xs mb-1">Course: {classItem.course_title || 'Unlinked course'}</p>
                <p className="text-gray-400 text-xs mb-1">Teacher: {classItem.teacher_name || 'Assigned teacher'}</p>
                <p className="text-gray-400 text-xs mb-1 capitalize">Status: {classItem.status || 'scheduled'}</p>
                <p className="text-gray-300 text-sm">{classItem.date} • {classItem.start_time} - {classItem.end_time}</p>
                {classItem.meeting_link && <a href={classItem.meeting_link} target="_blank" rel="noreferrer" className="text-yellow-400 text-sm mt-2 inline-block">Join Class</a>}
              </div>
            ))}
            <h3 className="text-xl font-bold text-yellow-400">Upcoming Meetings</h3>
            {renderCardGrid(studentScheduleMeetingsFiltered || [], 'No upcoming meetings', 'Meeting links will appear here.', (meeting) => (
              <div key={meeting.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <h4 className="text-lg font-bold text-yellow-400 mb-2">{meeting.title}</h4>
                <p className="text-gray-300 text-sm mb-2">{meeting.date} • {meeting.start_time}</p>
                <a href={meeting.meeting_url} target="_blank" rel="noreferrer" className="text-yellow-400 text-sm">Join Meeting</a>
              </div>
            ))}
          </section>
        )}

        {!isTeacher && activeTab === 'assignments' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Assignments</h2>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-4">
              <input
                value={studentAssignmentSearch}
                onChange={(e) => setStudentAssignmentSearch(e.target.value)}
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                placeholder="Search assignments by title, course, class, due date, or status"
              />
            </div>
            {renderCardGrid(studentAssignmentsFiltered || [], 'No assignments yet', 'Assignments will appear here when your courses publish them.', (assignment) => (
              <div key={assignment.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <div className="text-sm text-gray-400 mb-2">{assignment.course_title}</div>
                <h3 className="text-lg font-bold text-yellow-400 mb-2">{assignment.title}</h3>
                <p className="text-gray-400 text-xs mb-1">Teacher: {assignment.teacher_name || 'Assigned teacher'}</p>
                <p className="text-gray-400 text-xs mb-1">Class: {assignment.class_name || 'General course assignment'}</p>
                <p className="text-gray-300 text-sm mb-4">{assignment.due_text}</p>
                <span className="inline-flex px-3 py-1 rounded-full text-xs bg-yellow-800 text-yellow-100">{assignment.status}</span>
                {assignment.submission?.grade !== undefined && assignment.submission?.grade !== null && (
                  <p className="text-green-300 text-xs mt-2">Grade: {assignment.submission.grade}</p>
                )}
                {assignment.submission?.feedback && (
                  <p className="text-gray-300 text-xs mt-1">Feedback: {assignment.submission.feedback}</p>
                )}
                {!assignment.submitted && (
                  <button onClick={() => submitAssignment(assignment.id)} className="block mt-3 text-sm text-yellow-400 hover:text-yellow-300">Submit Assignment</button>
                )}
              </div>
            ))}
          </section>
        )}

        {!isTeacher && activeTab === 'progress' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Progress Tracking</h2>
            {renderCardGrid(learningProgress || [], 'No progress records yet', 'Progress updates appear as you complete lessons.', (item) => (
              <div key={item.course_id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-yellow-400 mb-2">{item.course_title || 'Course'}</h3>
                <div className="w-full bg-gray-800 rounded-full h-2 mb-3"><div className="bg-yellow-500 h-2 rounded-full" style={{ width: `${item.progress_percent || 0}%` }}></div></div>
                <p className="text-gray-300 text-sm">Progress: {item.progress_percent || 0}%</p>
                <p className="text-gray-400 text-xs">Completed lessons: {item.completed_lessons || 0} / {item.total_lessons_tracked || 0}</p>
              </div>
            ))}
          </section>
        )}

        {!isTeacher && activeTab === 'certificates' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Certificates</h2>
            {renderCardGrid(dashboardData.certificates || [], 'No certificates yet', 'Complete courses to earn certificates.', (certificate) => (
              <div key={certificate.id || certificate.certificate_id || certificate.course_id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <div className="text-2xl mb-4">🏆</div>
                <h3 className="text-xl font-bold text-yellow-400 mb-2">{certificate.title || certificate.program_name || 'K.R.A Certificate'}</h3>
                {certificate.certificate_id && <p className="text-gray-300 text-xs mb-1">Certificate ID: {certificate.certificate_id}</p>}
                {certificate.course_name && <p className="text-gray-400 text-xs mb-1">Course: {certificate.course_name}</p>}
                {certificate.instructor_name && <p className="text-gray-400 text-xs mb-1">Instructor: {certificate.instructor_name}</p>}
                {certificate.instructional_hours && <p className="text-gray-400 text-xs mb-1">Hours: {certificate.instructional_hours}</p>}
                {(certificate.completion_date || certificate.issue_date) && (
                  <p className="text-gray-400 text-xs mb-1">Completion: {certificate.completion_date || 'N/A'} • Issued: {certificate.issue_date || 'N/A'}</p>
                )}
                {certificate.status && <p className="text-gray-400 text-xs mb-2 capitalize">Status: {certificate.status}</p>}
                {certificate.verification_url ? (
                  <a href={certificate.verification_url} target="_blank" rel="noreferrer" className="text-yellow-400 text-sm hover:text-yellow-300">Verify Certificate</a>
                ) : (
                  <p className="text-gray-400 text-sm">Certificate details available in dashboard records.</p>
                )}
              </div>
            ))}
          </section>
        )}

        {!isTeacher && activeTab === 'notifications' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Notifications</h2>
            {renderCardGrid(dashboardData.notifications || [], 'No notifications', 'You are all caught up for now.', (notification) => (
              <div key={notification.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <div className="text-sm text-gray-400 mb-2 capitalize">{notification.type}</div>
                <h3 className="text-lg font-bold text-yellow-400 mb-2">{notification.title}</h3>
                <p className="text-gray-300 text-sm">{notification.message}</p>
              </div>
            ))}
            <div>
              <h3 className="text-xl font-bold text-yellow-400 mb-4">Recent Activity</h3>
              {renderCardGrid(dashboardData.recent_activity || [], 'No recent activity', 'Your activity will appear here as you continue learning.', (activity) => (
                <div key={activity.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                  <div className="text-xs text-gray-500 mb-2">
                    {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : ''}
                    {activity.course_title ? ` • ${activity.course_title}` : ''}
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">{activity.title}</h4>
                  <p className="text-gray-400 text-sm mb-4">{activity.description}</p>
                  <div className="w-full bg-gray-800 rounded-full h-2">
                    <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 h-2 rounded-full" style={{ width: `${activity.progress || 0}%` }}></div>
                  </div>
                  {activity.link?.page && (
                    <button
                      onClick={() => goToPage(activity.link.page, activity.link.params || {})}
                      className="mt-3 text-sm text-yellow-400 hover:text-yellow-300"
                    >
                      Open Activity
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {!isTeacher && activeTab === 'profile' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Profile</h2>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 space-y-2">
              <div className="text-gray-300">Name: {user.firstname || user.name}</div>
              <div className="text-gray-300">Email: {user.email}</div>
              <div className="text-gray-300 capitalize">Role: {userRole}</div>
            </div>
          </section>
        )}

        {isTeacher && activeTab === 'dashboard' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Teacher Dashboard</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-gray-400 text-sm">Active Students</div><div className="text-yellow-400 text-2xl font-bold">{activeStudents}</div></div>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-gray-400 text-sm">Attendance Rate</div><div className="text-yellow-400 text-2xl font-bold">{stats.attendance_rate || 0}%</div></div>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-gray-400 text-sm">Classes</div><div className="text-yellow-400 text-2xl font-bold">{stats.class_count || 0}</div></div>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-gray-400 text-sm">Hours Today</div><div className="text-yellow-400 text-2xl font-bold">{stats.hours_today || 0}</div></div>
            </div>
            {renderCardGrid(dashboardData.teacher_courses || [], 'No teacher courses yet', 'Courses you teach will appear here.', (course) => (
              <div key={course.id} onClick={() => goToPage('teacher-course-detail', { course_id: course.id })} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 cursor-pointer hover:border-yellow-400 transition-all">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-2xl">{course.category === 'software' ? '💻' : course.category === 'ai' ? '🤖' : course.category === 'web' ? '🌐' : '📘'}</span>
                  <span className="text-xs bg-yellow-800 text-yellow-100 px-2 py-1 rounded-full">Managed</span>
                </div>
                <h3 className="text-xl font-bold text-yellow-400 mb-2">{course.title}</h3>
                <p className="text-gray-400 text-sm mb-2">{course.description}</p>
                <p className="text-gray-300 text-sm">{course.instructor}</p>
              </div>
            ))}
          </section>
        )}

        {isTeacher && activeTab === 'students' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">My Students</h2>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-4">
              <input
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                placeholder="Search students by name, email, or id"
              />
            </div>
            {dashboardData.assignment_requests?.length > 0 && (
              <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-yellow-400 mb-4">Pending Teacher Requests</h3>
                <div className="space-y-3">
                  {dashboardData.assignment_requests.map((req) => (
                    <div key={req.student_id} className="bg-gray-800 rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <div className="text-white font-medium">{req.name}</div>
                        <div className="text-gray-400 text-sm">{req.email}</div>
                        {req.note && <div className="text-gray-500 text-xs mt-1">Note: {req.note}</div>}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => approveStudentRequest(req.student_id)} className="bg-yellow-500 hover:bg-yellow-600 text-black px-3 py-2 rounded-lg font-bold text-sm">Approve</button>
                        <button onClick={() => rejectStudentRequest(req.student_id)} className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg font-bold text-sm">Reject</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {dashboardData.course_enrollment_requests?.length > 0 && (
              <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-yellow-400 mb-4">Pending Course Enrollment Requests</h3>
                <div className="space-y-3">
                  {dashboardData.course_enrollment_requests.map((req) => (
                    <div key={req.id} className="bg-gray-800 rounded-xl p-4 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-white font-medium">{req.student_name || req.student_email}</div>
                        <div className="text-gray-400 text-sm">Course: {req.course_title}</div>
                        {req.program_label && <div className="text-gray-400 text-sm">Program: {req.program_label}</div>}
                        {req.registration_profile?.educational_information?.educational_goals && (
                          <div className="text-gray-500 text-xs mt-1">Goal: {req.registration_profile.educational_information.educational_goals}</div>
                        )}
                        <div className="text-gray-500 text-xs">Requested: {req.created_at ? new Date(req.created_at).toLocaleString() : 'recently'}</div>
                      </div>
                      <div className="flex gap-2 flex-wrap justify-end">
                        <button onClick={() => approveCourseEnrollmentRequest(req.id)} className="bg-yellow-500 hover:bg-yellow-600 text-black px-3 py-2 rounded-lg font-bold text-sm">Approve</button>
                        <button onClick={() => waitlistCourseEnrollmentRequest(req.id)} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg font-bold text-sm">Waitlist</button>
                        <button onClick={() => rejectCourseEnrollmentRequest(req.id)} className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg font-bold text-sm">Reject</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {renderCardGrid(teacherStudentsFiltered || [], 'No students yet', 'Students enrolled in your courses will appear here.', (student) => (
              <div key={student.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-yellow-400 mb-2">{student.name}</h3>
                <p className="text-gray-300 text-sm mb-2">{student.email}</p>
                <p className="text-gray-400 text-sm">{student.courses_count} course(s) in your classes</p>
              </div>
            ))}
          </section>
        )}

        {isTeacher && activeTab === 'attendance-management' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Attendance Management</h2>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-xs text-gray-400 mb-2">Class</label>
                  <select
                    value={attendanceClassId}
                    onChange={(e) => {
                      const classId = e.target.value;
                      setAttendanceClassId(classId);
                      const selectedClass = (dashboardData.class_sessions || []).find((item) => item.id === classId);
                      setAttendanceDate(selectedClass?.date || '');
                    }}
                    className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                  >
                    <option value="">Select class</option>
                    {(dashboardData.class_sessions || []).map((classItem) => <option key={classItem.id} value={classItem.id}>{classItem.name} ({classItem.date})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-2">Attendance Date</label>
                  <input
                    type="date"
                    value={attendanceDate}
                    onChange={(e) => setAttendanceDate(e.target.value)}
                    className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                  />
                </div>
                <button
                  onClick={() => loadAttendanceSession(attendanceClassId, attendanceDate)}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-3 rounded-lg font-bold"
                >
                  Load Session
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-xs text-gray-400">Overall Attendance</div><div className="text-2xl font-bold text-yellow-400">{dashboardData.attendance_report?.attendance_rate || 0}%</div></div>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-xs text-gray-400">Records</div><div className="text-2xl font-bold text-yellow-400">{dashboardData.attendance_report?.total_records || 0}</div></div>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-xs text-gray-400">Daily Buckets</div><div className="text-2xl font-bold text-yellow-400">{dashboardData.attendance_report?.daily?.length || 0}</div></div>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-xs text-gray-400">Monthly Buckets</div><div className="text-2xl font-bold text-yellow-400">{dashboardData.attendance_report?.monthly?.length || 0}</div></div>
            </div>

            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 space-y-4">
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <input
                  value={attendanceStudentSearch}
                  onChange={(e) => setAttendanceStudentSearch(e.target.value)}
                  className="flex-1 bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                  placeholder="Search students in this attendance session"
                />
                <label className="inline-flex items-center gap-2 text-sm text-gray-300">
                  <input type="checkbox" checked={closeAttendanceAfterSave} onChange={(e) => setCloseAttendanceAfterSave(e.target.checked)} />
                  Close session after save
                </label>
              </div>

              {attendanceLoading ? (
                <div className="text-gray-400">Loading attendance session...</div>
              ) : !attendanceSession ? (
                <div className="text-gray-400">Select a class and load attendance.</div>
              ) : (
                <>
                  <div className="text-sm text-gray-400">
                    Session: <span className="text-yellow-400">{attendanceSession.session_id || 'N/A'}</span> •
                    Status: <span className="capitalize text-yellow-400">{attendanceSession.attendance_closed ? 'Closed' : 'Open'}</span>
                  </div>
                  <div className="space-y-3">
                    {(attendanceStudentsFiltered || []).map((student) => (
                      <div key={student.student_id} className="flex items-center justify-between bg-gray-800 rounded-xl p-3">
                        <div className="text-gray-200">{student.student_name}</div>
                        <select
                          value={attendanceDraft[`${attendanceClassId}_${student.student_id}`] || student.status || 'present'}
                          disabled={attendanceSession.attendance_closed}
                          onChange={(e) => setAttendanceDraft({ ...attendanceDraft, [`${attendanceClassId}_${student.student_id}`]: e.target.value })}
                          className="bg-gray-700 text-white px-3 py-1 rounded border border-gray-600"
                        >
                          <option value="present">Present</option>
                          <option value="late">Late</option>
                          <option value="absent">Absent</option>
                          <option value="excused">Excused</option>
                        </select>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button onClick={() => {
                      const next = { ...attendanceDraft };
                      (attendanceSession.students || []).forEach((student) => {
                        next[`${attendanceClassId}_${student.student_id}`] = 'present';
                      });
                      setAttendanceDraft(next);
                    }} className="border border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black px-4 py-2 rounded-lg text-sm">
                      Mark All Present
                    </button>
                    <button onClick={() => saveManagedAttendance(closeAttendanceAfterSave)} disabled={attendanceSession.attendance_closed} className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-bold disabled:opacity-50">
                      Save Attendance
                    </button>
                    <button onClick={reopenAttendance} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-bold">
                      Reopen Session
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 space-y-4">
              <h3 className="text-xl font-bold text-yellow-400">Attendance History</h3>
              <input
                value={attendanceHistorySearch}
                onChange={(e) => setAttendanceHistorySearch(e.target.value)}
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                placeholder="Search history by date, status, class, course, or student"
              />
              {renderCardGrid(attendanceHistoryFiltered || [], 'No attendance history', 'Attendance history appears after sessions are saved.', (item) => (
                <div key={item.id} className="bg-gray-800 border border-yellow-400/10 rounded-xl p-4">
                  <div className="text-yellow-400 font-semibold mb-1">{item.date}</div>
                  <div className="text-gray-300 text-sm">Status: <span className="capitalize">{item.status}</span></div>
                  <div className="text-gray-400 text-xs">Class: {item.class_name || 'N/A'} • Course: {item.course_title || 'N/A'}</div>
                  <div className="text-gray-500 text-xs">Student: {item.student_id}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {isTeacher && activeTab === 'classes' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Classes</h2>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 space-y-4">
              <h3 className="text-xl font-bold text-yellow-400">Create Class</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input value={classForm.name} onChange={(e) => setClassForm({ ...classForm, name: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none" placeholder="Class name" />
                <select value={classForm.course_id} onChange={(e) => setClassForm({ ...classForm, course_id: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none">
                  <option value="">Select linked course (required)</option>
                  {(dashboardData.teacher_courses || []).map((course) => <option key={course.id} value={course.id}>{course.title}</option>)}
                </select>
                <input type="date" value={classForm.date} onChange={(e) => setClassForm({ ...classForm, date: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none" />
                <input type="time" value={classForm.start_time} onChange={(e) => setClassForm({ ...classForm, start_time: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none" />
                <input type="time" value={classForm.end_time} onChange={(e) => setClassForm({ ...classForm, end_time: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none" />
                <input value={classForm.meeting_link} onChange={(e) => setClassForm({ ...classForm, meeting_link: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none" placeholder="Meeting link" />
              </div>
              <button onClick={createClass} className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-bold">Create Class</button>
            </div>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-4">
              <input
                value={classSearch}
                onChange={(e) => setClassSearch(e.target.value)}
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                placeholder="Search classes by name, course, date, or status"
              />
            </div>
            {renderCardGrid(teacherClassesFiltered || [], 'No classes yet', 'Create your first class session.', (classItem) => (
              <div key={classItem.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-yellow-400 mb-2">{classItem.name}</h3>
                <p className="text-gray-400 text-xs mb-1">Course: {classItem.course_title || 'Unlinked course'}</p>
                <p className="text-gray-300 text-sm mb-2">{classItem.date} • {classItem.start_time} - {classItem.end_time}</p>
                <p className="text-gray-400 text-sm mb-1">Students: {(classItem.student_ids || []).length}</p>
                <p className="text-gray-400 text-sm mb-3 capitalize">Status: {classItem.status || 'scheduled'}</p>
                <div className="flex gap-3">
                  <button onClick={() => cancelClass(classItem.id)} className="text-red-400 hover:text-red-300 text-sm">Cancel Class</button>
                </div>
              </div>
            ))}
          </section>
        )}

        {isTeacher && activeTab === 'meetings' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Meetings</h2>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 space-y-4">
              <h3 className="text-xl font-bold text-yellow-400">Schedule Meeting</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input value={meetingForm.title} onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none" placeholder="Meeting title" />
                <select value={meetingForm.class_id} onChange={(e) => setMeetingForm({ ...meetingForm, class_id: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none">
                  <option value="">Select class (optional)</option>
                  {(dashboardData.class_sessions || []).map((classItem) => <option key={classItem.id} value={classItem.id}>{classItem.name}</option>)}
                </select>
                <select value={meetingForm.provider} onChange={(e) => setMeetingForm({ ...meetingForm, provider: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none">
                  <option value="google_meet">Google Meet</option>
                  <option value="zoom">Zoom</option>
                  <option value="microsoft_teams">Microsoft Teams</option>
                  <option value="other">Other</option>
                </select>
                <input value={meetingForm.meeting_url} onChange={(e) => setMeetingForm({ ...meetingForm, meeting_url: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none" placeholder="Meeting URL" />
                <input type="date" value={meetingForm.date} onChange={(e) => setMeetingForm({ ...meetingForm, date: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none" />
                <input type="time" value={meetingForm.start_time} onChange={(e) => setMeetingForm({ ...meetingForm, start_time: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none" />
                <input type="time" value={meetingForm.end_time} onChange={(e) => setMeetingForm({ ...meetingForm, end_time: e.target.value })} className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none" />
              </div>
              <button onClick={createMeeting} className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-bold">Create Meeting</button>
            </div>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-4">
              <input
                value={meetingSearch}
                onChange={(e) => setMeetingSearch(e.target.value)}
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                placeholder="Search meetings by title, provider, date, or status"
              />
            </div>
            {renderCardGrid(teacherMeetingsFiltered || [], 'No meetings yet', 'Schedule online meetings for your classes.', (meeting) => (
              <div key={meeting.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-yellow-400 mb-2">{meeting.title}</h3>
                <p className="text-gray-300 text-sm mb-1">{meeting.date} • {meeting.start_time} - {meeting.end_time}</p>
                <a href={meeting.meeting_url} target="_blank" rel="noreferrer" className="text-yellow-400 text-sm">Open Meeting Link</a>
                <button onClick={() => cancelMeeting(meeting.id)} className="block mt-3 text-red-400 hover:text-red-300 text-sm">Cancel Meeting</button>
              </div>
            ))}
          </section>
        )}

        {isTeacher && activeTab === 'analytics' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Analytics</h2>
            {renderCardGrid(dashboardData.course_analytics || [], 'No analytics yet', 'Analytics populate when students join your courses.', (course) => (
              <div key={course.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-yellow-400 mb-2">{course.title}</h3>
                <p className="text-gray-300 text-sm mb-1">Students: {course.enrolled_students}</p>
                <p className="text-gray-300 text-sm mb-1">Lessons: {course.lessons}</p>
                <p className="text-gray-300 text-sm">Rating: {course.rating}</p>
              </div>
            ))}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-gray-400 text-sm">Attendance Rate</div><div className="text-2xl text-yellow-400 font-bold">{stats.attendance_rate || 0}%</div></div>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-gray-400 text-sm">Active Students</div><div className="text-2xl text-yellow-400 font-bold">{stats.active_students || 0}</div></div>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-gray-400 text-sm">Hours Worked (Month)</div><div className="text-2xl text-yellow-400 font-bold">{stats.hours_month || 0}</div></div>
            </div>
          </section>
        )}

        {isTeacher && activeTab === 'courses' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Assigned Courses</h2>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
              <p className="text-gray-300">Teachers can now only work with courses assigned by K.R.A administrators.</p>
            </div>
            {renderCardGrid(dashboardData.teacher_courses || [], 'No assigned courses yet', 'Courses assigned by K.R.A administrators will appear here.', (course) => (
              <div key={course.id} onClick={() => goToPage('teacher-course-detail', { course_id: course.id })} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 cursor-pointer hover:border-yellow-400 transition-all">
                <h3 className="text-lg font-bold text-yellow-400 mb-2">{course.title}</h3>
                <p className="text-gray-300 text-sm mb-2">{course.description}</p>
                <p className="text-gray-400 text-xs mb-2">Status: {course.status || (course.published ? 'published' : 'draft')}</p>
                <p className="text-gray-400 text-xs mb-2">Modules: {(course.modules || []).length} • Objectives: {(course.learning_objectives || []).length}</p>
                <div className="text-sm text-yellow-400 mt-3 font-medium">Open Course Management →</div>
              </div>
            ))}
          </section>
        )}

        {isTeacher && activeTab === 'course-detail' && (
          courseDetailId ? (
            <TeacherCourseManagement
              courseId={courseDetailId}
              courseTitle={(dashboardData.teacher_courses || []).find((c) => c.id === courseDetailId)?.title}
              initialSubTab={courseDetailSubTab}
              highlightParams={courseDetailHighlights}
              onBack={() => goToPage('teacher-courses')}
              onOpenGlobalTab={(tabId) => goToPage('teacher-dashboard', { tab: tabId })}
            />
          ) : (
            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-yellow-400">Course Management</h2>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <p className="text-gray-300">No course selected. Choose a course from the Courses tab.</p>
                <button onClick={() => goToPage('teacher-courses')} className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-bold">Back to Courses</button>
              </div>
            </section>
          )
        )}

        {isTeacher && activeTab === 'lectures' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Upload Lectures</h2>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 space-y-4">
              <select value={lectureForm.course_id} onChange={(e) => setLectureForm({ ...lectureForm, course_id: e.target.value })} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none">
                <option value="">Select course</option>
                {(dashboardData.teacher_courses || []).map((course) => <option key={course.id} value={course.id}>{course.title}</option>)}
              </select>
              <input value={lectureForm.title} onChange={(e) => setLectureForm({ ...lectureForm, title: e.target.value })} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none" placeholder="Lecture title" />
              <select value={lectureForm.resource_type} onChange={(e) => setLectureForm({ ...lectureForm, resource_type: e.target.value })} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none">
                <option value="video">Video</option>
                <option value="pdf">PDF</option>
                <option value="document">Document</option>
                <option value="assignment">Assignment Resource</option>
                <option value="external_link">External Link</option>
              </select>
              <input value={lectureForm.resource_url} onChange={(e) => setLectureForm({ ...lectureForm, resource_url: e.target.value })} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none" placeholder="YouTube/Vimeo/Drive URL (optional if uploading file)" />
              <input
                key={lectureFileInputKey}
                type="file"
                onChange={(e) => setLectureUploadFile(e.target.files?.[0] || null)}
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                accept="video/*,audio/*,image/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt"
              />
              <p className="text-xs text-gray-400">Choose either a URL or a file upload. Max upload size: 50MB.</p>
              <textarea value={lectureForm.description} onChange={(e) => setLectureForm({ ...lectureForm, description: e.target.value })} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none min-h-[90px]" placeholder="Description" />
              <button onClick={createLecture} className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-bold">Save Lecture</button>
            </div>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-4">
              <input
                value={lectureSearch}
                onChange={(e) => setLectureSearch(e.target.value)}
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                placeholder="Search lectures by title, description, type, module, or URL"
              />
            </div>
            {renderCardGrid(lectureResults || [], 'No lectures found', 'Lecture results appear after upload or search.', (lecture) => (
              <div key={lecture.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-yellow-400 mb-2">{lecture.title}</h3>
                <p className="text-gray-400 text-xs mb-1">Type: {lecture.resource_type || 'resource'}</p>
                <p className="text-gray-400 text-xs mb-1">Course: {lecture.course_id}</p>
                {lecture.description && <p className="text-gray-300 text-sm mb-2">{lecture.description}</p>}
                <a href={lecture.resource_url} target="_blank" rel="noreferrer" className="text-yellow-400 text-sm">Open Resource</a>
              </div>
            ))}
          </section>
        )}

        {isTeacher && activeTab === 'assignments' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Assignments</h2>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 space-y-4">
              <h3 className="text-xl font-bold text-yellow-400">Create Assignment</h3>
              <select value={assignmentForm.course_id} onChange={(e) => setAssignmentForm({ ...assignmentForm, course_id: e.target.value })} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none">
                <option value="">Select course</option>
                {(dashboardData.teacher_courses || []).map((course) => <option key={course.id} value={course.id}>{course.title}</option>)}
              </select>
              <input value={assignmentForm.title} onChange={(e) => setAssignmentForm({ ...assignmentForm, title: e.target.value })} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none" placeholder="Assignment title" />
              <textarea value={assignmentForm.description} onChange={(e) => setAssignmentForm({ ...assignmentForm, description: e.target.value })} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none min-h-[90px]" placeholder="Assignment description" />
              <input type="date" value={assignmentForm.due_date} onChange={(e) => setAssignmentForm({ ...assignmentForm, due_date: e.target.value })} className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none" />
              <button onClick={createAssignment} className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-bold">Create Assignment</button>
            </div>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-4">
              <input
                value={assignmentSearch}
                onChange={(e) => setAssignmentSearch(e.target.value)}
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                placeholder="Search assignments by title, description, due date, or status"
              />
            </div>
            {renderCardGrid(teacherAssignmentsFiltered || [], 'No assignments yet', 'Create assignments for your courses to track submissions.', (assignment) => (
              <div key={assignment.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-yellow-400 mb-2">{assignment.title}</h3>
                <p className="text-gray-300 text-sm mb-2">{assignment.description}</p>
                <p className="text-gray-400 text-sm">Due: {assignment.due_date || 'Not set'}</p>
                <div className="mt-3 space-y-2">
                  {(dashboardData.assignment_submissions || []).filter((submission) => submission.assignment_id === assignment.id).map((submission) => (
                    <div key={submission.id || `${assignment.id}_${submission.student_id}`} className="bg-gray-800 rounded-lg p-3 text-xs text-gray-300">
                      <div>Student: {submission.student_id}</div>
                      <div>Status: {submission.status || 'submitted'}</div>
                      {submission.submission_text && <div className="mt-1">Submission: {submission.submission_text}</div>}
                      {submission.grade !== undefined && submission.grade !== null && <div className="mt-1 text-green-300">Grade: {submission.grade}</div>}
                      {submission.feedback && <div className="mt-1">Feedback: {submission.feedback}</div>}
                      <button onClick={() => gradeStudentAssignment(assignment.id, submission.student_id)} className="mt-2 text-yellow-400 hover:text-yellow-300">Grade / Update Feedback</button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}

        {isTeacher && activeTab === 'clock' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Clock In/Out</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-gray-400 text-sm">Status</div><div className="text-2xl font-bold text-yellow-400 capitalize">{teacherClock.status || 'offline'}</div></div>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-gray-400 text-sm">Today</div><div className="text-2xl font-bold text-yellow-400">{teacherClock.today_hours || 0}h</div></div>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-gray-400 text-sm">Week</div><div className="text-2xl font-bold text-yellow-400">{teacherClock.weekly_hours || 0}h</div></div>
              <div className="bg-gray-900 border border-yellow-400/20 rounded-xl p-4"><div className="text-gray-400 text-sm">Month</div><div className="text-2xl font-bold text-yellow-400">{teacherClock.monthly_hours || 0}h</div></div>
            </div>
            {teacherClock.status === 'online' && teacherClock.active_record && (
              <div className="bg-gray-900 border border-green-500/30 rounded-2xl p-6">
                <div className="text-green-300 text-sm mb-2">Status: Online</div>
                <div className="text-gray-300 text-sm">Clock In: {new Date(teacherClock.active_record.clock_in_at).toLocaleTimeString()}</div>
                <div className="text-yellow-400 text-3xl font-bold mt-2">Current Session: {formatSessionDuration(liveSessionSeconds)}</div>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={clockIn} disabled={clocking || teacherClock.status === 'online'} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold disabled:opacity-50">Clock In</button>
              <button onClick={clockOut} disabled={clocking || teacherClock.status !== 'online'} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-bold disabled:opacity-50">Clock Out</button>
            </div>
            {renderCardGrid(teacherClock.history || [], 'No clock history', 'Clock records will appear here after your first session.', (record) => (
              <div key={record.id} className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-yellow-400 mb-2">{record.date}</h3>
                <p className="text-gray-300 text-sm">In: {record.clock_in_at ? new Date(record.clock_in_at).toLocaleTimeString() : '-'}</p>
                <p className="text-gray-300 text-sm">Out: {record.clock_out_at ? new Date(record.clock_out_at).toLocaleTimeString() : '-'}</p>
                <p className="text-gray-300 text-sm">Hours: {record.total_hours || 0}</p>
              </div>
            ))}
          </section>
        )}

        {isTeacher && activeTab === 'notifications' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Notifications</h2>
            {renderCardGrid(dashboardData.notifications || [], 'No notifications', 'You are all caught up.', (notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`bg-gray-900 border rounded-2xl p-6 transition-all ${notification.link ? 'cursor-pointer hover:border-yellow-400' : ''} ${notification.is_read ? 'border-gray-700' : 'border-yellow-400/40'}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-400 capitalize">{(notification.type || '').replace(/_/g, ' ')}</span>
                  {!notification.is_read && <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded-full font-bold">New</span>}
                </div>
                <h3 className="text-lg font-bold text-yellow-400 mb-2">{notification.title}</h3>
                <p className="text-gray-300 text-sm">{notification.message}</p>
                {notification.link && <div className="text-yellow-400 text-xs mt-3 font-medium">View details \u2192</div>}
              </div>
            ))}
          </section>
        )}

        {isTeacher && activeTab === 'profile' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-yellow-400">Profile</h2>
            <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-6 space-y-2">
              <div className="text-gray-300">Name: {user.firstname || user.name}</div>
              <div className="text-gray-300">Email: {user.email}</div>
              <div className="text-gray-300 capitalize">Role: {userRole}</div>
              <div className="text-gray-300">Hours this week: {teacherClock.weekly_hours || 0}</div>
            </div>
          </section>
        )}

        {dashboardData.notifications && dashboardData.notifications.length > 0 && (
          <div className="mt-10 bg-gray-900 border border-yellow-400/20 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-yellow-400 mb-4">Notifications</h3>
            <div className="space-y-3">
              {dashboardData.notifications.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleNotificationClick(item)}
                  className={`bg-gray-800 rounded-xl p-4 transition-all ${item.link ? 'cursor-pointer hover:bg-gray-700' : ''}`}
                >
                  <div className="text-yellow-400 font-medium">{item.title}</div>
                  <div className="text-gray-300 text-sm mt-1">{item.message}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Order Confirmation Page
const OrderConfirmationPage = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const paypalOrderId = params.get('paypal_order_id');
    const paypalToken = params.get('token');
    if (!sessionId && !paypalOrderId && !paypalToken) {
      setError('Missing payment identifier');
      setLoading(false);
      return;
    }
    const fetchStatus = async () => {
      try {
        if (paypalOrderId || paypalToken) {
          const orderId = paypalOrderId || paypalToken;
          const res = await axios.get(`${API}/payments/paypal/status/${orderId}`);
          setStatus(res.data);
        } else if (sessionId) {
          const res = await axios.get(`${API}/payments/status/${sessionId}`);
          setStatus(res.data);
        }
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to confirm payment');
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <h1 className="text-4xl font-bold text-yellow-400 mb-6">✅ Order Confirmation</h1>
        {loading && <div className="text-gray-300">Checking payment status...</div>}
        {error && <div className="text-red-400">{error}</div>}
        {!loading && !error && status && (
          <div className="bg-gray-900 border border-yellow-400 rounded-lg p-6">
            <div className="text-xl mb-2">Payment Status: <span className="font-bold capitalize">{String(status.payment_status || 'unknown').replaceAll('_', ' ')}</span></div>
            {status.course_name && <div className="mb-2">Course / Club: <span className="font-bold">{status.course_name}</span></div>}
            <div className="mb-2">Amount: <span className="font-bold">{status.currency || 'CAD'} ${Number(status.amount || 0).toFixed(2)}</span></div>
            <div className="mb-2">Provider: <span className="font-bold uppercase">{status.provider || 'payment provider'}</span></div>
            {status.transaction_id && <div className="mb-2 text-sm text-gray-400">Transaction Reference: {status.transaction_id}</div>}
            <div className="text-gray-300 mb-4">Finalization: {String(status.finalization_status || 'pending').replaceAll('_', ' ')}</div>
            <a href="/" className="inline-block bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 rounded-lg font-medium">Return Home</a>
          </div>
        )}
      </div>
    </div>
  );
};

// Profile Page Component
const ProfilePage = () => {
  const [profilePicture, setProfilePicture] = useState(null);
  const [idDocument, setIdDocument] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [codes, setCodes] = useState([]);
  const [codesLoading, setCodesLoading] = useState(true);
  const [redeemCode, setRedeemCode] = useState('');
  const [deleteOtpRequested, setDeleteOtpRequested] = useState(false);
  const [deleteOtp, setDeleteOtp] = useState('');
  const [deleteMessage, setDeleteMessage] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteCooldownUntil, setDeleteCooldownUntil] = useState(null);
  const [timerNow, setTimerNow] = useState(Date.now());
  const [relationshipLoading, setRelationshipLoading] = useState(false);
  const [assignedTeacher, setAssignedTeacher] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const { user, logout, refreshUser } = useAuth();
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [profileForm, setProfileForm] = useState({
    name: '',
    firstname: '',
    lastname: '',
    bio: '',
    phone_number: '',
  });
  const userRole = (user?.role || 'student').toLowerCase();
  const isTeacher = userRole === 'teacher' || userRole === 'mentor';
  const roleDisplay = userRole === 'mentor' ? 'teacher' : userRole;

  useEffect(() => {
    const timer = window.setInterval(() => setTimerNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const deleteCooldown = deleteCooldownUntil ? Math.max(0, Math.ceil((deleteCooldownUntil - timerNow) / 1000)) : 0;

  const fetchCodes = async () => {
    try {
      setCodesLoading(true);
      const res = await axios.get(`${API}/user/codes`);
      setCodes(res.data?.codes || []);
    } catch (e) {
      console.error('Failed to load codes', e);
    } finally {
      setCodesLoading(false);
    }
  };

  useEffect(() => {
    fetchCodes();
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/payments/history`)
      .then((response) => setPaymentHistory(response.data?.payments || []))
      .catch(() => setPaymentHistory([]));
  }, [user?.id]);

  const fetchRelationshipData = async () => {
    if (!user) return;
    try {
      setRelationshipLoading(true);
      if (isTeacher) {
        const response = await axios.get(`${API}/dashboard/teacher`);
        setSelectedStudents(response.data?.students || []);
        setAssignedTeacher(null);
      } else {
        const response = await axios.get(`${API}/student/teacher`);
        setAssignedTeacher(response.data?.teacher || null);
        setSelectedStudents([]);
      }
    } catch (error) {
      setAssignedTeacher(null);
      setSelectedStudents([]);
    } finally {
      setRelationshipLoading(false);
    }
  };

  useEffect(() => {
    fetchRelationshipData();
  }, [user?.id, isTeacher]);

  useEffect(() => {
    if (!user) return;
    setProfileForm({
      name: user.name || '',
      firstname: user.firstname || '',
      lastname: user.lastname || '',
      bio: user.bio || '',
      phone_number: user.phone_number || '',
    });
  }, [user]);

  const saveProfile = async () => {
    try {
      setProfileSaving(true);
      setProfileMessage('');
      const payload = {
        name: profileForm.name,
        firstname: profileForm.firstname,
        lastname: profileForm.lastname,
        bio: profileForm.bio,
        phone_number: profileForm.phone_number,
      };
      await axios.put(`${API}/user/profile`, payload);
      await refreshUser();
      setProfileMessage('Profile updated successfully.');
    } catch (error) {
      setProfileMessage(error.response?.data?.detail || 'Unable to update profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  const requestDeleteOtp = async () => {
    try {
      setDeleteLoading(true);
      const response = await axios.post(`${API}/user/delete-account/request-otp`);
      setDeleteOtpRequested(true);
      setDeleteMessage(response.data?.message || 'Verification code sent to your email');
      setDeleteCooldownUntil(Date.now() + (response.data?.resend_available_in_seconds || 60) * 1000);
    } catch (error) {
      setDeleteMessage(error.response?.data?.detail || 'Unable to send verification code');
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirmDeleteAccount = async () => {
    if (!deleteOtp || deleteOtp.length !== 6) {
      setDeleteMessage('Enter the 6-digit verification code');
      return;
    }
    try {
      setDeleteLoading(true);
      await axios.post(`${API}/user/delete-account/verify-otp`, { otp: deleteOtp });
      logout();
      window.location.href = '/';
    } catch (error) {
      setDeleteMessage(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploading(true);
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('profile_picture', file);
      
      try {
        const response = await axios.post(`${API}/user/upload-profile-picture`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        console.log('Profile picture updated successfully!');
        // Update user state immediately to show new picture
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (error) {
        console.warn('Error uploading profile picture:', error.response?.data?.detail || 'Unknown error');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleIdVerification = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('id_document', file);
      
      try {
        const response = await axios.post(`${API}/user/submit-id-verification`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        console.log('ID verified successfully!');
        // Refresh to reflect verified status
        setTimeout(() => window.location.reload(), 800);
      } catch (error) {
        console.warn('Error submitting ID verification:', error.response?.data?.detail || 'Unknown error');
      } finally {
        setUploading(false);
      }
    }
  };

  if (!user) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-yellow-400 mb-4">👤 Access Denied</h2>
          <p className="text-gray-300">Please login to access your profile</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-yellow-400 mb-8">👤 Profile Settings</h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-gray-900 border border-yellow-400/30 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Role</div>
            <div className="text-lg font-bold text-yellow-400 capitalize">{roleDisplay}</div>
          </div>
          <div className="bg-gray-900 border border-yellow-400/30 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Account</div>
            <div className="text-lg font-bold text-yellow-400">{user.firstname || user.name}</div>
          </div>
          <div className="bg-gray-900 border border-yellow-400/30 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Joined</div>
            <div className="text-lg font-bold text-yellow-400">{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Recently'}</div>
          </div>
        </div>

        {/* Profile Picture Section */}
        <div className="bg-gray-900 border border-yellow-400/30 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-yellow-400 mb-6">Profile Picture</h2>
          
          <div className="flex items-center space-x-6">
            <div className="relative">
              {(user.profileImage || user.profile_photo_url || user.profile_picture) ? (
                <img 
                  src={resolveImageUrl(user.profileImage || user.profile_photo_url || user.profile_picture)} 
                  alt="Profile" 
                  className="w-24 h-24 rounded-full object-cover border-4 border-yellow-400"
                />
              ) : (
                <div className="w-24 h-24 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center text-black text-2xl font-bold">
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              )}
            </div>
            
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-2">{user.name}</h3>
              <p className="text-gray-400 mb-4">
                {isTeacher
                  ? 'Upload a profile picture so students can recognize you'
                  : 'Upload a profile picture so mentors can recognize you'}
              </p>
              
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePictureUpload}
                className="hidden"
                id="profile-upload"
                disabled={uploading}
              />
              <label
                htmlFor="profile-upload"
                className={`inline-block bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 rounded-lg font-medium cursor-pointer transition-all ${
                  uploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {uploading ? 'Uploading...' : 'Upload Picture'}
              </label>
            </div>
          </div>
        </div>

        {/* ID Verification Section */}
        <div className="bg-gray-900 border border-yellow-400/30 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-yellow-400 mb-6">ID Verification</h2>
          
          <div className="flex items-center space-x-4 mb-6">
            <div className={`px-4 py-2 rounded-full text-sm font-bold ${
              user.id_verified 
                ? 'bg-green-600 text-white' 
                : user.id_verification_status === 'pending'
                ? 'bg-yellow-600 text-black'
                : 'bg-red-600 text-white'
            }`}>
              {user.id_verified ? '✅ Verified' : 
               user.id_verification_status === 'pending' ? '⏳ Pending Review' : 
               '❌ Not Verified'}
            </div>
          </div>
          
          {!user.id_verified && (
            <>
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-2">Why verify your identity?</h3>
                <ul className="text-gray-300 space-y-2">
                  <li className="flex items-center space-x-2">
                    <span className="text-yellow-400">•</span>
                    <span>Build trust with mentors and other learners</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-yellow-400">•</span>
                    <span>Access premium features and mentorship</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-yellow-400">•</span>
                    <span>Prevent scams and protect the community</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-yellow-400">•</span>
                    <span>Get verified badge on your profile</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg mb-6">
                <h4 className="text-yellow-400 font-bold mb-2">🆔 Accepted Documents:</h4>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Government-issued ID (Driver's License, Passport, National ID)</li>
                  <li>• Student ID (for educational verification)</li>
                  <li>• Professional license or certification</li>
                </ul>
                <p className="text-xs text-gray-400 mt-2">
                  Your document will be reviewed within 24-48 hours and then securely deleted.
                </p>
              </div>
              
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleIdVerification}
                className="hidden"
                id="id-upload"
                disabled={uploading || user.id_verification_status === 'pending'}
              />
              <label
                htmlFor="id-upload"
                className={`inline-block px-6 py-3 rounded-lg font-bold transition-all ${
                  user.id_verification_status === 'pending'
                    ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                    : uploading
                    ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                }`}
              >
                {uploading ? 'Uploading...' : 
                 user.id_verification_status === 'pending' ? 'Verification Pending' :
                 'Submit ID for Verification'}
              </label>
            </>
          )}
        </div>

        {/* Account Information */}
        <div className="bg-gray-900 border border-yellow-400/30 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-yellow-400 mb-6">Account Information</h2>
          {profileMessage && <div className="mb-4 text-sm text-yellow-300">{profileMessage}</div>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-yellow-400 font-medium mb-2">Display Name</label>
              <input
                type="text"
                value={profileForm.name}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, name: e.target.value }))}
                className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-yellow-400 font-medium mb-2">Phone Number</label>
              <input
                type="text"
                value={profileForm.phone_number}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, phone_number: e.target.value }))}
                className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-yellow-400 font-medium mb-2">First Name</label>
              <input
                type="text"
                value={profileForm.firstname}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, firstname: e.target.value }))}
                className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-yellow-400 font-medium mb-2">Last Name</label>
              <input
                type="text"
                value={profileForm.lastname}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, lastname: e.target.value }))}
                className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-yellow-400 font-medium mb-2">Bio</label>
              <textarea
                value={profileForm.bio}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, bio: e.target.value }))}
                rows={4}
                className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
                placeholder="Tell others about your learning goals or experience"
              />
            </div>
          </div>

          <div className="mb-8">
            <button
              onClick={saveProfile}
              disabled={profileSaving}
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 rounded-lg font-bold disabled:opacity-50"
            >
              {profileSaving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-yellow-400 font-medium mb-2">Email</h3>
              <p className="text-white">{user.email}</p>
            </div>
            <div>
              <h3 className="text-yellow-400 font-medium mb-2">First Name</h3>
              <p className="text-white">{user.firstname || user.name?.split(' ')?.[0] || 'Not set'}</p>
            </div>
            <div>
              <h3 className="text-yellow-400 font-medium mb-2">Last Name</h3>
              <p className="text-white">{user.lastname || user.name?.split(' ')?.slice(1).join(' ') || 'Not set'}</p>
            </div>
            <div>
              <h3 className="text-yellow-400 font-medium mb-2">Subscription</h3>
              <p className="text-white capitalize">{user.subscription_tier}</p>
            </div>
            <div>
              <h3 className="text-yellow-400 font-medium mb-2">Role</h3>
              <p className="text-white capitalize">{roleDisplay}</p>
            </div>
            <div>
              <h3 className="text-yellow-400 font-medium mb-2">Member Since</h3>
              <p className="text-white">
                {user.createdAt || user.created_at ? new Date(user.createdAt || user.created_at).toLocaleDateString() : 'Recently'}
              </p>
            </div>
            <div className="md:col-span-2">
              <h3 className="text-yellow-400 font-medium mb-2">Bio</h3>
              <p className="text-white">{user.bio || 'Add a short bio to personalize your profile.'}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 border border-yellow-400/30 rounded-lg p-6 mt-8">
          <h2 className="text-2xl font-bold text-yellow-400 mb-4">Payment History</h2>
          {paymentHistory.length === 0 ? (
            <p className="text-gray-400">No payment records available.</p>
          ) : (
            <div className="space-y-3">
              {paymentHistory.map((payment) => (
                <div key={payment.id || payment.session_id} className="bg-gray-800 rounded-xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <div className="text-white font-semibold">{payment.course_name || payment.product_type || 'K.R.A Payment'}</div>
                    <div className="text-gray-400 text-sm">{payment.created_at ? new Date(payment.created_at).toLocaleString() : 'Date unavailable'} • {String(payment.provider || 'stripe').toUpperCase()}</div>
                    <div className="text-gray-300 text-sm">{payment.currency || 'CAD'} ${Number(payment.amount || 0).toFixed(2)} • {String(payment.payment_status || 'unknown').replaceAll('_', ' ')}</div>
                  </div>
                  {payment.session_id && <a href={`/order-confirmation?session_id=${encodeURIComponent(payment.session_id)}`} className="border border-yellow-400 text-yellow-300 hover:bg-yellow-400 hover:text-black px-3 py-2 rounded-lg text-sm text-center">View Receipt</a>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-gray-900 border border-yellow-400/30 rounded-lg p-6 mt-8">
          <h2 className="text-2xl font-bold text-yellow-400 mb-6">Learning Relationship</h2>
          {relationshipLoading ? (
            <p className="text-gray-400">Loading relationship data...</p>
          ) : isTeacher ? (
            <div>
              <h3 className="text-yellow-400 font-medium mb-3">Students Who Chose You</h3>
              {selectedStudents.length === 0 ? (
                <p className="text-gray-400">No students linked yet.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedStudents.map((student) => (
                    <div key={student.id} className="bg-gray-800 border border-yellow-400/20 rounded-lg p-4">
                      <div className="text-white font-medium">{student.name}</div>
                      <div className="text-gray-400 text-sm">{student.email}</div>
                      <div className="text-gray-500 text-xs mt-1">Courses with you: {student.courses_count || 0}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <h3 className="text-yellow-400 font-medium mb-3">Teacher You Chose</h3>
              {assignedTeacher ? (
                <div className="bg-gray-800 border border-yellow-400/20 rounded-lg p-4">
                  <div className="text-white font-medium">{assignedTeacher.name}</div>
                  <div className="text-gray-400 text-sm">{assignedTeacher.email}</div>
                  <div className="text-gray-300 text-sm mt-2">{assignedTeacher.bio || 'Teacher profile available.'}</div>
                </div>
              ) : (
                <p className="text-gray-400">No teacher selected yet.</p>
              )}
            </div>
          )}
        </div>

        {/* Delete Account */}
        <div className="bg-gray-900 border border-red-500/30 rounded-lg p-6 mt-8">
          <h2 className="text-2xl font-bold text-red-400 mb-4">Delete Account</h2>
          <p className="text-gray-300 mb-3">Delete your account permanently. OTP confirmation is required.</p>
          {deleteMessage && <div className="mb-3 text-sm text-yellow-300">{deleteMessage}</div>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
            <input
              type="text"
              value={deleteOtp}
              onChange={(e) => setDeleteOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit OTP"
              className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
              disabled={!deleteOtpRequested}
            />
            {!deleteOtpRequested ? (
              <button
                onClick={requestDeleteOtp}
                disabled={deleteLoading}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-bold disabled:opacity-50"
              >{deleteLoading ? 'Sending OTP...' : 'Send Delete OTP'}</button>
            ) : (
              <button
                onClick={confirmDeleteAccount}
                disabled={deleteLoading}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-bold disabled:opacity-50"
              >{deleteLoading ? 'Deleting...' : 'Confirm Delete'}</button>
            )}
            <div className="text-xs text-gray-400">This removes your profile and newsletter subscriptions.</div>
          </div>
          {deleteOtpRequested && (
            <button
              onClick={requestDeleteOtp}
              disabled={deleteLoading || deleteCooldown > 0}
              className="mt-3 text-sm text-yellow-400 hover:text-yellow-300 disabled:opacity-50"
            >
              {deleteCooldown > 0 ? `Resend OTP in ${deleteCooldown}s` : 'Resend OTP'}
            </button>
          )}
        </div>

        {/* My Codes */}
        <div className="bg-gray-900 border border-yellow-400/30 rounded-lg p-6 mt-8">
          <h2 className="text-2xl font-bold text-yellow-400 mb-6">🎟️ My Codes</h2>
          <div className="mb-4">
            <div className="text-gray-300 mb-2">Redeem a code to unlock access:</div>
            <div className="flex gap-2">
              <input
                type="text"
                value={redeemCode}
                onChange={(e) => setRedeemCode(e.target.value)}
                placeholder="Enter code"
                className="flex-1 bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none"
              />
              <button
                onClick={async () => {
                  if (!redeemCode.trim()) return;
                  try {
                    const res = await axios.post(`${API}/redeem`, { code: redeemCode.trim() });
                    console.log(res.data?.message || 'Redeemed successfully');
                    setRedeemCode('');
                    fetchCodes();
                  } catch (e) {
                    console.warn(e.response?.data?.detail || 'Failed to redeem');
                  }
                }}
                className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-medium"
              >Redeem</button>
            </div>
          </div>
          <div className="text-gray-300 mb-3">Your codes:</div>
          {codesLoading ? (
            <div className="text-gray-400">Loading codes...</div>
          ) : codes && codes.length > 0 ? (
            <div className="space-y-3">
              {codes.map((c) => (
                <div key={c.code} className="bg-gray-800 p-3 rounded flex items-center justify-between">
                  <div>
                    <div className="text-white font-bold">{c.code}</div>
                    <div className="text-gray-400 text-sm">{c.category} • Expires {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : 'N/A'}</div>
                  </div>
                  <div className={`text-sm px-3 py-1 rounded ${c.status === 'redeemed' ? 'bg-green-700 text-white' : c.status === 'expired' ? 'bg-red-700 text-white' : 'bg-gray-700 text-white'}`}>
                    {c.status}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-400">No codes yet</div>
          )}
        </div>
      </div>
    </div>
  );
};

// HomePage Component
const HomePage = ({ onPageChange }) => {
  const [courses, setCourses] = useState([]);
  const [products, setProducts] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    fetchCourses();
    fetchProducts();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`, { params: { limit: 500 } });
      const coursesData = response.data.courses || response.data || [];
      setCourses(coursesData.slice(0, 6));
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      const productsData = response.data.products || response.data || [];
      setProducts(productsData.slice(0, 3)); // Show top 3 products
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <AnnouncementBanner placement="homepage" />
      </div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-black via-gray-900 to-black py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <img 
            src="/kra-logo.png" 
            alt="K.R.A Logo" 
            className="mx-auto h-32 w-32 mb-8 animate-pulse"
          />
          <h1 className="text-6xl md:text-8xl font-bold mb-6 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">
            Transform Your <span className="text-white">Future</span>
          </h1>
          <p className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto text-gray-300 leading-relaxed">
            Master cutting-edge skills in Engineering, Technology, and Innovation. 
            Join thousands of learners building their dream careers with <span className="text-yellow-400 font-bold">K.R.A</span>
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <button 
              onClick={() => onPageChange(user ? 'courses' : 'auth')}
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-lg"
            >
              {user ? 'Explore Courses' : 'Start Learning Today'}
            </button>
            <button 
              onClick={() => onPageChange('community')}
              className="border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105"
            >
              Join Community
            </button>
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-yellow-400">Signature Programs</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Premium flagship programs designed for robotics, AI, engineering, and future-ready skills
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses && courses.length > 0 && courses.map((course) => (
              <div key={course.id} className="bg-black border border-yellow-400/30 rounded-lg overflow-hidden hover:border-yellow-400 transition-all transform hover:scale-105 cursor-pointer" onClick={() => onPageChange('courses')}>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl">{course.category && course.category.toLowerCase().includes('robot') ? '🤖' : course.category && course.category.toLowerCase().includes('cad') ? '📐' : course.category && course.category.toLowerCase().includes('math') ? '📘' : course.category && course.category.toLowerCase().includes('language') ? '🗣️' : '✨'}</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      course.featured ? 'bg-purple-800 text-purple-200' : course.price === 0 ? 'bg-green-800 text-green-200' : 'bg-yellow-800 text-yellow-200'
                    }`}>
                      {course.featured ? 'FLAGSHIP' : course.price === 0 ? 'FREE' : 'MONTHLY PRICING'}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-yellow-400">{course.title}</h3>
                  <p className="text-gray-400 mb-4">{course.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1">
                      <span className="text-yellow-400">⭐</span>
                      <span className="text-sm text-gray-300">4.8 (120 reviews)</span>
                    </div>
                    <div className="text-sm text-gray-400">
                      {course.lessons?.length || 12} lessons
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <button 
              onClick={() => onPageChange('courses')}
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black px-8 py-3 rounded-lg font-bold transition-all transform hover:scale-105"
            >
              View All Courses
            </button>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-yellow-400">Tech Products</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Cutting-edge technology to accelerate your learning and projects
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products && products.length > 0 && products.map((product) => (
              <div key={product.id} className="bg-gray-900 border border-yellow-400/30 rounded-lg overflow-hidden hover:border-yellow-400 transition-all transform hover:scale-105 cursor-pointer" onClick={() => onPageChange('shop')}>
                <div className="p-6">
                  <div className="text-4xl mb-4 text-center">
                    {product.name.includes('Glasses') ? '👓' : product.name.includes('Robotics') ? '🤖' : '🔌'}
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-yellow-400">{product.name}</h3>
                  <p className="text-gray-400 mb-4">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-bold text-yellow-400">${product.price}</div>
                    <div className="text-sm text-gray-400">In Stock</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <button 
              onClick={() => onPageChange('shop')}
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black px-8 py-3 rounded-lg font-bold transition-all transform hover:scale-105"
            >
              Shop All Products
            </button>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-yellow-600 to-yellow-500">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6 text-black">Ready to Start Your Journey?</h2>
          <p className="text-xl mb-8 text-gray-800 max-w-2xl mx-auto">
            Join thousands of learners already building their future with K.R.A's comprehensive platform
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => onPageChange(user ? 'pricing' : 'auth')}
              className="bg-black text-yellow-400 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-800 transition-all transform hover:scale-105"
            >
              Get Started Free
            </button>
            <button 
              onClick={() => onPageChange('mentorship')}
              className="border-2 border-black text-black hover:bg-black hover:text-yellow-400 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105"
            >
              Find a Mentor
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

// Main App Content Component
const AppContent = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const currentPage = resolveCurrentPage(location.pathname, location.search) || 'not-found';
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const isEmployeeUser = (user?.role || '').toLowerCase() === 'employee';
  const employeeAllowedPages = new Set(['employee-dashboard', 'change-password', 'reset-password', 'auth', 'terms', 'privacy', 'certificate-verification']);
  const hasFooterAdminSession = (() => {
    try {
      return sessionStorage.getItem(ADMIN_FOOTER_SESSION_KEY) === '1';
    } catch (error) {
      return false;
    }
  })();
  const canAccessAdminPages = isAdmin && (hasFooterAdminSession || ['admin', 'admin-dashboard', 'admin-verifications', 'admin-certificates'].includes(currentPage));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const paypalToken = params.get('token');
    const paypalOrderId = params.get('paypal_order_id');
    const paymentSuccess = params.get('payment') === 'success';
    if (location.pathname === '/payment-cancelled' || params.get('payment') === 'cancelled') {
      navigate(PAGE_TO_PATH.home, { replace: true });
      return;
    }
    if (location.pathname !== PAGE_TO_PATH['order-confirmation'] && (sessionId || paypalOrderId || paypalToken || paymentSuccess)) {
      navigate(`${PAGE_TO_PATH['order-confirmation']}${location.search}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isAdmin) {
      try {
        sessionStorage.removeItem(ADMIN_FOOTER_SESSION_KEY);
      } catch (error) {
        // Ignore session storage failures.
      }
    }
  }, [isAdmin]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname, location.search]);

  useEffect(() => {
    const role = (user?.role || '').toLowerCase();
    const mustChangePassword = Boolean(user?.must_change_password || user?.mustChangePassword);
    if ((role === 'teacher' || role === 'employee') && mustChangePassword) {
      if (!PASSWORD_CHANGE_ALLOWED_PAGES.includes(currentPage)) {
        navigate(PAGE_TO_PATH['change-password'], { replace: true });
      }
    }
  }, [user, currentPage, navigate]);

  useEffect(() => {
    if (!user || loading) return;
    if (!isEmployeeUser) return;
    if (employeeAllowedPages.has(currentPage)) return;
    navigate(PAGE_TO_PATH['employee-dashboard'], { replace: true });
  }, [user, loading, isEmployeeUser, currentPage, navigate]);

  const navigateToPage = (page, options = {}) => {
    const targetPath = options.path || PAGE_TO_PATH[page] || PAGE_TO_PATH.home;
    const nextSearch = typeof options.search === 'string' ? options.search : '';
    const targetUrl = `${targetPath}${nextSearch}`;
    const currentUrl = `${location.pathname}${location.search}`;
    if (targetUrl === currentUrl) {
      return;
    }
    navigate(targetUrl, { replace: Boolean(options.replace) });
  };

  const renderAdminDenied = () => (
    <div className="bg-black min-h-screen text-white flex items-center justify-center px-6">
      <div className="max-w-xl bg-gray-900 border border-red-500/40 rounded-2xl p-8 text-center">
        <h2 className="text-3xl font-bold text-red-400 mb-4">Admin Access Required</h2>
        <p className="text-gray-300 mb-6">Only administrator accounts can access this area.</p>
        <button onClick={() => navigateToPage('home')} className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-3 rounded-lg font-bold">Back to Home</button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-black min-h-screen flex items-center justify-center">
        <div className="text-center">
          <img 
            src="/kra-logo.png" 
            alt="K.R.A Logo" 
            className="mx-auto h-16 w-16 mb-4 animate-spin"
          />
          <div className="text-yellow-400 text-xl font-bold">Loading K.R.A Platform...</div>
        </div>
      </div>
    );
  }

  const renderPage = () => {
    try {
      switch (currentPage) {
        case 'not-found':
          return <NotFoundPage />;
        case 'home':
          return <HomePage onPageChange={navigateToPage} />;
        case 'auth':
          return <AuthPage onSuccess={(authUser) => {
            const authRole = (authUser?.role || 'student').toLowerCase();
            const mustChangePassword = Boolean(authUser?.must_change_password || authUser?.mustChangePassword);
            if ((authRole === 'teacher' || authRole === 'employee') && mustChangePassword) {
              navigateToPage('change-password');
              return;
            }
            const pendingEnrollment = readPendingEnrollment();
            if (authRole !== 'student' && pendingEnrollment?.course_id) {
              clearPendingEnrollment();
            }
            if (authRole === 'admin') {
              try {
                sessionStorage.setItem(ADMIN_FOOTER_SESSION_KEY, '1');
              } catch (error) {
                // Ignore session storage failures.
              }
              navigateToPage('admin-dashboard');
              return;
            }
            if (authRole === 'employee') {
              navigateToPage('employee-dashboard');
              return;
            }
            if (authRole === 'student' && pendingEnrollment?.course_id) {
              const search = `?resume_enrollment=1&course_id=${encodeURIComponent(String(pendingEnrollment.course_id))}`;
              navigateToPage('courses', { search, replace: true });
              return;
            }
            const redirect = localStorage.getItem('kra_post_login_redirect');
            if (redirect) {
              localStorage.removeItem('kra_post_login_redirect');
              navigateToPage(redirect);
            } else {
              navigateToPage(authRole === 'teacher' ? 'teacher-dashboard' : 'student-dashboard');
            }
          }} onPageChange={navigateToPage} />;
        case 'student-dashboard':
          return <DashboardPage initialTab="dashboard" />;
        case 'student-teacher':
          return <DashboardPage initialTab="my-teacher" />;
        case 'student-learning':
          return <DashboardPage initialTab="my-courses" />;
        case 'student-my-courses':
          return <MyCoursesPage />;
        case 'student-attendance':
          return <DashboardPage initialTab="attendance" />;
        case 'student-schedule':
          return <DashboardPage initialTab="class-schedule" />;
        case 'student-assignments':
          return <DashboardPage initialTab="assignments" />;
        case 'student-certificates':
          return <DashboardPage initialTab="certificates" />;
        case 'student-notifications':
          return <DashboardPage initialTab="notifications" />;
        case 'student-messages':
          return <StudentMessagesPage />;
        case 'student-course-detail':
          return <StudentCourseDetailPage />;
        case 'student-my-course-detail':
          return <MyCourseDetailsPage />;
        case 'teacher-dashboard':
          return <DashboardPage initialTab="dashboard" />;
        case 'teacher-students':
          return <DashboardPage initialTab="students" />;
        case 'teacher-attendance':
          return <DashboardPage initialTab="attendance-management" />;
        case 'teacher-classes':
          return <DashboardPage initialTab="classes" />;
        case 'teacher-meetings':
          return <DashboardPage initialTab="meetings" />;
        case 'teacher-analytics':
          return <DashboardPage initialTab="analytics" />;
        case 'teacher-courses':
          return <TeacherCoursesPage />;
        case 'teacher-course-detail':
          return <TeacherCoursesPage />;
        case 'teacher-lectures':
          return <DashboardPage initialTab="lectures" />;
        case 'teacher-assignments':
          return <DashboardPage initialTab="assignments" />;
        case 'teacher-clock':
          return <DashboardPage initialTab="clock" />;
        case 'teacher-messages':
          return <TeacherMessagesPage />;
        case 'teacher-notifications':
          return <DashboardPage initialTab="notifications" />;
        case 'employee-dashboard':
          return <EmployeePortal user={user} onPageChange={navigateToPage} />;
        case 'reset-password':
          return <ResetPasswordPage onPageChange={navigateToPage} />;
        case 'change-password':
          return <ChangePasswordPage onPageChange={navigateToPage} />;
        case 'courses':
        case 'courses-category':
          return <CoursesPage onPageChange={navigateToPage} />;
        case 'shop':
          return <ShopPage />;
        case 'pricing':
          return <PricingPage />;
        case 'community':
          return <CommunityPage />;
        case 'mentorship':
          return <MentorshipPage />;
        case 'code-editor':
          return <CodeEditorPage />;
        case 'jobs':
          return <JobsPage onPageChange={navigateToPage} />;
        case 'hackathons':
          return <HackathonsPage />;
        case 'dashboard':
          if (user) {
            const role = (user.role || 'student').toLowerCase();
            if (role === 'admin') return <Navigate to={PAGE_TO_PATH['admin-dashboard']} replace />;
            if (role === 'employee') return <Navigate to={PAGE_TO_PATH['employee-dashboard']} replace />;
            if (role === 'teacher' || role === 'mentor') return <Navigate to={PAGE_TO_PATH['teacher-dashboard']} replace />;
            return <Navigate to={PAGE_TO_PATH['student-dashboard']} replace />;
          }
          return <DashboardPage initialTab="dashboard" />;
        case 'order-confirmation':
          return <OrderConfirmationPage />;
        case 'profile':
          return <ProfilePage />;
        case 'terms':
          return <TermsPage />;
        case 'privacy':
          return <PrivacyPage />;
        case 'admin':
        case 'admin-dashboard':
          return canAccessAdminPages ? <AdminPortal user={user} onPageChange={navigateToPage} /> : renderAdminDenied();
        case 'admin-verifications':
          return canAccessAdminPages ? <AdminVerifications /> : renderAdminDenied();
        case 'admin-certificates':
          return canAccessAdminPages ? <AdminCertificates /> : renderAdminDenied();
        case 'certificate-verification':
          return <CertificateVerificationPage />;
        default:
          return (
            <div className="bg-black min-h-screen text-white flex items-center justify-center">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-yellow-400 mb-4">🚧 {currentPage.toUpperCase()}</h2>
                <p className="text-gray-300 mb-6">This feature is being built with all the functionality you requested!</p>
                <button 
                  onClick={() => navigateToPage('home')}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 rounded-lg font-medium"
                >
                  Back to Home
                </button>
              </div>
            </div>
          );
      }
    } catch (error) {
      console.error('Error rendering page:', error);
      return (
        <div className="bg-black min-h-screen text-white flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-red-400 mb-4">Error Loading Page</h2>
            <p className="text-gray-300 mb-6">Something went wrong. Please refresh the page.</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 rounded-lg font-medium"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="App bg-black min-h-screen">
      <Header onPageChange={navigateToPage} currentPage={currentPage} />
      {renderPage()}
      <Footer onPageChange={navigateToPage} currentPage={currentPage} />
      <AIChatbot />
      {currentPage === 'home' && <MagicCursor />}
    </div>
  );
};

// Main App Component
const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;